/* English UI catalogue.
 *
 * Structure mirrors the surfaces, not the component tree: `nav`, `dashboard`,
 * `cases`, `quotes`, `finance`, `directories`, `settings`, `profile`, and a
 * shared `common`. A string used by two surfaces lives in `common`; a string
 * used once lives with its surface. This keeps the two catalogues diffable side
 * by side, which is how a missing Russian key gets noticed.
 *
 * Domain vocabularies (`caseStatus`, `quoteStatus`, `docStatus`, `legStatus`,
 * `participantRole`, `modes`, `roles`, `notificationKind`) are top-level rather
 * than nested under a surface, because each is keyed by a union from
 * types/domain and is read from several surfaces at once. Keeping them beside
 * the union they mirror is what makes a new member of that union a visible gap
 * here rather than a silent English leak.
 *
 * Copy rules this file obeys:
 *   · Buttons are 1–3 words, verb-first, sentence case. Never Title Case.
 *   · Errors state what happened, then what to do. Never apologise.
 *   · Empty states say what the surface is FOR and offer the action that fills
 *     it. Where nothing is wrong, say so plainly. Never encourage.
 *   · Blocking messages name the block and the reason together, so the rule is
 *     learned once rather than rediscovered.
 */

export const en = {
  common: {
    appName: 'ASA Gateway',
    /** The em-dash placeholder for an absent value. Shared with lib/format. */
    empty: '—',
    actions: {
      retry: 'Try again',
      retryNow: 'Retry now',
      dismiss: 'Dismiss',
      undo: 'Undo',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      add: 'Add',
      open: 'Open',
      close: 'Close',
      /** The scrim behind a drawer: names WHICH panel a click there closes. */
      closeNamed: 'Close “{{title}}”',
      delete: 'Delete',
      upload: 'Upload',
      back: 'Back to the dashboard',
      /** Names an actions column whose header is visually empty. */
      label: 'Actions',
    },
    /* The table/board switch on list pages. "Board" not "kanban": the word
       must read in both catalogues without explanation. */
    view: {
      label: 'View',
      table: 'Table',
      board: 'Board',
    },
    /* The dense table's own two controls (UX-DR-4). Both are per-user,
       per-surface workstation habits kept beside the table/board choice, so the
       copy talks about THIS SCREEN rather than about a profile setting. */
    columns: {
      label: 'Columns',
      description: 'Which columns this screen shows',
      reset: 'Show every column',
      hiddenCount_one: '{{count}} column hidden',
      hiddenCount_other: '{{count}} columns hidden',
    },
    filters: {
      label: 'Filter',
      status: 'Status',
      all: 'All',
      clear: 'Clear filter',
      /* Plural, and a separate key from `clear`: a finance view carries a date
         range AND a facet AND a search, and a button offering to clear "the
         filter" when three are set leaves the reader guessing which one it
         means. */
      clearAll: 'Clear filters',
      activeCount_one: '{{count}} filter',
      activeCount_other: '{{count}} filters',
      emptyTitle: 'Nothing matches this filter',
      emptyDescription: 'Clear the filter to see the whole list again.',
    },
    /* The dense table's two empty states (UX-DR-17). NOTHING YET is this pair;
       NOTHING MATCHES is `filters.emptyTitle` above, reused rather than
       duplicated so the two readings of an empty table stay one decision.
       Deliberately unspecific, because it is the fallback: a surface that can
       say what fills it ("Raise one to bill the client") passes its own. */
    table: {
      emptyTitle: 'Nothing here yet',
      emptyDescription: 'Rows appear here as they are created.',
    },
    /* The paged table's footer. "Rows per page" is the one place in the product
       that says ROWS rather than naming the record: it is a fact about the
       table, not about the quotes in it — which is why the count beside it
       ("6 quotes") comes from the surface instead. */
    pagination: {
      label: 'Pages',
      rowsPerPage: 'Rows per page:',
      previous: 'Previous page',
      next: 'Next page',
      page: 'Page {{page}}',
      position: '{{page}} / {{total}}',
    },
    language: {
      label: 'Interface language',
      en: 'EN',
      ru: 'RU',
      english: 'English',
      russian: 'Русский',
    },
    search: {
      label: 'Search',
      placeholder: 'Search cases, quotes, clients',
      loading: 'Searching…',
      emptyTitle: 'No matches',
      emptyDescription: 'Check the number, or try part of the client name.',
      resultCount_one: '{{count}} match',
      resultCount_other: '{{count}} matches',
      hintNavigate: 'navigate',
      hintOpen: 'open',
      hintClose: 'close',
      kind: {
        case: 'Case',
        quote: 'Quote',
        client: 'Client',
      },
    },
    notifications: {
      label: 'Notifications',
      labelWithCount_one: 'Notifications, {{count}} unread',
      labelWithCount_other: 'Notifications, {{count}} unread',
      emptyTitle: 'Nothing new',
      emptyDescription:
        'Chat replies, status changes, approvals and payments on your quotes and cases appear here.',
  loading: 'Loading notifications',
      markAllRead: 'Mark all read',
      unreadItem: 'Unread — {{kind}}',
      /* Titles the server composes in English. Shown only while the topic AND
         the text both match features/notifications/notificationTitles.ts
         exactly; anything else renders as the server sent it. */
      titles: {
        teamAssigned: 'You were added to this quote',
        pricingRequested: 'A price is wanted on this quote',
        pricingReady: 'The price is ready on this quote',
        pricingReopened: 'This quote changed and needs pricing again',
        caseAssigned: 'You were assigned to this case',
        statusSent: 'Status sent to client',
        rateChangeRequested: 'Rate change requested',
        rateChangeApproved: 'Rate change approved',
        expenseRecorded: 'Expense line recorded',
        expenseAmended: 'Expense line amended',
        expenseRemoved: 'Expense line removed',
        paymentAwaitsConfirmation: 'Payment awaits confirmation',
      },
      /* A row's message CODE in words, filled from its params
         (features/notifications/notificationMessages.ts). Nested by the code's
         own dots — `case.status.moved` is `messages.case.status.moved`. A code
         whose detail is data (a chat excerpt, a route, a client's name) has no
         `detail` here: the stored one prints as is. `_bare` is the detail for a
         line with no item or reference. */
      messages: {
        case: {
          status: {
            moved: { title: 'Case status changed: {{from}} → {{to}}' },
          },
          assigned: { title: 'You were assigned to this case' },
          'status-sent': {
            title: 'Status sent to client',
            detail: '{{status}} → {{email}}',
          },
          message: { title: '{{author}} posted in the case chat' },
          'expense-changed': {
            recorded: {
              title: 'Expense line recorded',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
            amended: {
              title: 'Expense line amended',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
            removed: {
              title: 'Expense line removed',
              detail: '{{item}} — {{amount}}',
              detail_bare: '{{amount}}',
            },
          },
        },
        quote: {
          message: { title: '{{author}} posted in the quote chat' },
          sent: {
            title: 'Quote sent',
            detail_zero: 'Revision {{revision}}, no email addressed',
            detail_one: 'Revision {{revision}}, to {{count}} recipient',
            detail_other: 'Revision {{revision}}, to {{count}} recipients',
          },
          answered: {
            rejected: {
              title: 'The client rejected this quote',
              detail: 'Recorded on {{date}}',
            },
          },
          'participant-added': {
            title: 'A participant was added to your quote',
            detail: 'Role: {{role}}',
          },
          'team-assigned': { title: 'You were added to this quote' },
          'pricing-requested': { title: 'A price is wanted on this quote' },
          'pricing-ready': { title: 'The price is ready on this quote' },
          'pricing-reopened': { title: 'This quote changed and needs pricing again' },
          converted: {
            title: 'Case created from the quote',
            detail: 'Case {{caseNumber}} is open',
          },
        },
        finance: {
          'rate-change-requested': {
            title: 'Rate change requested',
            detail: '{{current}} → {{requested}}',
          },
          'rate-change-decided': {
            approved: {
              title: 'Rate change approved',
              detail: '{{current}} → {{requested}}',
            },
            rejected: {
              title: 'Rate change rejected',
              detail: '{{requested}} refused; the rate stays {{current}}',
            },
          },
          'payment-awaits-confirmation': {
            title: 'Payment awaits confirmation',
            detail: '{{reference}} — {{amount}}',
            detail_bare: '{{amount}}',
          },
        },
      },
    },
    /** Column and field labels shared by more than one table. */
    fields: {
      name: 'Name',
      country: 'Country',
      contact: 'Contact',
      email: 'Email',
      phone: 'Phone',
      owner: 'Owner',
      type: 'Type',
      status: 'Status',
      amount: 'Amount',
      number: 'Number',
      total: 'Total',
      date: 'Date',
    },
    /* The add/edit sheet. Shared by every surface that creates or changes a
     * record, so the commit word is the same everywhere — a user who learned
     * "Save" on one sheet must not meet "Submit" on the next. */
    form: {
      saving: 'Saving…',
      required: 'Required',
      /* Field-level codes the server sends as stable tokens (AD-27). */
      invalid: 'This value cannot be accepted',
      tooLong: 'This is too long',
      malformed: 'This could not be read',
      /* The money field codes (QW-9, E-1): a zero or negative figure, and one
         more precise than its column. FIELD codes under `errors`, not problem
         types. */
      notPositive: 'Must be greater than zero',
      negative: 'Cannot be negative',
      tooPrecise: 'Too many decimal places',
      /* The superuser's row scope is fixed (story B3): its flag reaches every
         record whatever the stored rows say. A FIELD code on `scopes`. */
      superuserScopesFixed: 'The full-access role always sees every record; this cannot be changed.',
      /* The account's and the role's `role` / `capabilities` field codes
         (S-7, O7/O8). */
      exceedsAuthority: 'More than you hold yourself — you cannot grant it',
      reserved: 'Only the super-admin holds this permission',
      /* The role editor's FIELD codes (S-13): a role key another role holds,
         and a permission or section the server does not know — a tab opened
         before the server was updated. */
      taken: 'Already in use',
      unknownCapability: 'No such permission — reload the page',
      unknownSurface: 'No such section — reload the page',
      /* The FIELD codes of a carrier payment over several lines (S-18a): a
         line naming both a cost and a debt, the same unit twice, over 101
         lines, a payment that is not the sum of its lines, lines of different
         carriers. */
      exclusive: 'Choose one, not both',
      duplicate: 'Already in this payment',
      tooMany: 'Too many lines in one payment — 101 at most',
      sumMismatch: 'The payment amount is not the sum of its lines',
      carrierMismatch: 'One payment pays one directory carrier only',
      carrierNotInDirectory: 'The carrier is not in the directory — pick it from the directory on the leg',
      counterpartyPick: 'Pick a counterparty from the directory',
      counterpartyUnknown: 'This counterparty is not in the directory',
      /* The payment's party (finance/payment_party.go): an incoming payment
         naming a directory counterparty, and a payment naming both the client
         and a counterparty. The form sends neither — only a stale tab does. */
      partyDirection:
        'An incoming payment cannot come from a directory counterparty — pick the client or another payer',
      withCounterparty: 'Name one party: the client or a counterparty',
      /* The leg units' FIELD codes (S-18b): a key the server no longer reads —
         only a tab opened before the server was updated sends one — and a
         field this kind of unit does not carry (`units.<i>.<field>`). */
      retired: 'This page is out of date — reload it',
      notForKind: 'This field does not apply to this kind of unit',
      /* finance/debitnote_service.go (S-22a): a new debit note bills this
         case's client only — on `counterparty`. */
      clientOnly: 'A debit note can be billed to this case’s client only',
      /* directory (S-22b): only two counterparties of one category merge — the
         `category` FIELD code. */
      mismatched: 'Does not match',
      /* The `planned` FIELD code on the folded expense write (S-4.1): an
         unplanned cost never gains a plan after the fact — such a plan is what
         hid the overrun. The sentence says what to do instead. */
      unplannedLine:
        'An unplanned cost cannot be given a plan — delete it and enter it again with a plan',
      invalidNumber: 'Enter a number',
      /* A figure that IS a number but carries more precision than the column
         that stores it. Said separately from invalidNumber because "Enter a
         number" over 3.607 sends the reader looking for a fault that is not
         there. */
      tooManyDecimals: 'Use at most 2 decimal places',
      /* Blocking messages name the block and the reason together. */
      fixFields: 'Some fields need attention before this can be saved.',
      active: 'Active',
      inactive: 'Inactive',
      optional: 'Optional',
      /* The summary on every disclosure that hides the rest of a form. One
         word, used on all of them, because a reader who learned where the rare
         fields live on one form must find them in the same place on the next.
         The count beside it is the component's. */
      more: 'More',
      selectPlaceholder: 'Select…',
      searchPlaceholder: 'Search…',
      noMatches: 'No matches',
      noMatchesHint: 'Try a shorter search',
      /* The country picker (ISO 3166-1). The closed control is a button now, so
         its placeholder names the CHOICE; the field that advertises what may be
         typed is the search at the top of the list, below. */
      countryPlaceholder: 'Select country…',
      /* The filter inside the country popup. It names the code forms because
         the search matches them as well as the name, and a box labelled only
         "Search…" does not advertise that "RUS" or "398" work. */
      countrySearch: 'Country, code…',
      /* The phone field's two halves: the calling-code button and the number
         beside it. The button's own text is "+7", which says nothing about what
         it is, so the label supplies the noun. */
      phoneCode: 'Country calling code',
      phoneSearch: 'Country, +code…',
      phonePlaceholder: 'Phone number',
      /* Shown against a saved value that is not one of the 249 codes — a port
         or city entered before this field became a picker. It stays selectable:
         the record is not wrong, it is older than the control. */
      countryCarriedOver: 'as entered',
      /* Announced to the filter's own field as it types, so the effect of a
         keystroke is heard without leaving the box to count rows. */
      matchCount_one: '{{count}} of {{total}} shown',
      matchCount_other: '{{count}} of {{total}} shown',
      clear: 'Clear',
      /* The date picker's own strings. The placeholder shows the ISO shape the
         field accepts rather than a localised example, because the value is
         typed as digits and `YYYY-MM-DD` is the only form it stores. */
      datePlaceholder: 'YYYY-MM-DD',
      openCalendar: 'Open calendar',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      today: 'Today',
    },
    /* The unsaved-changes guard on an editing sheet.
     *
     * A question rather than a warning, and it names what is at stake: "are you
     * sure?" over an unnamed loss makes the reader guess what they are agreeing
     * to. The primary action here is CANCELLING the close — the safe one — so
     * a reflexive Enter keeps the draft rather than discarding it. */
    unsaved: {
      title: 'Discard your changes?',
      body: 'This form has edits that have not been saved. Closing it loses them.',
      keep: 'Keep editing',
      discard: 'Discard changes',
    },
    menu: {
      open: 'Open navigation',
      close: 'Close navigation',
    },
    loading: 'Loading',
    /* ONE vocabulary, shared by every finance view and the dashboard
       (components/data/DateRangeControl.tsx). `1W` was removed with the second
       vocabulary that declared it: a week of receivables is noise around a
       single invoice, and two sets of periods let two surfaces answer
       different questions while looking alike. */
    dateRange: {
      label: '{{label}} date range',
      shared: 'Date range',
      '1M': '1M',
      '3M': '3M',
      '1Y': '1Y',
      all: 'All',
    },
    /* Suppressed fields (AD-6). A field a role may not see is ABSENT from its
     * DTO, never nulled, and the UI says so rather than rendering an empty
     * cell — suppression must never be misread as missing data. */
    suppressed: {
      agent: 'Agent hidden',
      carrier: 'Carrier hidden',
      vendor: 'Vendor hidden',
      contact: 'Contact restricted',
      generic: 'Hidden',
      reason: 'Your role does not show this field',
    },
    /** Terse, unitful ages for event feeds — see lib/format.formatRelativeTime. */
    relativeTime: {
      now: 'now',
      minutes: '{{count}} min',
      hours: '{{count}} h',
      days: '{{count}} d',
    },
  },

  nav: {
    label: 'Main',
    sections: {
      main: 'Main',
      company: 'Company',
    },
    dashboard: 'Dashboard',
    myTasks: 'My tasks',
    quotes: 'Quotes',
    cases: 'Cases',
    chat: 'Chat',
    finance: 'Finance',
    clients: 'Clients',
    counterparties: 'Counterparties',
    directories: 'Directories',
    settings: 'Settings',
    profile: 'Profile',
  },

  roles: {
    sales: 'Sales',
    pricing: 'Pricing',
    operations: 'Operations',
    finance: 'Finance',
    'super-admin': 'Super-admin',
  },

  /* What each SEEDED role is for, shown while its stored description is still
     exactly the one migration 0021 seeded (`roleDescription` in
     lib/navigation.ts). An admin's own words replace these in every language. */
  roleDescriptions: {
    sales: 'Owns the client relationship and the quote pipeline.',
    pricing: 'Builds the cost side of quotes and maintains agents.',
    operations: 'Runs shipments once a quote converts to a case.',
    finance: 'Invoices, payments, receivables and the cross-case views.',
    'super-admin': 'Full access, user management, and the roles themselves.',
  },

  modes: {
    sea: 'Sea',
    rail: 'Rail',
    road: 'Road',
    air: 'Air',
    multimodal: 'Multimodal',
  },

  caseStatus: {
    draft: 'Draft',
    'in-progress': 'In progress',
    stuck: 'Stuck',
    delivered: 'Delivered',
    // The two-stage close FR-25 runs on, and BOTH halves say which one they
    // are. "Closed" alone was the second lane's label and it was the wrong
    // word beside "Ops closed": read down a board, the pair asked the reader
    // to infer that the unqualified one meant the money, which is exactly the
    // guess a lifecycle label exists to remove.
    'operationally-closed': 'Ops closed',
    'financially-closed': 'Financially closed',
    cancelled: 'Cancelled',
  },

  quoteStatus: {
    draft: 'Draft',
    sent: 'Sent',
    rejected: 'Rejected',
    expired: 'Expired',
    converted: 'Converted',
  },

  docStatus: {
    draft: 'Draft',
    issued: 'Issued',
    'part-paid': 'Part paid',
    paid: 'Paid',
    /* There is no `void` here, deliberately. AD-24 makes Draft → Issued
       one-way and nothing in this application moves a document back out of
       Issued: a correction is a NEW document, which is how the paper world
       does it. A pill for a state no act can produce is an affordance for
       something that cannot happen — the same defect the money section had
       when it told a founder with every role that money was hidden from him.
       `part-paid` and `paid` are not stored either; they are derived on the
       client from the served figures. But they are real readings of a real
       document, which is why those two stay and `void` went. */
  },

  documentKind: {
    invoice: 'Invoice',
    'debit-note': 'Debit note',
    offer: 'Offer',
    /* The status PDF the send filed against the case before S-15. The record
       of a send is now the «Sent statuses» journal; the old documents still
       list among the case's paper. */
    'status-update': 'Status update',
  },

  /* What a route point IS (migration 0062) — shared by quote route options,
     case leg points and direction templates. Keys are the wire values. */
  routePoints: {
    kind: 'Point type',
    kindN: 'Type of point {{n}}',
    kinds: {
      border_crossing: 'Border crossing',
      customs_terminal: 'Customs terminal',
      waypoint: 'Waypoint',
    },
    transshipment: 'Transshipment',
    transshipmentYes: 'Yes',
  },
  legStatus: {
    planned: 'Planned',
    'in-transit': 'In transit',
    'waiting-border': 'Waiting at border',
    arrived: 'Arrived',
    completed: 'Completed',
  },

  /* A counterparty's category (S-22b). The server sends the CODE; the words
     are here. */
  counterpartyCategory: {
    carrier: 'Carrier',
    forwarder: 'Forwarder',
    'customs-broker': 'Customs broker',
    driver: 'Driver',
    agent: 'Agent',
    other: 'Other',
  },

  /* The directory's participant types before S-22b. Kept only for old bell
     rows that name a role in these words. */
  participantRole: {
    shipper: 'Shipper',
    consignee: 'Consignee',
    agent: 'Agent',
    'counter-agent': 'Counter-agent',
    'vehicle-owner': 'Vehicle owner',
    dispatcher: 'Dispatcher',
    driver: 'Driver',
  },

  notificationKind: {
    message: 'Message',
    status: 'Status',
    approval: 'Approval',
    payment: 'Payment',
    expense: 'Cost',
    pricing: 'Pricing',
    document: 'Document',
    file: 'File',
    assignment: 'Assignment',
    stuck: 'Stuck',
  },

  recordKind: {
    case: 'Case',
    quote: 'Quote',
    invoice: 'Invoice',
  },

  /* «Мои задачи» (S-2b.1, the /tasks route). The server sends the sections
     and their order; the titles and lines are here, keyed by the section. The
     "since" line names the event the record has waited since. */
  myTasks: {
    title: 'My tasks',
    subtitle: 'The quotes, cases and money waiting on you.',
    eyebrow: '{{date}} · Data as of {{time}}',
    nothingWaiting: 'Nothing waiting',
    /* The server sends at most 50 records and the real total. No "Show all"
       link: the quote and case lists cannot filter by these rules. */
    more: 'and {{count}} more',
    /* The strip above the dashboard shows up to five rows a section; the rest
       are on «My tasks». */
    allTasks: 'All tasks',
    /* One line for Finance: the to-pay figure — the dashboard's own — and a
       link to where it is worked. The link promises no list that sums to it. */
    toPay: {
      label: 'To pay',
      open: 'Open balances',
    },
    noSections: {
      title: 'No sections for your role',
      description:
        'This page gathers what is waiting on you: pricing, sending the offer, legs, trucks, actual costs, rate approvals, confirming payments. Your role does none of these.',
    },
    sections: {
      'pricing-requested': {
        title: 'Awaiting your price',
        hint: 'You were asked for the price.',
        since: 'Asked {{date}}',
      },
      'pricing-ready': {
        title: 'Priced, offer not sent',
        hint: 'The price is worked out — the offer can go to the client.',
        /* The later of when the price was ready and when the offer was last
           sent: a reopened quote stays here, so the label is true of either. */
        since: 'Unsent since {{date}}',
      },
      'awaiting-client': {
        title: "Awaiting the client's answer",
        hint: "Sent a few days ago and still valid, and the client's answer is not recorded.",
        since: 'Sent {{date}}',
      },
      /* Sent offers whose validity ends within the warning window (a Settings
         threshold). The row's date is the last day the offer stands. */
      'expiring-soon': {
        title: 'Offers about to expire',
        hint: "Sent, the client's answer is not recorded, and the offer's validity is running out.",
        until: 'until {{date}}',
      },
      'case-no-legs': {
        title: 'Cases with no legs',
        hint: 'The case has no leg yet.',
        since: 'Opened {{date}}',
      },
      'case-no-vehicle': {
        title: 'Legs with no truck',
        hint: 'A road leg, the cargo not yet delivered, and no truck added.',
        since: 'Leg added {{date}}',
      },
      'case-no-actuals': {
        title: 'Cases with no actual costs',
        hint: 'The case is in progress or operationally closed, and no actual cost is recorded.',
        since: 'Opened {{date}}',
      },
      /* S-12: a leg's truck rates detail no plan line and are not marked
         "not detailing", while the case's plan holds a line nobody details —
         the carriage may be counted twice. The row opens the first such leg. */
      'case-truck-double-count': {
        title: 'Truck rates counted twice',
        hint: "A leg's truck rates are not linked to a plan line while the plan holds a carriage line — it may be counted twice.",
        since: 'Since {{date}}',
      },
      /* The approvals tab's own queue: a row is the case number, who asked and
         the change. The reason is shown on the tab only. */
      'rate-approvals': {
        title: 'Rate approvals',
        hint: 'A rate change was requested and needs your decision.',
      },
      /* A count only: the server sends no payer and no amount. */
      'payments-to-confirm': {
        title: 'Payments to confirm',
        hint: 'Recorded and not yet confirmed.',
        awaiting_one: '{{count}} payment awaits confirmation',
        awaiting_other: '{{count}} payments await confirmation',
      },
    },
  },

  dashboard: {
    /* The greeting is time-of-day aware. Russian splits the day differently, so
     * the boundaries live in the component and both catalogues simply supply
     * three greetings. */
    greeting: {
      morning: 'Good morning, {{name}}',
      afternoon: 'Good afternoon, {{name}}',
      evening: 'Good evening, {{name}}',
    },
    eyebrow: '{{date}} · Data as of {{time}}',
    /* The count is the «Needs a decision» rows plus the strip's tasks. */
    subtitle_one: '{{count}} item is waiting on your decision or action.',
    subtitle_other: '{{count}} items are waiting on your decision or action.',
    loading: 'Loading dashboard',

    counters: {
      activeCases: 'Active cases',
      shipmentsThisMonth: 'Shipments this month',
      toPay: 'To pay',
    },

    /* The money tiles. A label here is not a promise that the figure exists:
       a figure the server could not compute is declared `pending`, and its
       tile states its own absence rather than rendering an empty card.

       Turnover (Story B7) is what was billed this calendar month: issued
       invoices and client debit notes, at booked USD, NET of VAT — so it does
       not reconcile with "To collect", which is gross. "This month" is in the
       label because the tile has no range control. */
    tiles: {
      turnover: 'Turnover this month',
      /* "Profit", not "Forecast profit": FR-51 AC 2 makes this one figure over
         two bases — what a financially closed case actually earned, and what
         every other case is forecast to earn — so naming it after one of the two
         would misdescribe the other half of its own total. */
      profit: 'Profit',
      /* "To collect" and "To pay", not "Accounts receivable/payable": the
         founder's own words, and each says what to do about it. */
      toCollect: 'To collect',
      toPay: 'To pay',
      /* The drill-through affordance (UX-DR-10). One label for every tile
         rather than one per destination: the tile's own heading already says
         which figure is being opened, and "View receivables" beside a heading
         reading "To collect" says it twice. */
      drillThrough: 'View list',
    },

    pending: {
      /* A tile the server declared `pending`. The sentence promises nothing: the
         tile is unbuilt, and a story number is a flag for the code, not news for
         the reader (QW-4). The server still sends `story`; it never reaches the
         screen. */
      description: 'Not calculated yet.',
    },

    attention: {
      title: 'Needs a decision',
      /* Loss-making and stuck cases only: rate approvals and payments to
         confirm moved to the tasks strip above the dashboard (S-2b.2). */
      emptyTitle: 'No loss-making or stuck cases',
      /* The threshold comes from the server, so the sentence states the rule
         actually applied rather than a number copied into the client. */
      emptyDescription_one:
        'A shipment appears here after {{count}} day without movement.',
      emptyDescription_other:
        'A shipment appears here after {{count}} days without movement.',
      stuckFor_one: 'No movement for {{count}} day',
      stuckFor_other: 'No movement for {{count}} days',
      /* Which figure is negative on a minus shipment (FR-52). The distinction
         matters to what the founder does next: a forecast is still recoverable
         while the shipment runs, a final figure is the loss as booked. */
      minusBasis: {
        forecast: 'Forecast to lose money',
        final: 'Closed at a loss',
      },
      kind: {
        stuck: 'Stuck',
        minus: 'Minus',
        overdue: 'Overdue',
        checklist: 'Incomplete',
        'quote-followup': 'Follow up',
      },
    },

    activity: {
      title: 'Recent activity',
      empty: 'No activity yet',
    },

    aging: {
      caption: '{{clients}} clients · {{tranches}} tranches due this week',
    },
  },

  cases: {
    conversionLegSkipped:
      'The case was created without a leg: the selected delivery option has no transport mode. Add the leg on the case.',
    title: 'Cases',
    subtitle: 'Every case in motion, and what it is worth.',
    // A deep link from a role with no access to cases (#129).
    noAccess: {
      title: 'This section is not available',
      description:
        'Cases open with your role’s access to the Cases area. Ask an administrator if you need it.',
    },
    loading: 'Loading case',
    backToList: 'Back to cases',
    detailNotReady: 'The case detail screen is not connected yet — {{number}} was created and is in the list.',
    sectionsLabel: 'Case sections',
    actions: {
      fromQuote: 'From quote',
      newCase: 'New case',
      editCase: 'Edit case',
      create: 'Create case',
      sendStatus: 'Send status to client',
    },

    /* Telling the client where their shipment is (FR-41).
       The sheet names the recipient before the send, because an email leaves
       permanently and the address is the one fact worth checking first. */
    statusSend: {
      title: 'Send status to client',
      submit: 'Send',
      recipient: 'To',
      status: 'Status',
      language: 'Language',
      languageHint: "The language your client reads, not yours.",
      note: 'Note',
      noteHint: 'Optional. Added below the status and the arrival date.',
      notePlaceholder: 'Anything the client should know beyond the status.',
      /* Not an error: nothing failed, the directory is simply incomplete. */
      noContact:
        'This client has no email contact yet. Add one in the client directory to send them updates.',
      sent: 'Status sent to {{email}}.',
      failed: 'The status was not sent. Check the connection and try again.',
      /* A role without access to the client's contacts: the server refuses
         (403), because the send would reveal the address. Not "no email" —
         the directory is fine. */
      noContactAccess: "You have no access to the client's contacts — a colleague who has it can send the status",

      /* The email as the client will receive it, rendered by the server with
         the send's own template, under the fields that change it. */
      preview: {
        title: 'Preview',
        hint: 'This is what the client will see. It refreshes when the language changes and a second after the note is edited.',
        loading: 'Preparing the email preview…',
        frameTitle: 'Status email to the client',
        subject: 'Subject',
        noneSelected: 'Tick at least one case to see the email here.',
      },
    },

    /* The same act from the LIST: one client, several of their cases, one
       send. Asked in the order the work happens — who first, then which. */
    clientStatusSend: {
      title: 'Send status to client',
      client: 'Client',
      clientPlaceholder: 'Choose a client',
      pickClientFirst: 'Choose a client to see their cases.',
      cases: 'Cases',
      selectAll: 'Select all',
      selectedCount: '{{selected}} of {{total}}',
      submitCount_one: 'Send 1 case',
      submitCount_other: 'Send {{count}} cases',
      noteHint: 'Optional. Added to every case in this send.',
      noContact:
        'This client has no email contact yet. Add one in the client directory to send them updates.',
      /* Distinct from noContact because the action differs: nothing here is
         anybody's mistake, so there is nothing to go and fix. */
      nothingReportable:
        'Every shipment for this client is cancelled or closed, so there is no status to report.',
      notReportable: 'Cancelled or closed — not included',
      sentAll_one: 'Status sent for 1 case.',
      sentAll_other: 'Status sent for {{count}} cases.',
      sentPartial: 'Status sent for {{sent}} of {{total}} cases.',
    },

    /* The send journal: every status email that went to the client on this
       case, exactly as it went. Shown in the «History» sheet. */
    statusSends: {
      title: 'Sent statuses',
      subtitle: 'Every status email that went to the client on this case.',
      loading: 'Loading the sent statuses…',
      empty: 'No status has been sent to the client on this case yet.',
      /* The email went as a summary — one email about several of the client's cases. */
      batch: 'summary',
      batchHint: "One email about several of the client's cases",
      noRecipientName: 'No name',
      openLabel: 'Open the email of {{date}}',
      viewerTitle: 'Email of {{date}}',
      subject: 'Subject',
      sentBy: 'Sender',
      recipient: 'To',
      /* The summary covered a case outside the reader's scope — the server serves neither subject nor body. */
      bodyWithheld: 'This summary covers shipments outside your access, so the email is not shown.',
    },

    /* Creating a case WITHOUT a quote is a primary path (FR-70): repeat
       business runs through it, and the migrated cases arrive quote-less. */
    form: {
      /* The FR-32 trio. A pick from four rather than free text: the terms have
         to become dated expectations the platform can chase (FR-33), and no
         parse of a typed phrase yields two dated amounts. */
      paymentTermsKind: 'Payment terms',
      paymentMethod: 'Payment method',
      creditDays: 'Credit days',
      creditDaysHint: 'Days from registration until the whole amount is due.',
      creditDaysInvalid: 'Enter a whole number of days, from 1 to 365.',
      nextStep: 'Legs and money are added on the case itself.',
    },
    /* The five kinds, keyed by the WIRE value so a lookup is
       t(`cases.terms.${kind}`) rather than a switch that silently falls
       through on a kind this build has not heard of. */
    terms: {
      in_advance: 'Prepayment',
      fifty_fifty: '50-50',
      full_on_delivery: 'Full upon delivery',
      credit: 'Credit',
      other: 'Other',
    },
    method: {
      cash: 'Cash',
      bank_transfer: 'Bank transfer',
    },
    columns: {
      number: 'Case',
      client: 'Client',
      route: 'Route',
      leg: 'Leg',
      status: 'Status',
      eta: 'ETA',
      rate: 'Rate USD',
      /* The figure on the right of the case's «Profit» line (S-6): the
         forecast while the case runs, the final figure once it is financially
         closed or cancelled. */
      profit: 'Profit USD',
    },
    card: {
      /* The board card's figures block. Short labels, because they are read
         once and then ignored — the column of values beside them is what the
         eye actually runs down. The dates keep their shipping-trade names:
         ETD and ETA are what the desk says out loud, in both languages. */
      rate: 'Rate',
      profit: 'Profit',
      /* Where the cargo is on the route, on the board card. A POSITION, not a
         countdown — "Leg 2/3" says both how far it has come and how much is
         left, where "1 leg to go" says only the second and reads as a promise. */
      leg: 'Leg {{sequence}}/{{total}}',
      etd: 'ETD',
      eta: 'ETA',
      /** The compact stalled marker on a card, where the full sentence will not fit. */
      stalledShort: '{{count}} d',
    },

    /* Dragging a card to another lane is a real status change (FR-25, AD-24).
       The message names the case because the card has already slid back by the
       time it is read, and points at the one refusal a person can act on. */
    board: {
      moveRefused:
        'Case {{number}} was not moved. The server refused the change — a financial close needs every balance settled first.',
      /* An empty lane, in words. The invitation is a SECOND key because it is
         only true of a lane something may actually be dragged into — printing
         it over Cancelled would teach a gesture that lane refuses. */
      laneEmpty: 'No cases',
      laneDrop: 'Drag one here to change its status.',
    },

    /* The lifecycle on the RECORD (FR-25, FR-37, AD-24), where the board's
       drag has an opposite number that stops to ask. Two of the moves have
       consequences a person should not discover having caused, so the copy
       names what happens rather than asking "are you sure". */
    lifecycle: {
      /* FR-25's refusal, naming WHICH balance is outstanding rather than
         grateful silence over a greyed-out control. */
      unsettledBalances:
        'This case cannot be closed financially while money is outstanding: {{items}}.',
      label: 'Case lifecycle',
      /* The status names come from the `caseStatus` catalogue above, so a
         relabelled state changes here too rather than in two places. */
      change: 'Change status',
      moveTo: 'Move to {{status}}',
      moving: 'Moving…',

      confirmTitle: 'Confirm the change',
      confirmFinancialBody:
        'Closing {{number}} financially ends the money side of this case: it stops accepting invoices and payments. Only a Super-admin can reopen it afterwards, and the reopening is recorded.',
      confirmFinancialAction: 'Close financially',
      confirmCancelBody:
        'Cancelling {{number}} is permanent. The case keeps its history and its quote link, but it cannot be moved to another status afterwards.',
      confirmCancelAction: 'Cancel case',

      /* Not a generic refusal: the server names the balances that are holding
         the close, and they are listed under this line (FR-25). */
      balancesTitle: 'This case cannot be closed financially yet',

      reopen: 'Reopen',
      reopenTitle: 'Reopen {{number}}',
      reopenSubmit: 'Reopen case',
      reopenBody:
        'The case returns to Ops closed. Reopening undoes the financial close only — the delivery is not reopened with it.',
      reason: 'Reason',
      reasonHint:
        'Recorded in the case history with your name. Reopening without a reason is not possible.',
    },
    stalled_one: 'No movement for {{count}} day',
    stalled_other: 'No movement for {{count}} days',
    /* The unfiltered empty list (QW-9, C-7). The server picks the rows by the
       reader's access (AD-5) and the client does not know those rules, so the
       sentence names none: it says what the list holds and what to do when a
       colleague's case is missing. "No cases yet" was false for a reader whose
       colleague is already running one they cannot see. */
    emptyTitle: 'No cases to show',
    emptyDescription:
      'This list shows the cases open to you. Waiting for a colleague’s case? Ask to be added to its team.',

    header: {
      rate: 'Rate',
      cost: 'Cost',
      /** The cost figure's basis caption — «Cost · forecast»: actuals where a
       *  line has them, plan where it does not. */
      costBasis: 'forecast',
      profit: 'Profit',
      mode: 'Mode',
    },

    /* The case page's six sections (S-11 and its revision). "Team" is the
       COLLEAGUES working the case. "Client billing" is what the client is
       invoiced and has paid, "Costs" what the case costs and what each
       supplier is owed. "Documents" is the `files` key: the case's paper and
       its files together; the old `?section=documents` is an alias that lands
       on the invoices in "Client billing" (or here, for a reader without
       Finance's workspace). The history opens from the header's «⋯». */
    tabs: {
      overview: 'Overview',
      legs: 'Legs',
      team: 'Team',
      billing: 'Client billing',
      costs: 'Costs',
      files: 'Documents',
    },

    /* «Client billing» (S-11 revision): four tiles on top. Every figure is
       the server's (`clientTotals`); a negative «Client owes» reads as
       «Client advance», without the minus. */
    /* «Costs» (S-11 revision): four tiles and «Supplier payments». Every
       figure is the server's (`plannedCost`, `actualToDate`, `suppliers`).
       «Owed to suppliers» is the forecast basis with vendorless lines
       included, not FR-30's «Payables». */
    costs: {
      tilesLabel: 'Cost totals',
      planned: 'Plan',
      actual: 'Actual',
      paid: 'Paid',
      outstanding: 'Owed to suppliers',
      suppliers: {
        title: 'Supplier payments',
        /* For Sales: the server serves no groups and no names (AD-6). */
        hidden: 'Suppliers hidden',
        unattributed: 'No supplier / unallocated',
        debt: 'owed',
        paid: 'paid',
        outstanding: 'left',
        noPayments: 'No payments',
        emptyTitle: 'No supplier payments yet',
        emptyDescription: 'Costs by supplier and the payments that settle them appear here.',
        unallocated: 'unallocated',
        /* The other rows of a payment split between suppliers: its acts are
           on one row only — the first, or its remainder. */
        partOf: 'part of payment {{reference}}',
        columns: {
          date: 'Date',
          leg: 'Leg / unit',
          reference: 'Reference',
          amount: 'Amount',
          status: 'Status',
          acts: 'Actions',
        },
      },
    },
    billing: {
      tilesLabel: 'Client totals',
      rate: 'Rate',
      noRate: 'No rate agreed',
      invoiced: 'Invoiced',
      invoicedNote: 'Invoices and debit notes to the client',
      received: 'Received',
      receivedUnallocated: 'of which unallocated {{amounts}}',
      /* S-22f: a refund to the client — a note under «Received», never a
         fifth tile; «Received» is already net of it. The amount as served. */
      refunded: 'Refund: {{amount}}',
      debt: 'Client owes',
      advance: 'Client advance',
      advanceNote: 'The client paid more than was invoiced',
    },
    /* The header's «⋯» (S-11): the history left the rail for it, as on the
       quote. */
    more: {
      label: 'More',
      history: 'History',
    },
    /* The drawer a cost line's leg cell opens in «Money» (S-11): that leg's
       carrier money, read-only, and the way to pay the carrier. */
    legDrawer: {
      leg: 'Leg {{n}} · {{mode}}',
      planned: 'Plan',
      actual: 'Actual',
      /* The LEG's figure beside the LINE's two: what is still owed to its
         carriers across every line on the leg. */
      legOutstanding: 'To pay on the leg',
      payments: 'Payments by unit',
      openLeg: 'Open leg {{n}}',
      pay: 'Pay carrier',
      loading: 'Loading the leg',
      noMoney: "The leg's money is not available right now. Everything about it is on the leg's page.",
    },

    overview: {
      route: 'Route',
      /* "rail + road via Khorgos" — modes come in already localised. */
      routeVia: '{{modes}} via {{border}}',
      stopRole: {
        loading: 'POL',
        border: 'Border crossing',
        discharge: 'POD',
        unloading: 'Unloading place',
      },
      departed: 'departed {{date}}',
      arrivedOn: 'arrived {{date}}',
      hereNow: 'here now',
      hereNowFor: 'here now, {{days}} d',
      etaOn: 'ETA {{date}}',

      cargo: 'Cargo',
      imo: 'IMO {{class}}',
      kg: 'kg',
      cbm: 'CBM',
      declaredTotal: 'Declared total',
      measuredTotal: 'Measured',
      differsFromDeclared: 'differs from declared',
      measuredAt: 'Measured at {{location}}',

      containers: 'Containers and seals',
      containersEmptyTitle: 'No containers yet',
      containersEmptyDescription:
        'Container and seal pairs populate the documents this case issues.',
      seal: 'Seal',
      equipment: 'Equipment',

      /* «Case details» split into two cards of the overview's even grid
         (S-11 revision): what was agreed, and when and by whom. */
      terms: 'Commercial terms',
      schedule: 'Dates and owners',
      dates: 'Dates',
      transit: 'Transit time',
      incoterm: 'Incoterms',
      paymentTerms: 'Payment terms',
      paymentMethod: 'Payment method',
      creditDays_one: '{{count}} day',
      creditDays_other: '{{count}} days',
      responsible: 'Responsible',
      opsNamed: 'Ops: {{name}}',
      entity: 'Issuing entity',
      packingList: 'Packing list',
      packingListReceived: "Final list received — client's figures",
      packingListEstimate: 'Not received — our estimate',
      notes: 'Notes',
      routeEmpty: 'No route set yet',
      cargoEmpty: 'No cargo described',
      /* The containers card lists four and folds the rest. */
      moreContainers: '{{count}} more',
      /* The case's profit, as a card of the overview (S-11 revision). */
      profit: 'Profit',
    },

    legs: {
      /* FR-67's refusal. The names come from problem.errors.expenses and are
         interpolated, never concatenated in English on the server — see
         refusalMessage in CaseSections.tsx. */
      carriesExpenses:
        'This leg cannot be deleted while costs are recorded against it: {{items}}. Move or remove them first.',
      add: 'Add leg',
      edit: 'Edit leg',
      /* The card's accessible name: it is a LINK to the leg's page now rather
         than a block that opens a form. It names the route as well as the
         number, because "Open leg" four times in a list of four cards does not
         say which one is being opened. */
      open: 'Open leg {{n}}: {{from}} → {{to}}',
      carrier: 'Carrier',
      /* The carrier picker (FR-40). Two of the seven types are offered — the
         counterparty who moves cargo and the company whose truck it is — and
         the row says which, because a haulier and a truck owner are different
         relationships and the same company is sometimes both. */
      carrierPlaceholder: 'Select carrier…',
      carrierSearch: 'Carrier name…',
      /* Shown against a saved carrier that matches no directory row: a name off
         a waybill, entered before this field became a picker. It stays
         selected — the record is not wrong, it is older than the control. */
      carrierAsEntered: 'as entered',
      /* Shown against a linked carrier the desk has since deactivated. Still
         selectable, for the reason above: the movement happened. */
      carrierInactive: 'inactive',
      /* The escape hatch inside the picker: the carrier standing in front of
         somebody is frequently not in the directory yet, and making them leave
         the leg to add one is how a leg gets saved with no carrier at all. */
      carrierAdd: 'Add a carrier',
      /* The directory sheet that button opens, titled as what it adds. */
      carrierNew: 'New carrier',
      carriersCount_one: '{{count}} carrier',
      carriersCount_other: '{{count}} carriers',
      carriersList: "The leg's carriers",
      reference: 'Reference',
      /* The leg's document number, named by the paper its mode travels on —
         the split the leg page's header makes (S-8): CMR on the road, the
         waybill on every other mode the client knows. `reference` above while
         the mode is unset or unknown. The road hint says where the OTHER
         "CMR no." lives: each truck carries its own. */
      referenceCmr: 'CMR no.',
      referenceWaybill: 'Waybill no.',
      referenceCmrHint: 'Each truck has its own CMR no. — on the “Trucks” card',
      /* The country beside each endpoint (FR-19). The PLACE stays what it was —
         a terminal, a siding, a yard — and this is the second, structured fact
         a corridor report groups by. */
      originCountry: 'Origin country',
      destinationCountry: 'Destination country',
      /* The section's summary line: "2 legs · rail + road via Khorgos". */
      count_one: '{{count}} leg',
      count_other: '{{count}} legs',
      legN: 'Leg {{n}}',
      /* The leg's paper, in the trade's words: CMR on the road, the waybill
         everywhere else. */
      waybill: 'Waybill',
      cmr: 'CMR',
      notIssued: 'not issued',
      /* The CONVEYANCE, labelled per mode (FR-26). One label reading "flight /
         voyage number" would make the desk work out which half applies to them
         on every leg; the card and the sheet know the mode already. */
      flightNumber: 'Flight',
      voyageNumber: 'Voyage',
      /* The control a road or rail leg keeps the voyage behind while it has
         none (QW-8): a truck has no voyage. The containers are leg units
         since S-18b and are no longer on the form. */
      revealVoyage: 'Voyage',
      trucks: 'Trucks',
      /* Containers as CNTR/SEAL pairs (FR-67) — the leg card's count. */
      containers: 'Containers',
      /* An unsealed box says so. Empty means NOT YET SEALED or not yet known —
         never "unsealed" — and a blank beside the number would read as a
         container nobody had to seal. */
      sealNotRecorded: 'seal not recorded',
      datePlanned: '{{date}} planned',
      /* The slip against the ETA, beside the actual: "20 Jul (+1 d)". */
      delay: '(+{{count}} d)',
      /* How long the leg is planned to take, SERVED (casefile.Leg.TransitDays)
         rather than subtracted here — two rules about which end counts is two
         answers on one screen. */
      duration: 'Duration',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      /* Zero days is a real span, not a missing one: a truck that loads and
         delivers between two towns does it in a morning. Said in words, because
         "0 days" reads as a leg nobody has dated. */
      sameDay: 'same day',
      /* The route strip's span: the first departure to the last arrival. No day
         count beside it — the legs carry the counts the server computes, and a
         total added up here would be a second rule about gaps between legs. */
      span: '{{from}} — {{to}}',
      spanFrom: 'from {{date}}',
      /* The marker on the leg the cargo is on. The server decides which one
         (casefile's `legnow`); this only names it. */
      currentLeg: 'Current leg',
      expenses: 'Expenses',
      plannedExpense: 'Planned expense',
      /* What has gone out to whoever moves this leg, and what is left on it
         (issue #35 story 6). Composed per leg from finance's own read, so the
         card and the direction's page state one figure. "Carrier" in both
         labels because the case's own payables are a different question. */
      paidToCarrier: 'Paid to carrier',
      carrierOutstanding: 'Owed to carrier',
      /* The bulk truck table on the direction's page (FR-67, story 6). A
         direction is routinely booked as six trucks in one call, so the strip
         on top fills the new rows and each row may still differ. */
      vehiclesTable: {
        newRows: 'New rows:',
        cellLabel: '{{column}}, row {{n}}',
        fillAll: 'all',
        fillCarrier: 'Default carrier into every new row',
        fillRate: 'Default rate and currency into every new row',
        moreFieldsNamed: 'CMR number and container of truck {{plate}}',
        /* The phone sheet's button; {{unit}} is the kind's noun
           (cases.legs.units.noun). */
        removeRow: 'Remove {{unit}}',
        /* {{unit}} is the unit as an object ("truck 01KZ123ABC",
           cases.legs.units.object); {{label}} the same as a name ("Truck
           01KZ123ABC", cases.legs.units.label). */
        removeHeld: 'It carries costs or payments, so it cannot be removed',
        removeHeldNamed: '{{label}} cannot be removed: it carries costs or payments',
        removeRateHeld: 'The rate is waiting to be recorded, so this cannot be removed; «Cancel» discards the rate',
        removeRateHeldNamed:
          '{{label}} cannot be removed: its rate is waiting to be recorded; «Cancel» discards the rate',
        plateHeld: 'The rate is waiting to be recorded, so the number cannot change; «Cancel» discards the rate',
        pasteHint: 'Paste a list of numbers and the rows add themselves',
        pasteRefused_one:
          '{{count}} number pasted, but there is room for {{room}}: a leg takes at most {{max}} units. Nothing was added.',
        pasteRefused_other:
          '{{count}} numbers pasted, but there is room for {{room}}: a leg takes at most {{max}} units. Nothing was added.',
        full: 'The leg holds {{max}} units, the most it can take',
        weightKg: '{{weight}} kg',
        /* The pencil on a row. Named by the unit, not by the number: three
           rows carry the same icon and a screen reader hearing "Edit, Edit"
           cannot tell which one it is about to open. */
        editRowNamed: 'Edit {{unit}}',
        groupedColumns: {
          transport: 'Transport', people: 'Carrier and driver', weight: 'Weight, kg',
          rate: 'Rate', paid: 'Paid', actions: 'Actions',
        },
        currency: 'Currency',
        detailOfNone: 'not detailing',
        detailOfFailed:
          'The leg and its rates are saved, but re-filing the plan lines under the chosen group failed.',
        /* The untouched choice when the plan has several lines. It used to
           mean "not detailing" silently, and the truck rates were saved beside
           the quote's transport line — the freight counted twice (UX
           walkthrough T-1). The question is now asked, and a save of new
           rates waits for the answer. */
        detailOfPlaceholder: 'Choose a plan line',
        detailOfRequired:
          'Choose which plan line the rates detail, or “not detailing” if the plan has no carriage line',
        /* The rates a failed press holds were composed with no answer, and the
           plan now has a line they could detail: re-sending them as they are
           would plan the carriage twice, so the press waits — and «Cancel»,
           which discards them, is how the question gets answered. */
        detailOfRequiredHeld:
          'The rates not yet recorded detail no plan line, and the plan now has one they could detail. «Cancel» discards those rates so you can choose.',
        planLinesLoading: 'Loading the plan lines…',
        planLinesFailed:
          'The plan lines did not load — without them the rates have nothing to be linked to.',
        /* The fact outside edit mode: this direction's rates count on their
           own while the plan holds lines nobody details. No figure — the
           client computes no money. */
        unlinkedWarning: 'The leg rates count separately from the plan lines {{lines}}.',
        quotedLine: '“{{line}}”',
        columns: {
          trailer: 'Trailer',
          container: 'Container',
          cmr: 'CMR no.',
          carrier: 'Carrier',
          driver: 'Driver',
          phone: 'Phone / WhatsApp',
          weight: 'Weight, kg',
          rate: 'Rate',
          advance: 'Advance',
          paid: 'Paid',
          pay: 'Payment',
        },
        /* The act on a saved row. Named for the unit rather than the visible
           word alone: several rows carry the same "Pay", and a screen reader
           hearing "Pay, Pay" cannot tell which one it is about to pay for. */
        payRow: 'Pay',
        payRowNamed: 'Pay for {{unit}}',
        removeRowNamed: 'Delete {{unit}}',
        /* What a new truck's rate is recorded AS: one planned cost line on the
           case's money, named for the truck it was agreed for. */
        rateItem: 'Truck rate {{plate}}',
        /* HALF the save landed, and the sentence says which half. Anything
           vaguer would have somebody re-enter six units that are already
           saved. */
        savedTrucksRatesFailed_one:
          'The leg is saved; {{count}} rate was not recorded.',
        savedTrucksRatesFailed_other:
          'The leg is saved; {{count}} rates were not recorded.',
        /* A rate whose batch did not land, shown as the figure «Retry» will
           send. Read-only: the held line is re-sent as itself, and an input
           beside it would show a figure the press does not send. */
        rateHeld: 'Not recorded yet',
        rateHeldMissing: 'No {{currency}} rate',
        rateHeldHint: '«Retry» sends this rate as shown; «Cancel» discards it so it can be entered again.',
        /* The two figures a row can get wrong. Said rather than dropped: a rate
           that does not parse used to save the truck with no rate at all, and a
           weight that does not parse used to erase the kilograms already
           stored — both under a save that reported success. */
        rateInvalid_one: '{{count}} rate is not a number — for example 1200 or 1 200,50.',
        rateInvalid_other:
          '{{count}} rates are not numbers — for example 1200 or 1 200,50.',
        weightInvalid_one: '{{count}} weight is not whole kilograms — digits only.',
        weightInvalid_other: '{{count}} weights are not whole kilograms — digits only.',
      },
      /* The leg's units (S-18b): trucks, containers, wagons, air waybills,
         bills of lading and others — one table, one card per kind. The kind
         arrives as a code and its words are composed here (lib/legUnits.ts). */
      units: {
        title: {
          truck: 'Trucks',
          container: 'Containers',
          wagon: 'Wagons',
          awb: 'Air waybills',
          bl: 'Bills of lading',
          other: 'Other',
        },
        /* A unit by kind and number, where a bare plate used to print. */
        label: {
          truck: 'Truck {{number}}',
          container: 'Container {{number}}',
          wagon: 'Wagon {{number}}',
          awb: 'Air waybill {{number}}',
          bl: 'B/L {{number}}',
          other: '{{number}}',
        },
        /* The same as the object of an action — "Delete truck 01KZ". */
        object: {
          truck: 'truck {{number}}',
          container: 'container {{number}}',
          wagon: 'wagon {{number}}',
          awb: 'air waybill {{number}}',
          bl: 'B/L {{number}}',
          other: '“{{number}}”',
        },
        noun: {
          truck: 'truck',
          container: 'container',
          wagon: 'wagon',
          awb: 'air waybill',
          bl: 'B/L',
          other: 'row',
        },
        rowN: {
          truck: 'Truck {{n}}',
          container: 'Container {{n}}',
          wagon: 'Wagon {{n}}',
          awb: 'Air waybill {{n}}',
          bl: 'B/L {{n}}',
          other: 'Row {{n}}',
        },
        /* A field's name in a row — unique across the page's cards. */
        cellLabel: {
          truck: '{{column}}, row {{n}}',
          container: '{{column}}, container {{n}}',
          wagon: '{{column}}, wagon {{n}}',
          awb: '{{column}}, air waybill {{n}}',
          bl: '{{column}}, B/L {{n}}',
          other: '{{column}}, other, row {{n}}',
        },
        number: {
          truck: 'Plate',
          container: 'Container no.',
          wagon: 'Wagon no.',
          awb: 'AWB no.',
          bl: 'B/L no.',
          other: 'Label',
        },
        columns: {
          seal: 'Seal',
          /* A wagon's seal is its locking-and-sealing device (ЗПУ). */
          zpu: 'Seal (ZPU)',
          unitType: 'Type',
          remaining: 'Remaining',
        },
        /* What a unit's rate is recorded AS — STORED wording, never renamed
           after the fact. A truck keeps cases.legs.vehiclesTable.rateItem. */
        rateItem: {
          container: 'Container rate {{number}}',
          wagon: 'Wagon rate {{number}}',
          awb: 'Air waybill rate {{number}}',
          bl: 'B/L rate {{number}}',
          other: 'Rate {{number}}',
        },
        add: {
          truck: 'Add truck',
          container: 'Add container',
          wagon: 'Add wagon',
          awb: 'Add air waybill',
          bl: 'Add B/L',
          other: 'Add row',
        },
        defaultCarrier: 'Default carrier, {{kind}}',
        defaultRate: 'Default carrier rate, {{kind}}',
        defaultCurrency: 'Currency for new rows, {{kind}}',
        fillCarrier: 'Default carrier into every new row, {{kind}}',
        fillRate: 'Default rate and currency into every new row, {{kind}}',
        empty: 'Nothing recorded yet.',
        addKind: 'Another kind of unit',
      },
      /* Why the leg is read-only for a reader not shown its carrier and driver
         (AD-6, #133). The server keeps the stored carrier and driver for such
         a caller (`keepIdentity`), so the sentence does not claim a save would
         clear them: the page simply takes no input it could not send. One
         sentence for the whole leg — the header, «Leg parameters» and the unit
         cards. */
      identityHidden:
        'The carrier and driver on this leg are not shown to your role, so the leg is open read-only here. Someone whose role sees counterparties can change it.',
      /* `createdTemplateFailed` was here and is gone with the surface that
         needed it: the create no longer holds the reader on a form when only the
         template apply is refused. It lands them on the leg that exists, with
         the refusal above an empty chain and «Apply template» beside it, which
         is the ordinary state of a leg nobody has given a corridor plus one
         sentence — rather than a mode with its own vocabulary. */
      /* Where the leg was last seen (FR-24). The server has carried this since
         Story 3.4 and nothing rendered it. */
      lastLocation: 'Last location',
      reportedAt: 'reported {{at}}',
      /* Deleting a leg. The refusal is the control (FR-67): a leg carrying cost
         lines cannot be deleted, and the server names them. */
      delete: 'Delete this leg',
      deleteConfirm: 'Delete this leg? Its dates, points and containers go with it.',
      deleteConfirmAction: 'Delete leg',
      emptyTitle: 'No legs yet',
      emptyDescription:
        'Add the legs this shipment moves through to track it from origin to delivery.',
    },

    /* One leg on its own page (issue #35 story 3).
     *
     * A leg became a URL because it acquired a ROUTE CHAIN: where the cargo has
     * got to, how many days each transition is allowed, and when the next point
     * is due. A chain is read across, not down a column the width of a modal.
     *
     * The RUSSIAN catalogue calls this «Направление» rather than «Плечо» — the
     * word the desk uses for the named routes this is filled from. English
     * keeps "leg", which is the trade's own English word; the keys are the same
     * on both sides, which is what the parity check is about.
     *
     * The wording keeps two facts apart. The CHAIN is the position; the STATUS
     * is the movement report — a leg can sit at `waiting-border` while its last
     * reached point is Almaty, and one control answering both would be wrong
     * half the time. */
    legPage: {
      dateLabels: { etd: 'Planned departure', eta: 'Planned arrival', atd: 'Actual departure', ata: 'Actual arrival' },
      back: 'back to the case',
      /* S-18a: paying the carrier — a secondary button in the summary row,
         left of «Leg settings». The page's one blue button is the save bar's
         primary act. */
      payCarrier: 'Pay the carrier',
      /* A neutral tag: the chain carries normative days, so the position
         advances on its own (story 4). A FACT about this leg rather than a
         setting — a hand-typed chain with no day counts does not move, and a
         tag on it would promise a movement that is never coming. */
      autoTag: 'Auto',
      /* The subline: who carries it, on what paper, when it left and when it is
         expected. Absent clauses are dropped rather than dashed out. */
      departs: 'departs {{date}}',
      departed: 'departed {{date}}',
      arrives: 'expected {{date}}',
      arrived: 'arrived {{date}}',

      routeTitle: 'Route points',
      /* Pressing a disc. Two labels, because they are two different acts, and
         both name the point: a disc says nothing to a screen reader on its own,
         and "Record arrival" without the name would not say which. The name is
         quoted and left uninflected — the Russian catalogue cannot decline an
         arbitrary place name after a preposition, and both say the same thing in
         the same shape. */
      markThrough: 'Record arrival through “{{point}}”',
      clearFrom: 'Clear the arrival at “{{point}}” and every point after it',
      /* A refusal rather than a permission: on a departed leg the server's own
         `stampDeparture` re-marks the origin, so clearing it would undo itself.
         A control that visibly does nothing reads as broken tracking, so the
         reason is in the label. */
      departureHeld:
        'This leg has departed: the origin arrival is cleared by changing the status, not here.',
      /* The norm under each transition. No plural forms: the Russian
         abbreviation does not inflect, and the delay chip above takes the same
         shape. */
      normDays: '{{count}} d',
      /* Whose word an arrival is on — two different degrees of certainty about
         where a client's cargo is. One word for both would let an assumption be
         read back as a confirmation. */
      pointReachedFact: 'actual',
      pointReachedAuto: 'auto',
      pointExpected: 'exp. {{date}}',
      currentPoint: 'Current point',
      /* An overdue transition. Quiet, because on a corridor with a normative
         time this is the ORDINARY course of events — the position moves by
         itself — and the strip exists for the one correction only a human can
         make. */
      overdueBanner_one:
        '{{from}} → {{to}} is {{count}} day overdue. The position will advance automatically; record the actual arrival if the cargo is already there.',
      overdueBanner_other:
        '{{from}} → {{to}} is {{count}} days overdue. The position will advance automatically; record the actual arrival if the cargo is already there.',
      /* A colon rather than "Cargo at Sarahs": the Russian side cannot inflect
         an arbitrary place name after its preposition, and the two catalogues
         say the same thing in the same shape. */
      cargoAtNext: 'Record arrival: {{point}}',
      notContiguous:
        'A point cannot be marked reached while the one before it is not: record that one first.',

      /* Typing the chain by hand. It sits beside «Apply template» in the card's
         header: both write the same chain, at two different costs of typing. */
      routeEdit: 'Edit route',
      routeEditTitle: 'Edit the route',
      routeEditSubtitle:
        'The points in travel order, with the norm for each transition. The first and the last set the leg’s own “from” and “to”.',
      /* A notice rather than an error: nothing has gone wrong, this is a
         standing property of saving. The server carries arrivals over BY
         POSITION rather than by name, so moving a row leaves the arrival where
         it was in the list. */
      routeEditArrivalsPinned:
        'Arrivals are pinned to a position in the chain rather than to a point’s name: moving a row leaves the arrival where it was in the list.',
      routeEditTooFew:
        'A route of one point is not a route — it holds no transition. Add a second point, or remove the only one.',
      routeEditTooMany: 'A route holds at most {{max}} points.',
      routeEditSave: 'Save route',
      routeEditEndsHint: 'The leg’s ends follow the first and last point',
      arrivalHeldHere: 'An arrival is recorded at this position',
      movePointUp: 'Move point {{n}} up',
      movePointDown: 'Move point {{n}} down',

      applyTemplate: 'Apply template',
      applyTemplateWarning:
        'Applying replaces the current route points, including the arrivals recorded against them. This cannot be undone.',
      directionTemplate: 'Route template',
      directionTemplateHint:
        'The template’s points are copied onto this leg. Changing the template in Settings later does not change it.',
      directionTemplateNone: 'No template',

      /* The trucks moved to `cases.legs.vehiclesTable` in story 6, and to the
         unit cards (`cases.legs.units`) in S-18b. */
      vehiclesTitle: 'Vehicles',

      stallNote: 'Reason for the delay',
      lastLocationHint: 'Where the last report about the cargo came from.',
    },

    /* Adding a leg, on its own page at `/cases/:caseId/legs/new`.
     *
     * The create left the sheet for the reason the record itself did: a
     * direction is a page, and a create that filled a modal and then landed the
     * reader on a page was the one surface still saying otherwise. The wording
     * here does the same work the sheet's did — it says where the rest of the
     * questions are asked, so nobody hunts on this form for the chain, the
     * trucks or the money. */
    legNew: {
      title: 'New leg',
      create: 'Create leg',
      moneyLater: 'Agreed, paid and outstanding, once the leg exists.',
    },

    /* «Плечо целиком» (S-18a): the card above the trucks — the leg's
       carrier, the leg rate in its own currency and the outstanding the server
       computes. The rate is typed once, while the leg has no rate of its own
       (S-10's rule for truck rates); after that the money screen edits it. */
    legWhole: {
      title: 'The whole leg',
      carrier: 'Leg carrier',
      carrierHint: 'Changed in «Leg parameters» → «Transport».',
      rate: 'Rate',
      rateLabel: 'Leg rate',
      currencyLabel: 'Leg rate currency',
      remaining: 'Outstanding',
      /* Several leg-level rates — their total in USD, as the server computes
         it, and no input for another. */
      severalRates: 'several rates, in USD',
      /* Stored as the cost line's `item`, like «Ставка фуры {{plate}}» for a
         truck: renaming it would rename only the lines written from then on. */
      rateItem: 'Leg rate',
      rateInvalid: 'The leg rate is not a number',
    },
    legParams: {
      title: 'Leg parameters',
      open: 'Leg parameters',
      tabsLabel: 'Leg parameter sections',
      tabs: {
        route: 'Route',
        transport: 'Transport',
        documents: 'Documents',
        dates: 'Dates',
        status: 'Status',
      },
      done: 'Done',
      discardConfirm: 'Discard the changes to the parameters?',
      keepEditing: 'Keep editing',
      discard: 'Discard changes',
      newTemplate: 'New',
      addPoint: 'Add point',
      daysUnit: 'd',
      chainNote:
        "The first and last points are the leg's ends. The days between points are the transit norm the cargo's position moves by.",
      origin: 'Origin',
      destination: 'Destination',
      saveAsTemplate: 'Save as template',
      templateName: 'Template name',
      templateNameTaken: 'A template already has this name, perhaps a hidden one. Choose another.',
      templateNameRequired: 'Name the template',
      templateNoDays: 'A template needs days on every transition. Enter them between the points.',
      templateNameTooLong: 'A template point name takes at most 120 characters.',
      endsMissing: 'An end of the leg is missing',
      modeMissing: 'No transport mode chosen',
      editPoints: 'Edit points',
      carrierHint: "New units start with this carrier; a unit with no carrier of its own counts as this carrier's.",
    },

    legSave: {
      unsavedLeg: 'Unsaved leg',
      unsavedChanges: 'Unsaved changes',
      saved: 'All changes saved',
      detailOf: 'Rates detail',
      detailOfHeld: 'Kept with the rates not yet recorded',
      detailOfHeldHint: '«Retry» sends the rates with this answer; «Cancel» discards them so it can be answered again.',
      discardConfirm: 'Discard the unsaved changes?',
      missing: 'Missing: {{fields}}',
      missingFix: 'Fill in: {{fields}}',
      missingOrigin: 'origin',
      missingDestination: 'destination',
      missingMode: 'transport mode',
      overCap: 'A leg takes at most {{max}} units.',
      nothingToSave: 'Nothing to save',
      readOnlyNoRight: 'Your role does not change routes, so the leg is open read-only.',
      readOnlyClosed: 'The case is closed, so the leg is open read-only.',
      legFailed: 'The leg was not saved.',
      chainFailed: 'The leg is saved; its route was not written.',
      templateFailed: 'The leg is saved; the route template was not created.',
      markFailed: 'The arrival was not recorded.',
      retry: 'Retry',
      /* 409 `leg-units-changed` (S-18b), one sentence for every kind. The
         page has already re-read the leg and laid the reader's edits over it,
         so it says "press again", not "reload", which would discard them. */
      unitsChanged:
        "Someone else has already changed this leg's units. The saved ones are shown, and your new rows and edits are kept. Press «Save» again.",
      unitsVanished: 'Removed by someone else: {{units}}.',
      /* A rate the press did not send because its unit is no longer on the
         leg — said by kind and number, never dropped silently. */
      ratesUnsent_one: 'The rate was not sent — this unit is no longer on the leg: {{units}}.',
      ratesUnsent_other: 'The rates were not sent — these units are no longer on the leg: {{units}}.',
      /* Beside a `retired` refusal: the page is older than the server. */
      reload: 'Reload the page',
      unlinkedHint:
        'If one of them is this same carriage, choose it in «Rates detail» below; if not, choose «not detailing».',
    },

    /* One direction's money (issue #35 story 6).
     *
     * The block is served to EVERY caller who reaches the case, with the
     * haulier's identity suppressed per field inside it — so this wording never
     * says "not shown for your role" about a figure. The advance/paid split is
     * the server's answer under the ATA rule, and the copy is careful not to
     * describe it as anything this screen worked out. */
    legFinance: {
      title: 'Payments to the carrier',
      subtitle:
        'The advance and the payments on this leg are counted from the payments recorded against the case.',
      agreed: 'Agreed',
      advance: 'Advance',
      paidTotal: 'Paid in total',
      outstanding: 'Outstanding',
      /* The tile sublines: what each figure is counted over. */
      units_one: '{{count}} unit',
      units_other: '{{count}} units',
      beforeArrival: 'before arrival',
      paymentCount_one: '{{count}} payment',
      paymentCount_other: '{{count}} payments',
      perUnit_one: 'across {{count}} unit',
      perUnit_other: 'across {{count}} units',
      /* The neutral tag on a settlement that moved before the leg arrived. A
         SERVER fact; the table renders it and never re-derives it. */
      advanceTag: 'advance',
      /* The record has not had its second pair of eyes yet (0059, L3). It says
         nothing about the MONEY: a pending settlement is inside every figure on
         this card exactly like a confirmed one (L4), so the wording is about
         the record — "not confirmed", never "not paid". */
      pendingTag: 'not confirmed',
      columns: {
        date: 'Date',
        item: 'For',
        unit: 'Unit',
        method: 'Method',
        /* S-12: the basis the payer typed, and the bank's transfer fee — its
           own column, never inside the amount (AD-11). */
        reference: 'Reference',
        fee: 'Fee',
        amount: 'Amount',
        decisions: 'Actions',
      },
      noSettlements: 'Nothing has been paid on this leg yet.',
      /* The per-truck list, and the act on each row. It replaced «record the
         cost first»: a rate nobody has invoiced is no longer a step to clear
         before paying, because paying recognises the debt in the same request. */

      /* «Оплатить перевозчику» — one sheet for the debt, the payment and what
         it settles. The copy never promises the figures will wait for anybody:
         the payment counts the moment it is recorded, and confirmation is a
         check on the record (L4). */
      pay: {
        /* S-18a: one payment for several trucks of one carrier, or for the leg
           as a whole. The title is a noun, as in the mock: the sheet is a
           payment, and the button that opens it is "Pay the carrier". */
        title: 'Carrier payment',
        submit: 'Record payment',
        atomicNote:
          'Saving records the costs of the ticked lines and one payment to the carrier at once, and links them. Finance checks the payment later, but it counts in the totals straight away.',
        /* The carrier first: it decides which lines can be ticked — one
           payment pays one directory carrier. */
        carrier: 'Carrier',
        /* A carrier with no directory record (a name only, or nobody): the
           server cannot check the lines are one carrier's, so one payment
           carries one line. */
        offDirectory: 'Carrier not in the directory',
        offDirectoryOne: 'Carrier not in the directory — one payment pays one line.',
        /* The currency the money leaves in: the lines' "To pay" and the total
           are in it. */
        currency: 'Payment currency',
        whatHeading: 'What is being paid',
        columns: {
          unit: 'Unit',
          remainder: 'Outstanding',
          amount: 'To pay',
        },
        wholeLeg: 'The whole leg',
        /* A line already paid in full: greyed, cannot be ticked. */
        paidTag: 'paid',
        /* The truck (or the leg) has several invoices or several rates — which
           one this payment settles is decided on the money screen. */
        several: 'Several invoices — pay on the money screen',
        unitCarrierNotInDirectory: 'Pick this unit’s carrier from the directory',
        legCarrierNotInDirectory: 'Pick the leg’s carrier from the directory',
        noRows: 'There is nothing to pay this carrier on this leg.',
        chooseRows: 'Tick what is being paid.',
        rowAmount: 'To pay: {{unit}}',
        /* The one sum the client computes (S-18a): an exact addition of the
           lines. The server still checks it against the lines. */
        total: 'Total to pay',

        /* A line's debt — only where the payment recognises the cost in the
           same request (a rate with no invoice, or no rate at all). Folded:
           the debt is usually the rate. */
        debtHeading: 'What is owed',
        debtSummary: 'Debt: {{amount}}',
        debtAmount: 'Debt',
        debtFromPlan:
          'The agreed carrier rate is {{amount}}. Change it if the carrier invoiced something else.',
        debtUnplanned: 'No rate was agreed — the cost is recorded as unplanned, at the debt.',
        /* The debt's own currency (S-4.1 follow-ups, D1). "Debt currency"
           rather than a bare "Currency": the payment's select is one too. */
        debtCurrency: 'Debt currency',
        /* The plan's figure is not carried into another currency — 3 000 USD
           left standing would become 3 000 CNY — so the box is empty. */
        debtOtherCurrency:
          'The agreed carrier rate is {{amount}}. Type the debt in {{currency}} — the agreed figure is not carried into another currency.',
        /* Two currencies, two days' rates (L3). */
        debtPaymentCurrencies:
          'The debt and the payment are in different currencies: the rate moves between the case date and the payment date, so a remainder may be left, and an allocation above the debt will be refused.',
        debtComment: 'Reason',
        debtCommentHint: 'Optional. It is kept with the cost, not sent to anybody.',
        /* Across currencies the server compares in USD at the case's booked
           rates; this client holds no rates and cannot tell. */
        debtCommentInUsd:
          'In another currency the debt is compared with the agreed {{amount}} in USD, at the rate of the case’s registration date. Above it, a reason is required.',
        debtCommentRequired:
          'This is above the agreed {{amount}} — say why. The reason is kept with the cost.',

        paymentHeading: 'The payment',
        /* The payee on the payment itself. Since S-18a the sheet opens with
           "Carrier", so the payment's field is named by its role — the payee,
           never Finance's accounting "Counterparty" (F1). Prefilled with the
           chosen carrier's name, and editable. */
        payee: 'Payee',
        payeeHint: 'Who the payment goes to — the chosen carrier by default.',
        payeePlaceholder: 'Choose the carrier',
      },
      unavailableTitle: 'The money on this leg could not be read',
      unavailableDescription:
        'Nothing is lost. Try again — if it keeps failing, there may be no exchange rate on the date of one of the amounts.',
    },

    team: {
      title: 'Team',
      /* The section stands on its own now rather than beside Overview, so it
         says what adding somebody DOES: membership IS the access (AD-5, FR-56),
         not paperwork. Same sentence as the quote's. */
      hint: 'Colleagues who work this case. Anybody added sees it from their next request.',
      assign: 'Assign colleague',
      colleague: 'Colleague',
      assignHint:
        'They see this case from their next request. Deactivated accounts are not listed.',
      owner: 'Owner',
      remove: 'Remove {{name}} from the team',
    },
    money: {
      /* `forbiddenTitle` and `forbiddenDescription` are DELETED, not moved. They
         described a state the server cannot produce: AD-5 gives a deal's money to
         the deal's own team, and a caller who cannot reach the case gets a 404 on
         the case itself. What they actually rendered was an unpriced case — the
         state every direct creation and all 241 migrated cases start in. */
      /* No block at all: the read or the FX lookup behind it failed. Names the
         block and the reason together, and never apologises. */
      unavailableTitle: 'The money for this shipment could not be read',
      unavailableDescription:
        'Nothing is lost. Try again — if it keeps failing, the rate for a booked currency may be missing.',
      /* Nothing recorded yet, which is not the same as nothing shown. Says what
         the surface is for and offers the action that fills it (UX-DR-17). */
      untouchedTitle: 'Nothing recorded against this shipment yet',
      untouchedDescription:
        'Costs you record appear here against the plan. The agreed rate is set by Finance on the record.',
      /* The cost lines card on «Costs» (S-11 revision). */
      title: 'Cost lines',
      /* The subtitle states the rule the whole card lives under (FR-36): the
         plan came off the accepted quote, and the rate only moves by approval. */
      subtitleFromQuote:
        'Planned lines carried from quote {{number}} · rate frozen, changes need approval',
      subtitle: 'Rate frozen — changes need approval',
      recordActual: 'Record actual expense',
      /* The two notices at the top of the money card (S-12, direction A).
         Neither moves a figure: the server decides the double count with one
         SQL predicate, and unallocated cash is never applied (AD-11). */
      notices: {
        doubleCountTitle_one: 'The truck rates count separately from the plan line {{lines}}',
        doubleCountTitle_other: 'The truck rates count separately from the plan lines {{lines}}',
        doubleCountTitleUnnamed: 'The truck rates count separately from the plan lines',
        doubleCountBody:
          'The carriage may be counted twice: the cost forecast is overstated and the profit understated.',
        doubleCountLegs: 'Legs affected: {{count}}; the first one opens.',
        openLeg: 'Open the leg',
        unallocatedTitle: 'This case has unallocated payments: {{amounts}}',
        allocate: 'Allocate',
        allocateBlocked: 'Only Finance allocates payments',
      },
      addExpense: 'Add expense',
      /* A new actual entered against a plan still awaiting one (S-4.1
         follow-ups) — otherwise the invoice for a planned cost was recorded
         unplanned beside its plan, and the forecast counted it twice. The
         label avoids "Planned" and "Actual": the amount fields are found by
         those words. */
      planLink: 'Plan line',
      planLinkNone: 'No plan — a new cost',
      planLinkHint: 'If this cost is already on the plan, choose it: the actual is entered against that plan.',
      editExpense: 'Edit expense',
      /* The line this sheet cannot edit. A plan settled by SEVERAL invoices
         (AD-4) has no single actual figure, so the folded form has nothing to
         hold and the server refuses the save with a 409. The copy names the
         line and the count, because "cannot be edited" without either reads as
         a malfunction rather than as a fact about this particular line. */
      severalActualsTitle: 'Settled by several invoices',
      severalActualsDescription:
        '“{{description}}” has {{lines}} recorded costs against one plan, so there is no single actual figure to edit here. Change the invoices where they were recorded.',
      /* Deleting a cost line removes the LINE: the plan and every actual
         against it, in one transaction. It used to remove only the actual and
         leave the plan on screen looking undeleted. */
      deleteExpense: 'Delete this line',
      /* The row's own two icons. Named by the LINE, not by the act alone: a
         table of eight costs carries eight identical pencils, and a screen
         reader hearing "Edit, Edit" cannot tell which one it is opening. */
      editExpenseNamed: 'Edit “{{line}}”',
      deleteExpenseNamed: 'Delete “{{line}}”',
      deleteExpenseConfirm: 'Delete this cost line?',
      deleteExpenseConfirmAction: 'Delete line',
      /* Whether the cost is paid (S-4.2). There is no «Settled» tick any
         more: the server derives the status from the payments allocated to
         the line's actuals (pending ones included), and the table says it in
         a word beside the name. A plan with no actual has none — nothing has
         been invoiced yet. */
      settlement: {
        unpaid: 'Unpaid',
        'partly-paid': 'Partly paid',
        paid: 'Paid',
      },
      /* Variance is derived, never entered — nothing in the product has a
         "recalculate" button. */
      actualHint: 'Leave empty until the cost lands. Variance is calculated.',
      note: 'Note',
      noteHint: 'Saved with the actual.',
      actualNeedsCounterparties:
        'The actual, its counterparty and note are recorded by someone with access to counterparties.',
      /* A cost with no plan (S-4.1). A new line takes a plan, an actual or
         both; neither is refused, and said under the plan. An unplanned line
         stays unplanned: its plan is read-only and its actual is required,
         because without it the line would carry no figure at all. */
      planOrActualRequired: 'Enter a plan or an actual',
      plannedHintNew: 'May be left empty to record the actual alone — the line is then unplanned.',
      actualRequired: 'Enter the actual. To remove an unplanned cost, delete it.',
      unplanned: 'Unplanned',
      unplannedHint: 'Unplanned: the cost was recorded without a plan',
      /* Each side has its own currency: a plan in dollars and an invoice in
         yuan is ordinary. The labels avoid a capitalised "Planned" / "Actual":
         the amount fields are found by those words, and the currency is a
         separate question beside them. */
      plannedCurrency: 'Currency of the plan',
      actualCurrency: 'Currency of the actual',
      totals: 'Totals',
      /* Under the line it explains, not in a banner above the card — the row
         itself carries the attention wash while the Super-admin decides. */
      passThroughPending: 'Pass-through to client pending approval',
      expensesEmptyTitle: 'No expense lines yet',
      expensesEmptyDescription:
        'Planned lines carry over from the quote; actuals are recorded here as invoices land.',
      /* The receivable's provenance (FR-32, FR-33). `tranchePaid` is gone with
         `ReceivableTranche.settled`: per-tranche settlement needs the
         allocations against that tranche, and the block serves only the case's
         receivable total — a paid/unpaid distinction the client cannot compute
         is one it must not render. */
      trancheExpected: '#{{n}} {{amount}} expected {{date}}',
      trancheUndated: '#{{n}} {{amount}} no date yet',
      /* The editable strip. A tranche with no date is not overdue — the second
         half of a 50-50 case starts undated, waiting for delivery. */
      tranchesTitle: 'Expected receipts',
      trancheN: 'Tranche {{n}}',
      trancheDue: 'Expected',
      /* The picker's own name, and it names the TRANCHE: a 50-50 case has
         two of these one under the other, and a series read out as
         "Expected, Expected" is a series nobody can act on. */
      trancheDueNamed: 'Expected date of tranche {{n}}',
      /* The two columns the series aligns under. Said once over the column
         instead of once per row — see TrancheStrip's own note. */
      trancheColumns: { tranche: 'Tranche', share: 'Share', amount: 'Amount' },
      /* The row's own name. The sequence is the series' MEANING — «the
         second tranche» is what Operations chase — and the count beside it
         is what makes «1» mean a half rather than the whole. */
      trancheOfSeries: 'Tranche {{n}} of {{total}}',

      /* One profit on the case (S-6). One word, "Profit", with the three
         FR-30 figures told apart by a qualifier — plan, forecast, final. The
         right of the line is the figure the server chose: the forecast while
         the case runs, the final figure once it is financially closed or
         cancelled. "How it is calculated" prints the server's terms only;
         the client adds nothing up itself. */
      profit: {
        lead: 'Profit:',
        planned: 'plan',
        forecast: 'forecast',
        final: 'final',
        noRate: 'No rate agreed',
        howCalculated: 'How it is calculated',
        rows: {
          planned: 'Plan',
          forecast: 'Forecast',
          final: 'Final',
          cashToDate: 'Cash to date',
        },
        terms: {
          rate: 'Rate',
          plannedCost: 'planned costs',
          billedExtras: 'extra invoices',
          debitNoteRecoveries: 'debit notes',
          cost: 'costs',
          received: 'Received and allocated',
          paid: 'paid',
        },
        cashToDateNote: 'Becomes the final figure after financial closing',
      },

      payments: {
        heading: 'Payments on this case',
        /* «Client billing» lists the inbound ones only (S-11 revision). */
        receivedHeading: 'Payments received',
        receivedEmptyTitle: 'No payments received yet',
        receivedEmptyDescription:
          'Money received is recorded under Finance → Payments and appears here with what it settles.',
        emptyTitle: 'No payments recorded',
        emptyDescription:
          'Money in and out is recorded under Finance → Payments and appears here with what it settles.',
        unallocated: '{{amount}} still unallocated',
        /* The five facts about a payment, one column each. «Fee» is its own
           column and never part of «Amount»: the amount is what SETTLED,
           the fee is what the transfer COST, and no surface may total the
           two (AD-11). */
        columns: {
          payment: 'Payment',
          fee: 'Transfer fee',
          unallocated: 'Unallocated',
          amount: 'Amount',
          /* The table lists incoming and outgoing payments (P-1, QW-2). */
          date: 'Payment date',
        },
      },

      columns: {
        description: 'Description',
        counterparty: 'Counterparty',
        planned: 'Planned',
        actual: 'Actual',
        line: 'Expense line',
        vendor: 'Vendor',
        leg: 'Leg',
        plannedIn: 'Planned {{currency}}',
        actualIn: 'Actual {{currency}}',
        variance: 'Variance',
        actions: 'Actions',
      },
      /* What a cost paid for, linked to that leg's page (FR-67, story 6). The
         plate is carried when the row names a truck — "which leg" and "which
         truck" are one question at the desk. */
      legLink: 'Leg {{n}}',
      legLinkUnit: 'Leg {{n}} · {{unit}}',
      /* A cost that belongs to no single movement — a clearance fee, a bank
         charge. Legitimate and common, and said in words: a blank cell would
         read as a row somebody forgot to file. */
      unattributed: 'unattributed',
      /* A plan detailed per truck (spec 2026-09-01-expense-detail-grouping).
         The parent's own figure has stopped counting in the totals, so it is
         said HERE, as the benchmark it became: what the plan allowed, and how
         far the rates actually agreed have moved from it. Across currencies
         there is no delta to state and the note counts the lines instead.
         "plan", not "quote" (T-4): the figure is the plan line's, and a quote
         is a different record. The `quota` parameter stays — it is the code's
         name. */
      groupNote: 'plan {{quota}} · {{delta}} · {{count}} lines',
      groupNoteMixed: '{{count}} detail lines',
      toggleGroup: 'Toggle details of {{line}}',
    },

    documents: {
      title: 'Invoices and debit notes',
      /* Says what separates this from Files: paper we RAISED, not paper that
         was scanned and attached. */
      subtitle: 'The paper raised against this case, and what is still open on it.',
      raise: 'New document',
      emptyTitle: 'Nothing raised yet',
      emptyDescription:
        'Invoices and debit notes for this case appear here. A new one opens as a draft — issuing it is a separate step.',
      /* A link rather than an Edit button: an invoice is edited on its page. */
      openInvoice: 'Open the invoice',
    },

    /* "Case paper" — the read-only list at the top of the case's Documents
       (S-11; the old `?section=documents` alias lands there for a reader
       without Finance's workspace): what documents exist on the case, with no
       amounts and none of Finance's actions. */
    papers: {
      title: 'Case paper',
      /* Said once above the list, rather than as a refusal on every row. */
      subtitle:
        'Finance issues invoices and debit notes — this shows what was issued and when. Invoice and debit-note PDFs are opened by Finance.',
      document: 'Document',
      /* A document kind this build does not know yet. */
      unknownKind: 'Document',
      openLabel: 'Open {{document}}',
      loading: 'Loading the case paper',
      emptyTitle: 'No paper on this case yet',
      emptyDescription:
        'Issued invoices and debit notes appear here, along with the client status updates.',
    },

    /* Finance's workspace under "Client invoices" in the case's Money (S-11;
     * `?section=documents` is now an alias that lands there): the agreed rate,
     * the payments it is collected in, and the paper that went to the client
     * against them.
     *
     * Its own group beside `documents` rather than inside it, because they are
     * two questions on one screen: "how much is still billable" and "what has
     * actually been sent". */
    invoices: {
      rateTitle: 'Rate from the quote',
      rateSubtitle:
        'What was agreed with the client, what has been billed already, and what closes the rest.',
      fromQuote: 'from quote {{quote}}',
      rate: 'Agreed rate',
      billed: 'Billed',
      paid: 'Paid',
      headroom: 'Still billable',
      /* Colour never carries meaning alone: the bar is decoration over figures
         printed beside it, and the legend names each band in words. */
      legendPaid: 'paid by the client',
      legendAwaiting: 'billed, awaiting payment',
      legendFree: 'not billed yet',
      /* What is inside "billed". The server judges the ceiling against EVERY
         charge on EVERY invoice of the case, drafts included (`BilledOnCase`),
         and refuses the next line against that same total. A reader who assumed
         only issued paper counted would read the headroom as bigger than the
         write will allow. */
      billedCountsDrafts:
        'Drafts count as billed: the rate ceiling is judged against every charge on every invoice of this case, and the next one is refused against the same total.',
      instalment: 'Instalment',
      expected: 'Expected',
      /* An undated tranche is not late: the second half of a 50-50 case is
         created undated on purpose, waiting for a delivery date nobody knows. */
      undated: 'no date agreed',
      notBilled: 'no invoice raised',
      /* An issued invoice removes its tranche from the registry OUTRIGHT,
         whatever it is for: the server asks "does an issued invoice name this
         tranche", not "does one cover it". So a 500.00 invoice against a
         2 000.00 instalment quietly takes 1 500.00 out of what the case
         expects. The row used to print the tranche's own figure beside that
         invoice's number, which is the one reading that is certainly wrong. */
      billedShort: '{{billed}} of {{expected}} {{currency}} billed — the rest is no longer expected',
      raiseInvoice: 'Raise invoice',
      raising: 'Opening…',
      noSchedule:
        'No payment schedule was set up, so the rate is collected in one invoice. Raising one is still fine — it simply closes no instalment.',
      /* A role with no commercial access gets no money block at all (AD-6).
         That is not "no rate agreed" — it is "we may not tell you". */
      rateWithheldTitle: 'The money on this case is not shown',
      /* The sentence this whole section was rebuilt to say: a case's costs are
         its own side of the deal and have no business on the client's invoice. */
      expensesNote:
        'Case costs do not reach this invoice: they add up to what the job cost us and are re-billed to the client by a debit note — a separate document.',
    },

    files: {
      title: 'Files',
      upload: 'Upload',
      takePhoto: 'Photo',
      pending: 'Pending',
      uploading: 'Sending…',
      /* The uploader of a queued file is the person reading the screen: there
         is no server row yet, so no name to resolve. */
      you: 'You',
      /* Story 8.2, UX-DR-19. The count is the point — "files are pending" is
         not something a user can check their work against, and "3 files
         waiting to send" is. */
      queuedTitle_one: '{{count}} file waiting to send',
      queuedTitle_other: '{{count}} files waiting to send',
      /* Says the queue is working, because it is. A user told only that files
         are "pending" starts pressing things. */
      queuedSending: 'They are sending now. You can leave this page — they finish on their own.',
      /* Offline. Names the guarantee explicitly, including surviving a close,
         because that is the promise that stops somebody driving back to
         re-photograph a reload. */
      queuedOffline:
        'No connection. They are saved on this device and send by themselves when the signal returns — you can close the app.',
      sendNow: 'Try now',
      dropHint: 'Drop files here, or use Upload. Photos can be taken directly on a phone.',
      uploadFailedTitle: 'A file did not send',
      /* Names the next action rather than the error. The user cannot act on
         "the PUT returned 403" and can act on "try it again". */
      uploadFailedBody:
        'Nothing was saved for it. Check the connection and add the file again.',
      emptyTitle: 'No files yet',
      emptyDescription:
        'Waybills, packing lists and reloading photos for this case appear here.',

      /* The document kind, asked once for the whole selection (0045). "What
         document is this" is a question about the PAPER, not about the file
         format: forty PDFs grouped by "PDF" are grouped by nothing. */
      kindTitle: 'What document is this',
      kind: 'Document type',
      kindHint:
        'The list is grouped by type, so the right paper is found without opening every file.',
      kindFieldHint: 'Unsure? Leave it as Other — the type is visible and can be corrected.',
      kindSubmit_one: 'Add {{count}} file',
      kindSubmit_other: 'Add {{count}} files',

      /* The type names. These are LABELS for the server's codes — 'cmr' is
         what travels on the wire, not "CMR" — because a translated grouping
         key would group one case differently for two colleagues (AD-27). */
      kinds: {
        cmr: 'CMR',
        awb: 'Air waybill',
        'rail-waybill': 'Rail waybill (SMGS)',
        'bill-of-lading': 'Bill of lading',
        invoice: 'Invoice',
        'packing-list': 'Packing list',
        'customs-declaration': 'Customs declaration',
        certificate: 'Certificate',
        contract: 'Contract',
        photo: 'Photo',
        other: 'Other',
      },

      /* Deleting. The filename goes into the accessible label because the list
         is many rows deep, and a column of buttons all called "Delete" says
         nothing about what is about to go. */
      delete: 'Delete “{{name}}”',
      deleteConfirm: 'Delete “{{name}}”? The file goes from storage too.',
      deleteConfirmAction: 'Delete',
      deleteFailed: 'The file was not deleted',
      deleteFailedHint: 'It is still there. Check the connection and try again.',
    },

    /* The case history: ONE feed over two sources — the action log and the
       financial audit trail. Nothing in the copy separates them, and nothing
       should: the question being asked is "when did this change and who did
       it", not "which of the two tables holds it".

       The `actions.*` keys repeat the machine keys in
       `backend/cmd/asa/actions.go` verbatim, with the `case.` prefix stripped:
       "case.status.change" → `actions.status.change`. The literal match is
       DELIBERATE — the feed translates the key directly and prints the raw key
       when there is no translation, so a route added to actions.go next month
       spoils one line rather than blanking the section.

       Voice follows `quotes.history.actions` below: a short passive sentence
       naming what happened, not what was clicked. "Status changed", never
       "The user changed the status" — the feed prints the actor on its own
       line, and repeating it in every title writes the name twice. */
    history: {
      title: 'History',
      count_one: '{{count}} event',
      count_other: '{{count}} events',
      /* Empty means empty across BOTH sources. The previous text promised
         financial changes only; once the action log joined the feed that
         stopped being true, so the promise widens to everything done to the
         case. */
      emptyTitle: 'Nothing has happened yet',
      emptyDescription:
        'Everything done to this case appears here — the team’s actions and the financial changes, with who and when.',
      /* The time windows. All time is the default: one case's history runs to
         tens of entries, so narrowing it is a deliberate step rather than the
         state the screen opens in. The wording matches the reporting period
         control — one choice said two different ways reads as two choices. */
      filters: {
        all: 'All time',
        today: 'Today',
        yesterday: 'Yesterday',
        week: 'This week',
      },
      searchPlaceholder: 'Search the history…',
      /* Distinct from `emptyTitle` above, and the difference is substantive: a
         case with no history has nothing to clear, this one does — and the
         text names what to clear. */
      noMatchTitle: 'Nothing found',
      noMatchDescription: 'Change the search, or pick All time to drop the time filter.',
      /* Distinct from `emptyTitle` again, and for the same reason the skeleton
         waits for both sources: with one of them refused and the other holding
         nothing, "Nothing has happened yet" is a claim about a history only
         half of which was read. Unknown is not empty. */
      unknownTitle: 'The history could not be read in full',
      unknownDescription:
        'One of the two sources did not answer and the other holds nothing, so whether anything has happened to this case is unknown. Retry above.',
      /* One source failing does not blank the section: the other renders as it
         is, and a notice names what is missing from the feed. Half a history
         that says nothing reads as the whole history — which is how "nobody
         ever changed that" gets said out loud. */
      loadFailed: {
        actions: 'The action log could not be loaded — the feed shows financial changes only.',
        audit: 'The financial changes could not be loaded — the feed shows the team’s actions only.',
        retry: 'Retry',
      },
      /* The feed does not paginate and BOTH its sources are capped: the trail
         at the limit this client asks for, the action log at the one the server
         imposes on a read it takes no limit for. When a cap bites, say so out
         loud — a truncated feed that stays quiet looks like a complete one.
         Each source gets its own sentence, because one cap biting says nothing
         about the other. The numbers are interpolated rather than spelled here,
         so that raising a cap cannot leave a sentence quietly stating the old
         one. */
      truncated: {
        actions: 'Showing the most recent {{limit}} recorded actions.',
        audit: 'Showing the most recent {{limit}} financial changes.',
      },
      audit: {
        reason: 'Reason',
        /* A financial trail row: "<record> · <field>" and its old → new. The
           server stores machine names, codes and a few English phrases, and
           stored rows cannot be rewritten, so the client words them
           (features/cases/lib/auditWording.ts). The server's vocabulary is
           backend/internal/finance/testdata/audit_phrases.golden.json; a test
           requires a label here for every record kind and field in it. */
        records: {
          quote: 'Quote',
          case: 'Case',
          invoice: 'Invoice',
          debit_note: 'Debit note',
          payment: 'Payment',
          expense_line: 'Cost line',
          rate_change_request: 'Rate change',
        },
        fields: {
          case: {
            billed_total: {
              migration_bypass: 'Billed total (data import)',
            },
            rate: 'Rate',
            receivableTranche: 'Tranche',
            status: 'Status',
            trancheExpectedDate: 'Tranche expected date',
          },
          debit_note: {
            amount: 'Amount',
            counterparty: 'Counterparty',
            line_amount: 'Line amount',
            note_date: 'Date',
            number: 'Number',
            status: 'Status',
          },
          expense_line: {
            amount: 'Amount',
            detail_of: 'Details the plan line',
            remarks: 'Note',
            stands_alone: 'Details no plan line',
            vendor_id: 'Directory counterparty',
            vendor_name: 'Vendor',
          },
          invoice: {
            amount: 'Amount',
            invoice_date: 'Invoice date',
            invoice_number: 'Number',
            line_amount: 'Line amount',
            status: 'Status',
            vat: 'VAT',
          },
          payment: {
            allocation: {
              tranche: 'Allocation',
              invoice: 'Allocation',
              debit_note: 'Allocation',
              expense_item: 'Allocation',
              expense: 'Allocation',
            },
            amount: 'Amount',
            /* Links to records (0080): the value is an id, so the history row
               says «Set / Changed / Removed»; the name beside it is
               `counterparty_name`. */
            client_id: 'Directory client',
            counterparty_id: 'Directory counterparty',
            counterparty_name: 'Counterparty',
            direction: 'Direction',
            fee: 'Transfer fee',
            fee_amount: 'Transfer fee',
            fx_settlement_difference: 'FX settlement difference',
            issuing_entity: 'Our entity',
            issuing_entity_id: 'Our entity',
            method: 'Method',
            received_on: 'Payment date',
            reference: 'Reference',
            status: 'Status',
          },
          rate_change_request: {
            requested_rate: 'Requested rate',
            status: 'Status',
          },
        },
        values: {
          /* A field whose value is an id: never printed. */
          reference: {
            set: 'Set',
            changed: 'Changed',
            cleared: 'Cleared',
          },
          vat: {
            none: 'No VAT',
            rate: 'VAT {{rate}}%',
          },
          allocation: {
            invoice: 'Invoice',
            debitNote: 'Debit note',
            vendorUnstated: 'vendor not stated',
            /* S-21: a reversed allocation, and the issue's carry-over from a
               tranche to the invoice (auditWording.ts wordAllocationMove). The
               invoice number is appended, never interpolated. */
            reversed: 'Allocation reversed',
            carried: 'Carried from tranche {{n}} to invoice',
            carriedOf: 'Carried from tranche {{n}} of {{total}} to invoice',
          },
          trancheDate: {
            expected: '#{{n}} expected {{date}}',
            undated: '#{{n}} no date yet',
          },
          debitNoteStatus: {
            draft: 'Draft',
            issued: 'Issued',
          },
          rateChangeStatus: {
            pending: 'Pending',
            approved: 'Approved',
            rejected: 'Rejected',
          },
        },
      },
      actions: {
        create: 'Case created',
        update: 'Case details updated',
        status: {
          change: 'Status changed',
        },
        reopen: 'Case reopened',
        leg: {
          add: 'Leg added',
          update: 'Leg updated',
          remove: 'Leg removed',
          /* A hop-by-hop route edit and a template that OVERWRITES the whole
             route are different acts, and the feed names them apart: otherwise
             the overwrite is buried among the edits. */
          points: {
            replace: 'Leg route edited',
          },
          template: {
            apply: 'Route template applied to the leg',
          },
          /* The platform marks it, not a person, and the feed says so. */
          'auto-advance': 'Route point marked automatically',
        },
        /* Participants are the external sides of the shipment; the team
           below is ASA staff. The pair sits together because these two are
           what get confused, and the words carry the difference rather than
           the order of the keys. */
        participant: {
          add: 'Participant added',
          update: 'Participant updated',
          remove: 'Participant removed from the case',
        },
        team: {
          add: 'Colleague added to the team',
          remove: 'Colleague taken off the team',
        },
        /* `remove` will never appear in a case's feed: deleting a file is
           logged under the "file" entity, not "case". The translation is here
           anyway — it costs one line and spares the next reader the conclusion
           that the dictionary is incomplete. Same for `status-update.send-bulk`
           below. */
        file: {
          attach: 'File attached',
          remove: 'File deleted',
        },
        /* Two keys, one string, and not by accident. Both entries mean the same
           thing: somebody wrote in the case conversation. Inventing separate
           titles would show the reader a distinction they cannot act on. */
        message: {
          post: 'Message posted to the chat',
        },
        chat: {
          post: 'Message posted to the chat',
        },
        /* The bulk send names its cases in the body rather than the path, so
           the entry carries no case id and lands in no feed. Translated for
           the same reason as `file.remove`. */
        'status-update': {
          send: 'Status update sent to the client',
          'send-bulk': 'Status update sent across several cases',
        },
        rate: {
          set: 'Rate set',
        },
        /* The per-row writes (`record`, `update`, `delete`) and the writes of
           a whole expense — its plan with its actuals (`save`, `revise`,
           `remove`) — are different acts: one entry of the second kind can
           mean two rows at once, and "Cost line removed" would picture one. */
        expense: {
          record: 'Cost line added',
          update: 'Cost line updated',
          delete: 'Cost line removed',
          detail: {
            set: 'Expense re-linked to a plan line',
          },
          batch: 'Carrier rates entered for the trucks',
          save: 'Expense entered',
          revise: 'Expense revised',
          remove: 'Expense removed in full',
        },
        payment: {
          record: 'Payment recorded',
        },
        carrier: {
          pay: 'Carrier payment recorded',
        },
        invoice: {
          raise: 'Invoice draft created',
          update: 'Invoice updated',
          line: {
            add: 'Invoice line added',
            update: 'Invoice line updated',
            delete: 'Invoice line removed',
          },
          render: 'Invoice document generated',
          discard: 'Invoice draft deleted',
          issue: 'Invoice issued',
        },
        'debit-note': {
          raise: 'Debit note draft created',
          update: 'Debit note updated',
          line: {
            add: 'Debit note line added',
            update: 'Debit note line updated',
            delete: 'Debit note line removed',
          },
          render: 'Debit note document generated',
          issue: 'Debit note issued',
        },
        tranche: {
          date: {
            set: 'Tranche date set',
          },
        },
      },
    },

    /* One chat panel serves a case and a quote (ChatPanel is not told whose
       it is), so its own words name the team, not the record (QW-2). The
       dock's header (`dock.case` / `dock.quote`) names the record. */
    chat: {
      label: 'Team chat',
      placeholder: 'Message the team',
      send: 'Send message',
      heading: 'Chat',
      internalOnly: 'Internal only',
      you: 'You',
      sending: 'Sending…',
      latest: 'Latest',
      /* Story 8.3. The conversation is read a page at a time and polled while
         visible, so these describe states an always-loaded fixture never had:
         a first page in flight, a page-back, and messages that landed while
         somebody was reading further up. */
      loading: 'Loading conversation',
      failed: 'The conversation could not be loaded.',
      empty: 'Nothing said yet. Start the thread.',
      earlier: 'Earlier messages',
      loadingEarlier: 'Loading…',
      /* The jump-to-latest pill once something has arrived below the reader.
         It states the count because "3 new" and "1 new" are different things
         to walk back into. */
      unread_one: '{{count}} new message',
      unread_other: '{{count}} new messages',
      /* The phone badge's accessible name. It replaces the plain label rather
         than being appended to it, so a screen reader hears the count as part
         of what the control is for rather than as trailing decoration. */
      openWithUnread_one: 'Team chat, {{count}} new message',
      openWithUnread_other: 'Team chat, {{count}} new messages',
      messageLabel: 'Message',
      sendHint: 'Enter to send · Shift + Enter for a new line',
      attach: 'Attach files',
      removeAttachment: 'Remove {{name}}',
      dropHere: 'Drop files to attach',
      tooLarge: '{{name}} is larger than 50 MB and was not attached.',
      tooMany: 'A message can carry {{count}} files.',
      /* The day separators between runs of messages. Two named days and then
         a formatted date, because "Today" and "Yesterday" are what a reader
         checks a coordination thread against — an absolute date for something
         said four hours ago makes them do arithmetic to learn it is current. */
      today: 'Today',
      yesterday: 'Yesterday',
      /* The conversation of the quote the case came from (S-1, owner decision
         O3): a shut block above the case chat, read-only. The quote is linked
         only for a reader who can open it. */
      archive: {
        title: 'Quote conversation (archive)',
        readOnly: 'Read-only: the conversation before the case was created.',
        openQuote: 'Open quote {{number}}',
        quote: 'Quote {{number}}',
        truncated_one: 'Showing the latest {{count}} message.',
        truncated_other: 'Showing the latest {{count}} messages.',
      },
      /* The record dock (FR-46, UX-DR-13, 0052). Its own group because these
         describe the CONTAINER — its title bar and its three controls — while
         everything above describes the conversation inside it, which is the
         same conversation the chat page renders. */
      dock: {
        case: 'Case chat',
        quote: 'Quote chat',
        /* The record the panel is about, under its title. The separator is a
           middle dot with hair spaces around it, as everywhere else. */
        subject: '{{number}} · {{client}}',
        internal: 'Internal',
        teamOnly: 'Visible only to your team',
        minimise: 'Minimise chat',
        expand: 'Open the full chat page',
        /* The collapsed control: a floating button with a label on hover. It
           says INTERNAL chat rather than naming the record, because the record
           is the page behind it and repeating it would be noise. */
        open: 'Internal chat',
        /* Replaces the plain label rather than trailing it, so a screen reader
           hears the count as part of what the control is for. */
        openWithUnread_one: 'Internal chat, {{count}} new message',
        openWithUnread_other: 'Internal chat, {{count}} new messages',
      },
    },
  },

  /* The chat page (0029): conversations across records, in two tabs.
     
     A top-level namespace rather than more keys under `cases`, because the
     page spans quotes AND cases and nesting it under either would misname
     half of what it shows. The panel's own strings stay at `cases.chat.*`:
     they are the same words in both places, and moving them would touch every
     call site in ChatPanel for no gain the reader can see. */
  chat: {
    title: 'Chat',
    subtitle: 'Conversations on the work you are on.',
    tabs: {
      cases: 'Cases',
      quotes: 'Quotes',
    },
    /* The list beside the panel. `empty` is per tab: a reader with no quote
       conversations has not lost anything, they simply have no quotes. */
    emptyCases: 'No case conversations yet.',
    emptyQuotes: 'No quote conversations yet.',
    /* Nothing is selected, which is the first thing the page shows on a wide
       screen. Not an error, so it reads as an invitation. */
    pickOne: 'Choose a conversation to read it.',
    loading: 'Loading conversations',
    failed: 'The conversations could not be loaded.',
    /* The unread pill on a list row. Capped at 99 by the server, so the
       hundredth is "99+" rather than a number nobody reads to the end of. */
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    unreadCapped: '99+',
    /* A converted quote's thread. Read-only, and the banner replaces the
       composer rather than disabling it — a disabled box cannot explain
       itself, and this refusal has a reason worth reading. */
    archived: 'Archive — read only',
    archivedWhy: 'This quote became a case. Its conversation continues there.',
    /* Editing and deleting (0029). The marker is permanent: 0023 refused
       editing outright because a silent rewrite is worse than no message, and
       what makes it acceptable is that it is never silent. */
    edited: 'edited',
    deletedBody: 'Message deleted',
    edit: 'Edit',
    editing: 'Editing',
    saveEdit: 'Save',
    cancelEdit: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Delete this message? It stays in the thread as a note that something was removed.',
    editFailed: 'The message could not be edited.',
    deleteFailed: 'The message could not be deleted.',
    /* The quote side has no paperclip: there is no quote file store, and a
       message must never carry its own bytes (0023). */
    noAttachmentsOnQuotes: 'Files attach to cases, not to quotes.',

    /* The list's own filter (0052). Client-side over the rows already held —
       the server sends every conversation this reader may open in ONE
       response, so narrowing it is a substring test rather than a request.
       The placeholder is per tab because the list is per tab: offering to
       search cases on the quote tab would describe rows that are not there. */
    search: {
      cases: 'Search cases…',
      quotes: 'Search quotes…',
      /* The accessible name. The placeholder is not one — it disappears the
         moment somebody types, which is exactly when a screen reader user
         would want to be told what the box is. */
      label: 'Search conversations',
      /* A filter that matched nothing. Distinct from `emptyCases` above: the
         reader HAS conversations, this text does not name any of them. */
      none: 'No conversation matches that.',
    },
    /* The last line of a conversation, on its row. Author and body in one
       string rather than two spans, so a translator can move the colon — and
       so a right-to-left language would not need a second layout. */
    preview: '{{author}}: {{body}}',

    /* The thread's header. It names the record, because the list beside it can
       be scrolled away from the selected row and a thread with no title is an
       unexplained column of bubbles. */
    threadSubtitle: {
      case: '{{client}} · Case conversation',
      quote: '{{client}} · Quote conversation',
    },
    /* The same subtitle for a record whose client the two lists have not
       resolved yet — better a shorter true line than "undefined · …". */
    conversationKind: {
      case: 'Case conversation',
      quote: 'Quote conversation',
    },
    /* Internal, always, and said twice on purpose: a chip beside the title for
       the reader who already knows, and a banner for the one who does not.
       External participants are structurally absent from this surface
       (FR-46) — nothing typed here can reach a client. */
    internal: 'Internal',
    internalBanner: 'Internal conversation — visible only to your team',
    /* Out of the conversation and onto the thing it is about. A link rather
       than an action, so it opens in a new tab the ordinary way. */
    viewCase: 'View case',
    viewQuote: 'View quote',
  },

  quotes: {
    title: 'Quotes',
    subtitle: 'What we have offered, and what it earns if it lands.',
    // A deep link from a role with no access to quotes (#129).
    noAccess: {
      title: 'This section is not available',
      description:
        'Quotes open with your role’s access to the Quotes area. Ask an administrator if you need it.',
    },
    loading: 'Loading quote',
    backToList: 'Back to quotes',
    /* The count in the table's footer. It counts what is visible AFTER the
       filter: the footer sits under that list and has to answer for it, not
       for a set the screen decided not to show. */
    count_one: '{{count}} quote',
    count_other: '{{count}} quotes',

    /* The five payment options from the source workbook's Reference list. The
       VALUE stored and printed is the English string; these are what our own
       colleague reads in the picker. */
    paymentOptions: {
      'in-advance-payment': 'Paid in advance',
      '50-50-payment-upon-pickup-delivery': '50/50 — on pickup and on delivery',
      'full-payment-upon-delivery-before-unloading':
        'Paid in full on delivery, before unloading',
      credit: 'On credit terms',
      other: 'Other — stated in the terms below',
    },

    /* The cargo package type (F3): the picker stores a CODE (`pallet`), not a
       word, and each reader — the screen and the offer — words the code in its
       own language. What a person types under «Other…» is never translated
       and prints as stored. The words match `offerPackages` in
       backend/internal/quote/offer.go; packageTypes.test.ts compares them. */
    packageTypes: {
      none: 'Not set',
      other: 'Other…',
      otherText: 'Other package type',
      /* In the picker: the singular noun. */
      options: {
        pallet: 'Pallet',
        crate: 'Crate',
        box: 'Box',
        drum: 'Drum',
        bag: 'Bag',
        'big-bag': 'Big bag',
        roll: 'Roll',
        bale: 'Bale',
      },
      /* Beside the package count: "1 pallet", "12 pallets". */
      counted: {
        pallet_one: 'pallet',
        pallet_other: 'pallets',
        crate_one: 'crate',
        crate_other: 'crates',
        box_one: 'box',
        box_other: 'boxes',
        drum_one: 'drum',
        drum_other: 'drums',
        bag_one: 'bag',
        bag_other: 'bags',
        'big-bag_one': 'big bag',
        'big-bag_other': 'big bags',
        roll_one: 'roll',
        roll_other: 'rolls',
        bale_one: 'bale',
        bale_other: 'bales',
      },
    },

    /* Win/loss over a date range (FR-15). WHOSE numbers these are is part of
       reading them: a Sales user's figures cover their own quotes, and a
       personal number read as a company number is the failure AC 6 guards
       against — so the caveat is a permanent part of the widget, never a
       tooltip. Expired keeps its own count and is never folded into Lost: a
       quote nobody answered is a different failure from one the client
       refused. */
    statistics: {
      title: 'Win / loss',
      won: 'Won',
      wonHint: 'Converted to a case',
      lost: 'Lost',
      lostHint: 'Rejected',
      expired: 'Expired',
      expiredHint: 'Counted on its own, never as lost',
      conversion: 'Conversion',
      /* Names the denominator rather than describing it. "Won of decided"
         leaves the reader to work out whether expired quotes are in it; the
         counts say so outright, and they are the same three figures standing
         beside this one. */
      conversionHint: '{{won}} of {{decided}} decided',
      wonValue: 'Won value',
      scope: {
        own: 'Your quotes only',
        assigned: 'Quotes you own or are on',
        all: 'Every quote, all owners',
      },
      range: {
        label: 'Period',
        d30: 'Last 30 days',
        d90: 'Last 90 days',
        year: 'This year',
        all: 'All time',
      },
      /* The days the server counted, printed on the panel's header line after
         the scope caveat: the control names the period that was asked for, this
         names the one that came back. Bare dates, because the line already
         reads as a caption — a "counted over" prefix repeated the obvious in
         the one place on the panel where every character is beside a heading. */
      period: '{{from}} – {{to}}',
      error: 'The win/loss figures could not be read.',
    },

    /* The quote page's sticky section bar (S-3): one scrolling page of five
       sections, and links to them. Revisions, history and the team are in the
       header's «⋯» menu rather than sections. */
    sectionsLabel: 'Quote sections',
    sections: {
      client: 'Client and cargo',
      route: 'Delivery options',
      pricing: 'Pricing',
      terms: 'Terms',
      preview: 'Preview',
    },
    /* The header's «⋯» menu. The number is how many entries open. */
    more: {
      label: 'More',
      revisions: 'Revisions ({{n}})',
      history: 'History',
      team: 'Team ({{n}})',
    },
    /* The client block at the top of «Client and cargo»: what the header asks,
       in one place. Edit opens the same sheet as the header's Edit. */
    clientBlock: {
      title: 'Client and contact',
      /* The button's accessible name: the visible "Edit" plus what it edits,
         because the header has an Edit of its own. */
      editLabel: 'Edit client and contact',
    },
    /* The «Preview» section on a draft: a summary of what will go out, and two
       buttons — the document itself still opens in its sheet. */
    previewSection: {
      hint: 'The offer as the client will read it. Check the document before you send it.',
      open: 'Open preview',
    },

    actions: {
      newQuote: 'New quote',
      editQuote: 'Edit quote',
      /* The way back into the three-step builder from a saved quote. Named for
         what it RESUMES rather than for the screen it opens ("Open the
         builder"), because the reader who needs it has already been there once:
         they created the quote in it, walked away, and came back for the markup
         it is the only home of. "Edit quote" beside it is the header sheet and
         a different, smaller act — the two labels have to stay tellable apart. */
      create: 'Create quote',
      /* The visible label says what the press DOES (QW-11): the client's yes
         creates the case. `approveLabel` is its accessible name, the same
         words without the arrow a screen reader would read out. */
      approve: 'Client approved → create case',
      approveLabel: 'Client approved, create case',
      converting: 'Creating case…',
      openCase: 'Open case {{number}}',
      newRevision: 'New revision',
      reopening: 'Opening…',
      sendOffer: 'Send offer',
      preview: 'Preview',
      fixCargo: 'Fix cargo',
    },

    /* The confirmation in front of the conversion (QW-11, walkthrough C-1).
       The case does not exist yet, so every line here is what it WILL carry —
       read off the quote the way the server reads it — and the lines that say
       what it will NOT get are the point of the sheet: a leg skipped, a rate
       unset, terms that land as «other», an Operations owner nobody assigned. */
    convert: {
      title: 'Create a case from {{number}}',
      intro: 'The client has agreed. The case is created from this quote as it stands:',
      confirm: 'Create case',
      /* Shown when the quote under the open sheet is no longer Sent and no
         refusal is there to say why. */
      notSent: 'This quote is no longer waiting on the client, so no case can be created from it.',
      client: 'Client',
      entity: 'Issuing entity',
      paymentTerms: 'Payment terms',
      /* Only for OTHER and wording the picker did not write: the case holds
         them as «other», and no screen changes a case's terms later (CREDIT
         is carried as credit with its days since S-12).
         The instalment exists only when a rate is set — CreateInTx registers
         the rate and its instalments together — so the two `NoRate` variants
         say there are none. */
      termsCaveat:
        'The case holds these terms as «other»: one instalment dated at registration. They cannot be changed on the case afterwards.',
      termsCaveatNoRate:
        'The case holds these terms as «other» and creates no instalments, because no agreed rate will be set. They cannot be changed on the case afterwards.',
      termsNoRate: 'No instalments are created, because no agreed rate will be set.',
      /* S-12: a credit quote stating no days. The case gets credit for the
         days entered, and the server creates no case without them. */
      creditDaysAsk: 'The quote states no credit days — enter them, and the case gets credit for that many days.',
      creditDaysBlocked: 'Enter the credit days — 1 to 365',
      incoterm: 'Incoterm',
      mode: 'Mode',
      transit: 'Transit time',
      leg: 'Leg',
      legPlanned: 'One planned leg, {{origin}} → {{destination}}',
      legNoOption:
        'Will not be created: no delivery option is selected. Operations add it on the case.',
      legNoMode:
        'Will not be created: the selected delivery option has no mode. Operations add it on the case.',
      rate: 'Agreed rate',
      /* A statement, not an instruction: no screen sets a case's rate. */
      noRate: "No agreed rate will be set: the quote's customer total is zero.",
      plannedCosts: 'Planned costs',
      plannedLines_one: '{{count}} cost line',
      plannedLines_other: '{{count}} cost lines',
      noPlannedLines: 'No planned costs: no line carries a cost.',
      team: 'Case team',
      /* S-1: the Operations owner is chosen on this sheet and joins the team;
         Pricing and Finance still do not (owner decision O1). */
      notJoining:
        'Pricing and Finance colleagues do not join the case; from Operations, only the owner you choose joins. They are still told the case was created.',
      /* The required Operations owner (S-1). The server serves the list: only
         the people who may run a case. */
      owner: {
        label: 'Operations owner',
        hint: 'Joins the case team and is notified.',
        /* The list was read and is empty — not loading, not failed. */
        empty: 'Nobody can be named: no colleague may run a case. Ask an administrator.',
        /* The reason on «Create case» while nobody is chosen. */
        blocked: 'Choose the Operations owner first.',
        errors: {
          required: 'Choose an owner.',
          notAssignable: 'This colleague cannot be named: the account is deactivated or no longer exists. Choose someone else.',
          notOperations: 'This colleague cannot run a case. Choose someone from Operations.',
        },
      },
      irreversibleTitle: 'This cannot be undone.',
      irreversible:
        'The quote closes and its chat is archived; the conversation continues on the case.',
      /* The three refusals the server sends as a `conflict` DETAIL token. */
      errors: {
        /* {{section}} is the sidebar's own label (`nav.cases`). No link is
           promised: the quote read serves the case's link only to a caller
           whose case scope reaches it, and this reader may not be one. */
        alreadyConverted:
          'This quote has already been converted — its case exists. Find it under «{{section}}».',
        notConvertible:
          'This quote is no longer waiting on the client, so it cannot become a case. The page has been refreshed.',
        moneyNotBookable:
          "The quote's figures cannot be booked on a case. Check the costing's currency and amounts, then try again.",
      },
    },

    /* The INTERNAL team on a quote (migration 0028) — colleagues, ASA staff
       with logins. A quote has had no external parties since S-22a (migration
       0075 dropped its participants).

       Kept separate from `cases.team`: a case is running and a quote is not
       yet won, and "they can see it" means different things on the two. */
    team: {
      title: 'Team',
      /* Says what adding somebody DOES, because on a quote it is not cosmetic:
         membership is what grants visibility. Optional is stated for the same
         reason it is on participants — most quotes are worked by one person. */
      hint: 'Colleagues who work this quote with you. Adding somebody lets them see it. Optional.',
      empty: 'Only you for now. Add a colleague if somebody else is working this quote.',
      add: 'Add colleague',
      colleague: 'Colleague',
      /* Names the person, not the position: unlike a participant row, a
         colleague is picked rather than typed, so their name always exists by
         the time this label is read. */
      remove: 'Remove {{name}} from the team',
      /* The owner is on the team from the moment the quote is created and is
         the one member who cannot be taken off. */
      owner: 'Owner',
      you: 'You',
      /* Shown where the picker would otherwise be empty. Distinguishes "nobody
         else works here" from "the list failed to load", which is the reading a
         bare empty dropdown invites. */
      noneAvailable: 'No other colleagues to add.',
      /* The two refusals the server can answer with, in the words the user can
         act on. */
      lastMember: 'Assign another colleague before removing the last one.',
      notAssignable: 'That account cannot be assigned.',
    },

    /* The stepped builder (UJ-1), which replaced the new-quote modal. The step
       labels name the WORK, and the hint under each says what belongs there —
       "Price and send" alone does not settle whether the agent's price is
       entered on that step or on the routing one. */
    /* Opening a quote (UJ-1, FR-4, FR-5).

       What is left of a three-step builder. The steps are gone because each one
       had a twin on the detail page; this asks only what the create payload
       carries, and everything else is answered on the record itself. */
    create: {
      title: 'New quote',
      subtitle: 'Who the offer is for. Everything else is filled in on the quote itself.',
      identity: 'Client and cargo',
      identityHint: 'Who the offer is for, and what is moving.',
      // A deep link to /quotes/new from a role that may not create quotes (#128).
      noAccess: {
        title: 'Creating quotes is not available',
        description:
          'A new quote opens with the permission “Open a draft quote”. Ask an administrator if you need it.',
      },
    },
    /* «Offer readiness» (S-3): what the server needs to send, and what is only
       advisable. Each row links to its section; the same rows give the section
       bar its markers. The state is in words — colour alone says nothing. */
    readiness: {
      required: 'Needed to send',
      advised: 'Advisable',
      row: {
        cargo: 'Cargo',
        recipient: 'Recipient',
        route: 'Delivery option',
        pricing: 'Pricing',
        validity: 'Validity',
      },
      done: 'done',
      gap: {
        issues_one: '{{count}} issue',
        issues_other: '{{count}} issues',
        noPositions: 'no positions',
        noContactAccess: "no access to the client's contacts",
        noRecipient: "no email on the client's contacts",
        noRoute: 'none yet',
        noCostLines: 'no cost lines',
        noValidity: 'not set',
      },
      /* The section-bar marker's label, for a screen reader and the tooltip. */
      marker: '{{row}}: {{gap}}',
    },

    /* The read-only cards beside both quote screens. Nothing here is the only
       home for a fact — every line of it is also on a card in the main column,
       which is what makes the rail droppable on a narrow screen. */
    aside: {
      quote: 'Quote',
      number: 'Number',
      /* The number is minted server-side (FR-5). Said in words, so nobody hunts
         for a field that does not exist. */
      numberPending: 'assigned on create',
      status: 'Status',
      owner: 'Owner',
      readiness: 'Offer readiness',
      offerCarries: 'The offer will carry',
      /* The dark money card. Its three figures are labelled from the costing
         and markup dictionaries — `quotes.costLines.customerTotal`,
         `quotes.pricing.cost`, `quotes.pricing.marginOverRevenue` — because the
         same values stand under the same words on the costing tab. A second
         dictionary for one value is two values to anybody reading quickly. */
      money: 'The money on this quote',
      /* Shown only while the costing is empty: three zeroes with nothing said
         about them look like a card that failed to load. */
      moneyNote:
        'No cost lines yet — the figures appear here as soon as the costing is filled in.',
    },

    /* Sending the offer (FR-71). The sheet exists to show the address before an
       email leaves permanently — not to ask for anything unnecessary. */
      /* The offer preview (UX-DR-15), shown before the irreversible send.

         The document's OWN labels are not here: they come from the server in
         the client's language, because that is the language the PDF prints in.
         These strings are the sheet's chrome — ours, in the user's language. */
      preview: {
        title: 'Offer preview',
        loading: 'Composing the offer',
        close: 'Close',
        /* Printed where the issued document prints its number. A preview has no
           number because minting belongs to the send (AD-12), and DRAFT is the
           same word the PDF renderer uses for an empty one. */
        draft: 'DRAFT',
        revision: 'revision {{label}}',
        signature: 'Signature and stamp',
        /* The DOCUMENT's language, not the interface's — which is why the
           control is labelled this way rather than "Language". The offer is
           written for the client, and a salesperson working in Russian still
           has to be able to read the English page before it goes out. */
        language: 'Document language',
        /* Says which way round the switch works, because the sheet's chrome
           does not move with it: the tables and field labels below are the
           client's, the buttons around them stay the reader's. */
        languageHint: 'The language the client will read this in. The send uses the same one.',
        /* The offer cannot go yet, and the document below is still accurate —
           the banner says the first without contradicting the second. */
        blockedTitle: 'This cannot be sent yet',
        blockedNoCargo: 'The offer has no cargo. Add the positions under "Client and cargo" — an offer with nothing on it prices nothing.',
        blockedChecklist: 'The cargo checklist is still open. The note above the sections names what is missing.',
        blockedNotDraft: 'This quote has already been issued. Open a new revision to send it again.',
        blockedNoRecipient: 'This client has no contact with an email address. Add one in the directory.',
        blockedGeneric: 'The server will not issue this offer yet.',
      },
    offerSend: {
      title: 'Send offer',
      document: 'Document',
      nextRevision: 'revision {{label}}',
      validUntil: 'Valid until {{date}}',
      recipient: 'Recipient',
      noRecipient: 'This client has no contact with an email address. Add one in the directory — there is nowhere to send the offer without it.',
      /* A role without contact access is served no address and the server
         refuses its send. Not "the client has no email": the fix is not in the
         directory. */
      noContactAccess: "You have no access to the client's contacts — a colleague who has it can send the offer",
      /* The FR-64 refusal is NOT here any more: it is `errors.branding`, because
         the offer is no longer the only document it can refuse. */
      explainer: 'The offer is saved as an immutable revision, the PDF is filed against the quote, and the client receives an email linking to the document.',
    },

    /* The new-quote sheet takes the header only. Cargo and cost lines are
       entered on the quote itself, where the checklist watches them. */
    form: {
      origin: 'Origin',
      destination: 'Destination',
      mode: 'Mode',
      incoterm: 'Incoterm',
      /* Our sentence about the rule, not the rule's own name — the ICC terms
         themselves stay English in both catalogues, because "Free On Board"
         translated appears on no bill of lading. */
      incotermSeaOnly: 'sea and inland waterway only',
      quoteDate: 'Quote date',
      paymentTerms: 'Payment terms',
      /* S-12: the credit term's length. Asked only while «Credit» is picked,
         and carried onto the case as days from registration. */
      creditDays: 'Credit days',
      creditDaysHint: '1 to 365',
      creditDaysValue_one: '{{terms}}, {{count}} day',
      creditDaysValue_other: '{{terms}}, {{count}} days',
      transitDays: 'Transit time the client asked for, in days',
      transitDaysMin: 'Fewest days',
      transitDaysMax: 'Most days',
      transitInverted: 'The shortest transit cannot be longer than the longest.',
      /* The nine FR-4 / FR-11 / FR-59 header fields that had no entry path in
         any quote surface until 2026-08-17. Every one is a column on `quotes`
         that the DTO already served and no form could fill. */
      clientContact: 'Contact at the client',
      /* Names the person the offer will actually go to when none is picked,
         because "optional" alone leaves the reader wondering whether an
         unaddressed offer sends at all. */
      clientContactDefault: 'Defaults to {{name}}',
      /* The server refused the contact: it is not one of this client's (the
         client was changed, or the contact was removed from the directory). */
      clientContactNotOfClient: 'This contact does not belong to this client — choose again',
      cargoOwner: 'Cargo owner',
      /* A placeholder, where the label used to be followed by "Optional". It
         answers the question that word never did — what a good answer looks
         like — and it costs the field no height. */
      cargoOwnerPlaceholder: 'If it is not the client',
      cargoDescription: 'Cargo description',
      cargoDescriptionPlaceholder: 'Marble slabs, 15 pallets',
      pickupLocation: 'Pickup location',
      dealTermsLegend: 'Commercial terms',
      demurrageTerms: 'Demurrage after free time',
      demurrageTermsHint: 'As agreed, in words — e.g. USD 80/day per container.',
      deliveryTerms: 'Delivery terms',
      /* Says what it is NOT, because the field above it in the same sheet is the
         Incoterm and the two are easy to conflate. */
      deliveryTermsHint: 'How delivery happens in practice — not the Incoterm.',
      carrierDisplayLegend: 'Carrier line on the offer',
      carrierDisplay: 'Carrier or supplier, as printed',
      /* The second clause of Story 2.1 AC 2, said where somebody might otherwise
         use this as a vendor field. */
      carrierDisplayHint:
        'Printed on the offer for information only. Assigns no vendor to any cost line.',
      validUntilHint: 'After this date the quote shows as expired.',
      measures: 'Weight and volume',
      grossWeightOverride: 'Gross weight kg',
      volumeOverride: 'Volume CBM',
      overrideHint:
        'Leave blank to use the total of the cargo positions. A figure here replaces it on the offer.',
      chargeableWeightHint: 'For the whole consignment, when the carrier gives one figure.',
      finalPackingList: 'Final packing list received',
      finalPackingListHint:
        'The volumes above are the client’s rather than our estimate. Stays visible on the case.',
      nextStep: 'Cargo and cost lines are added on the quote itself.',
    },
    columns: {
      number: 'Quote',
      client: 'Client',
      route: 'Route',
      /* The header's one-line cargo description, headed by the short word: the
         column is narrow and "Cargo description" would wrap the heading over a
         cell that is itself one truncated line. */
      cargo: 'Cargo',
      status: 'Status',
      validUntil: 'Valid to',
      sell: 'Sell USD',
      margin: 'Margin USD',
    },
    /* The board's drag receipt: what moved and where, in one clause, with the
       undo beside it. */
    board: {
      moved: '{{number}} moved to {{status}}',
      /* An empty lane, in words. The invitation is a SECOND key because it is
         only true of a lane something may actually be dragged into — printing
         it over Converted would teach a gesture that lane refuses. */
      laneEmpty: 'No quotes',
      laneDrop: 'Drag one here to change its status.',
    },

    card: {
      /** The issued-version marker beside the date: "Rev A · 26 Jul". */
      rev: 'Rev {{label}}',
      total: 'Total',
      margin: 'Margin',
      validTo: 'Valid to',
      /** Client silence on an issued quote, in the words used at standup. */
      noAnswer: 'no answer {{count}} d',
      /** A mixed quote counts positions rather than naming one package type. */
      positions_one: '{{count}} position',
      positions_other: '{{count}} positions',
      /** What blocks the send. The card counts; the quote lists (FR-3). */
      blocked_one: '{{count}} position incomplete',
      blocked_other: '{{count}} positions incomplete',
    },
    /* As for cases (QW-9, C-7): the server picks the rows; no rule is named. */
    emptyTitle: 'No quotes to show',
    emptyDescription:
      'This list shows the quotes open to you. Waiting for a colleague’s quote? Ask to be added to its team.',

    cargo: {
      title: 'Cargo',
      add: 'Add position',
      edit: 'Edit position',
      position: 'Position',
      hsCode: 'HS code',
      packages: 'Packages',
      /* The count travels as an integer: «2.5» is not a formatting slip but a
         different number. */
      packagesInvalid: 'Enter a whole number of packages',
      packageType: 'Package type',
      weight: 'Weight kg',
      volume: 'Volume CBM',
      /* Stands in for the number in the cargo table: not measured yet. */
      missing: 'Missing',
      /* The counter-signal: a 50 CBM declaration that arrives as a 90 CBM
         truck. Volume is needed to SEND, not to save (FR-12), and the hint says
         exactly that (QW-9, Q-6): "Required" made people invent a number. */
      volumeRequired: 'Needed to send the offer. The position can be saved without it.',
      volumeHint: 'Checked against the booking before the offer goes out.',
      /* Multi-shipper loadings are routine (FR-12): three shippers' boxes on
         one truck, and the offer has to say which are whose. */
      shipper: 'Shipper',
      shipperHint: 'Only when this position ships from someone else.',
      /* Entered, never computed — the volumetric divisor is the carrier's. */
      chargeableWeight: 'Chargeable weight kg',
      chargeableWeightHint: 'Only when the carrier bills something other than the gross weight.',
      totals: 'Total',
      overriddenMark: 'edited',
      overriddenFrom: 'Manually set. The positions add up to {{value}}.',
      partialTotal: 'Some positions have no figure entered, so this total is incomplete.',
      packingListReceived: 'Final packing list received',
      dimensions: 'Dimensions cm',
      lengthCm: 'Length',
      widthCm: 'Width',
      heightCm: 'Height',
      /* Like the volume hint: all three are needed to SEND, not to save
         (FR-12, N-15). A warning that names what it blocks, never a refusal. */
      dimensionsRequired:
        'All three are needed to send the offer. The position can be saved without them.',
      isChemical: 'Chemical cargo',
      chemicalNote:
        'Chemical cargo needs an MSDS and a transport certificate before the offer can be sent.',
      hasMsds: 'MSDS received',
      hasTransportCertificate: 'Transport certificate received',
      chemical: 'Chemical',
      readyTitle: 'Ready to send',
      readyDescription: 'Cargo is complete and the offer can go out.',
      emptyTitle: 'No cargo yet',
      emptyDescription:
        'Add the positions this quote covers. The offer cannot be sent until they are complete.',
      delete: 'Remove this position',
      deleteConfirm: 'Remove this position? This cannot be undone.',
      deleteConfirmAction: 'Remove',
    },

    /* The checklist counts what stands between a draft and a sent offer. */
    checklist: {
      blocked_one: '{{count}} thing blocks this offer',
      blocked_other: '{{count}} things block this offer',

      /* One line of the list: what is missing and what it blocks. Joined here
         rather than in code — the dash and the order of the parts are the
         language's business. */
      line: '{{issue}} — {{blocks}}',

      /* What is missing. The position is named by the number the user counts
         by, because this is read next to a numbered table. The unnumbered form
         covers a row that has no number yet. */
      issue: {
        dimensionsMissing: 'Dimensions missing on position {{position}}',
        dimensionsMissing_noPosition: 'Dimensions missing',
        volumeMissing: 'Volume missing on position {{position}}',
        volumeMissing_noPosition: 'Volume missing',
        msdsMissing: 'MSDS missing on position {{position}}',
        msdsMissing_noPosition: 'MSDS missing',
        certificateMissing: 'Transport certificate missing on position {{position}}',
        certificateMissing_noPosition: 'Transport certificate missing',
      },

      /* What it blocks. The chemical reason is named, or it is not clear why
         this position is held to a stricter rule than the one above it. */
      blocks: {
        send: 'Cannot send the offer',
        sendChemical: 'Cannot send the offer — cargo is chemical',
      },
    },

    routes: {
      title: 'Delivery options',
      transitDays_one: '{{count}} day',
      transitDays_other: '{{count}} days',
      /** Compact forms for the A/B/C rows, where the full sentence crowds. */
      daysShort: 'd',
      transitUpTo: 'up to {{count}} d',
      transitFrom: 'from {{count}} d',
      emptyTitle: 'No delivery options yet',
      emptyDescription: 'Delivery options you price appear here for the client to choose from.',
      add: 'Add option',
      /* The dashed tile in the empty grid slot. Names what the routing would be
         FOR rather than the act, because the act is already named by the button
         in the card header — and two controls with one name are two controls a
         screen reader cannot separate. */
      addNext: 'Option {{letter}} — for the client to compare',
      edit: 'Edit option',
      origin: 'Origin',
      points: 'Route points',
      noPoints: 'No points between origin and destination.',
      addPoint: 'Add point',
      removePoint: 'Remove point {{n}}',
      movePointUp: 'Move point {{n}} up',
      movePointDown: 'Move point {{n}} down',
      pointName: 'Point {{n}} name',
      pointCountry: 'Point {{n}} country',
      pointDays: 'Days to point {{n}}',
      pointPlaceholder: 'Place',
      daysPlaceholder: 'Days',
      destinationDays: 'Days to destination',
      applyDirection: 'Apply template',
      applyDirectionHint:
        'Copies the template: first point to origin, last to destination, the rest as points. Later edits to the template do not change the option.',
      destination: 'Destination',
      mode: 'Mode',
      indicativeTotal: 'Indicative total, USD',
      indicativeHint:
        'Shown on options B and C. The selected option is priced by its cost lines.',
      selected: 'Selected',
      select: 'Select',
      /* The FR-3 repricing warning. The server refuses the silent move; this
         is the question it asked. */
      selectWarning:
        'The cost lines price the currently selected option. After the switch they will describe this option — check them before the offer goes out.',
      selectConfirm: 'Switch selection',
      delete: 'Delete option',
      deleteConfirm: 'Delete this delivery option? The offer will no longer show it.',
      deleteConfirmAction: 'Delete',
      /* FR-3: the selected option holds the costing. An instruction, not an
         apology — the fix is one select away. */
      deleteHoldsCosting:
        'This option is selected and the cost lines price it. Select another option first — the offer is never left without the option its price was worked out for.',
      deleteFailed: 'The option could not be deleted. Try again.',
    },

    terms: {
      /* The first card under the «Terms» section's heading, so it names what
         it holds rather than repeating the section's word. */
      title: 'Incoterm, payment and validity',
      /* The card's subtitle while it can be edited in place. It says where the
         change lands, because otherwise it is unclear whether it rewrites an
         offer the client already has. */
      editHint: 'Changes apply to the next offer. Revisions already issued do not move.',
      /* Asked BEFORE the save (N-11): changing the Incoterm of a ready price
         sends it back to “Awaiting price”. No promise of who is notified — only
         those still working the quote are. */
      resetWarning:
        'The price is ready. Changing the Incoterm sends it back to “Awaiting price”, and it will have to be confirmed again.',
      resetConfirm: 'Save and reset the price',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
    },
    /* The third block of the reference quotation: IMPORTANT NOTES.

       "Note", not "disclaimer", and the difference is the reader's. A disclaimer
       is a lawyer's word for a paragraph that limits liability; what actually
       goes here is "free waiting time is normally two days at destination" —
       and the paper it prints on calls that a note. The stored kind is still
       `disclaimer`, because that is the word the contract and the templates use
       (0032); only the screen changed. */
    notes: {
      title: 'Notes',
      hint: 'Printed on the offer after the price.',
      frozen: 'Printed on the offer that was sent. A sent quote shows what the client was given.',
      empty: 'No notes yet.',
      /* The placeholder is the instruction — there is no label above the box,
         because the card heading already said what these are. */
      addPlaceholder: 'Add a note…',
      add: 'Add',
      edit: 'Edit this note',
      remove: 'Delete this note',
    },
    /* What the offer says the price covers and does not (Story 5.2, FR-60).
       These are words handed to a client, so the copy here describes what will
       be on the paper rather than what is in the database — "prints", not
       "saves". The headings match the settings templates the text is usually
       copied from, so a reader recognises them.

       The notes that used to be the third list here are `quotes.notes` now,
       on their own card. */
    blocks: {
      title: 'Included & not included',
      hint: 'Printed on the offer after the price: what the quote covers and what it does not.',
      frozen: 'Printed on the offer that was sent. A sent quote shows what the client was given.',
      /* The inclusions lead, because that is the order the offer prints them
         in. "Inclusion" as the noun and "Included" as the heading: the heading
         is the client's word, printed above the list on the paper. */
      included: 'Included',
      includedEmpty: 'Nothing listed as included yet.',
      addIncludedPlaceholder: 'Add an inclusion…',
      addIncluded: 'Add an inclusion',
      editIncluded: 'Edit this inclusion',
      removeIncluded: 'Delete this inclusion',
      notIncluded: 'Not included',
      notIncludedEmpty: 'Nothing excluded yet.',
      addNotIncludedPlaceholder: 'Add an exclusion…',
      addNotIncluded: 'Add an exclusion',
      editNotIncluded: 'Edit this exclusion',
      removeNotIncluded: 'Delete this exclusion',
      delete: 'Delete',
      capped:
        'This quote already carries as many entries of that kind as an offer can print. Delete one to make room.',
      loadFailed: 'These could not be loaded. Reload the page to try again.',
    },
    /* The action history (the History tab): the global action log's entries
       for this quote, translated from their machine keys — the server stores
       "quote.cargo.create", never a sentence (see QuoteDetailPage's
       ACTION_LABELS). Past tense throughout: these are facts about what
       happened, not buttons. */
    history: {
      title: 'History',
      emptyTitle: 'Nothing yet',
      emptyDescription: 'Every action on this quote is recorded here.',
      actions: {
        created: 'Quote created',
        updated: 'Quote details updated',
        cargoAdded: 'Cargo position added',
        cargoUpdated: 'Cargo position updated',
        cargoRemoved: 'Cargo position removed',
        costLineAdded: 'Cost line added',
        costLineUpdated: 'Cost line updated',
        costLineRemoved: 'Cost line removed',
        routeOptionAdded: 'Delivery option added',
        routeOptionUpdated: 'Delivery option updated',
        routeOptionSelected: 'Delivery option selected',
        routeOptionRemoved: 'Delivery option removed',
        blockAdded: 'Offer text block added',
        blockUpdated: 'Offer text block updated',
        blockRemoved: 'Offer text block removed',
        sent: 'Offer sent to the client',
        reopened: 'Reopened for a new revision',
        answerApproved: 'Client approved',
        answerRejected: 'Client rejected',
        answerRecorded: 'Client answer recorded',
        converted: 'Converted to a case',
        /* S-2a: the pricing state. Past tense, like its neighbours. */
        pricingRequested: 'Price requested',
        pricingReady: 'Price ready',
        pricingWithdrawn: 'Price request withdrawn',
        /* A preset is one request and one entry, however many lines it
           inserts; a markup re-prices every line at once. */
        costLinesBatchAdded: 'Cost lines added from a preset',
        markupApplied: 'Markup applied to every line',
        messagePosted: 'Message posted to the chat',
        /* Participants are the external sides of the shipment; the team is
           ASA staff. The same pair, and the same words, as the case feed. */
        participantAdded: 'Participant added',
        participantUpdated: 'Participant updated',
        participantRemoved: 'Participant removed from the quote',
        teamAdded: 'Colleague added to the team',
        teamRemoved: 'Colleague taken off the team',
      },
      /* An edit that returned a ready price to "Awaiting price" (the server
         annotates the entry `pricing: reopened`). A second line under the act,
         not an event of its own: this edit is what reset it. */
      pricingReopened: 'Price needed again — the quote changed after it was priced',
    },

    /* The quote's pricing state (S-2a): asking for a price as a RECORD, not a
       notification alone. The three state names are glossary terms. */
    pricingState: {
      title: 'Pricing',
      states: {
        none: 'Price not requested',
        requested: 'Awaiting price',
        ready: 'Price ready',
      },
      assignee: 'Pricing: {{name}}',
      requestedBy: 'Asked by {{name}}, {{when}}',
      requestedAt: 'Asked {{when}}',
      readyBy: 'Ready: {{name}}, {{when}}',
      /* A ready price sent back by an edit (N-11) — not a request: it went back
         to “Awaiting price” because something the price depends on changed.
         The name is who edited. */
      reopenedBy: 'Reset by an edit: {{causes}} — {{name}}, {{when}}',
      reopenedAt: 'Reset by an edit: {{causes}} — {{when}}',
      /* What changed, lower case — joined into the line with commas. «other»
         is for a code this client does not know yet. */
      causes: {
        cargo: 'cargo',
        routeOption: 'delivery option',
        transportMode: 'transport mode',
        pickupLocation: 'pickup location',
        incoterm: 'Incoterm',
        transitDays: 'transit time',
        measures: 'weight and volume',
        other: 'other',
      },
      noneHint:
        'Ask a Pricing colleague for the price — they are notified, and the quote waits for it.',
      actions: {
        request: 'Request price…',
        reRequest: 'Request again…',
        ready: 'Price ready',
        marking: 'Marking…',
        withdraw: 'Withdraw request',
        withdrawing: 'Withdrawing…',
      },
      sheet: {
        title: 'Request price',
        reTitle: 'Request the price again',
        colleague: 'Who prices it',
        hint: 'Only colleagues who may price quotes.',
        empty:
          'There is nobody to ask: no role may price quotes yet. Grant it in the role settings.',
        blocked: 'Choose who will price it first.',
        note: 'Note for them',
        noteHint: 'One line — what you need and by when. Optional.',
        submit: 'Request',
        reReady: 'The ready price is cleared: the quote will wait for pricing again.',
      },
      errors: {
        required: 'Choose who will price it.',
        notAssignable: 'This account cannot sign in — choose another colleague.',
        notPricing: 'This colleague may not price quotes — choose another.',
        noteTooLong: 'The note is longer than 500 characters — shorten it.',
        alreadyReady: 'The price is already marked ready — refreshed.',
        notRequested: 'There is no request any more: it was withdrawn or the price is ready. Refreshed.',
      },
      /* O7: a warning on send, never a gate. */
      sendWarning: 'Price requested, but not ready yet',
      sendWarningBody: 'You can still send it, but the price on the offer may change.',
    },
    revisions: {
      title: 'Revisions',
      emptyTitle: 'No revisions yet',
      emptyDescription: 'Each issued offer is kept here as it was sent.',
      /* A revision with no note of its own. */
      issued: 'Issued',
      /* Why the button is off. The status is the reason, so the sentence names
         it rather than a bare refusal. */
      notReopenable: 'A new revision can only be opened on a sent or rejected quote.',
      /* Why the header cannot be typed into. Says what to DO — an issued quote
         changes by revision and never in place (FR-7), so the next act is the
         button beside this one, not a retry of this one. */
      notEditable:
        'A sent quote is edited by opening a new revision, not in place.',
    },
    costLines: {
      title: 'Cost lines',
      add: 'Add line',
      edit: 'Edit line',
      description: 'Description',
      vendor: 'Vendor',
      /* The hint under the vendor field. It says exactly what migration 0019
         decided: the directory suggests here, it does not constrain. */
      vendorHint: 'Pick from the directory, or type your own',
      vendorPlaceholder: 'Start typing a name',
      vendorNoMatches: 'No counterparty by that name',
      vendorFreeTextHint: 'The name you typed is kept as it is',
      qty: 'Qty',
      quantity: 'Quantity',
      /* The FIRST money column, and the one price a line form asks for: the
         unit COST, exactly as in the source workbook's INTERNAL COST AND
         SELLING block. The client price is that figure carried up by the
         quote's one stored markup, so it is derived and never typed. The
         columns are Qty | Unit cost | Cost | Markup | Client price (S-5, owner
         decision O5); the key keeps its old name, the words are the cost's. */
      unitPrice: 'Unit cost',
      /* The SECOND money column: the unit cost carried out by the quantity —
         what the line costs. Its column total is the cost total. Read-only in
         both the read row and the open one: arithmetic, never an entry. */
      amount: 'Cost',
      /* The THIRD: the markup the line is priced at, over its cost — the
         server's figure (the quote's markup, or the line's own when it is
         priced differently, which only a legacy draft holds). */
      markup: 'Markup',
      /* The marker on a line that is not at the quote's markup: a shape and
         these words, never a colour alone. */
      offMarkup: 'Not at the quote’s markup',
      /* Rate memory REPORTS; it never pre-fills (FR-17). Accepting it puts the
         remembered figure in the unit cost field, where it can be read and
         overtyped before anything is saved. */
      rateMemory: 'Last on this lane: {{amount}} · {{case}} · {{month}}',
      useRate: 'Use',
      /* The phone footer's two rows (O5): the cost total and the client price,
         each the sum of what the cards above it carry. "Total cost" rather
         than "Totals": the row states ONE side of the quote.

         "Client price" is the QW-2 glossary word (owner decision O3) for the
         quote's computed amount, and it heads the last column too: the line's
         share of it above, the whole of it in the footer. */
      costTotal: 'Total cost',
      customerTotal: 'Client price',
      /* The desktop footer's first row: each figure under the column it adds
         up — the cost under "Cost", the client price under "Client price". */
      total: 'Total',
      /* The footer's second row, and the one vocabulary (S-5, L19): the markup
         is what is set, the margin what results, and the two are shown
         together. Percentages arrive formatted ("15.00%"). The mixed form says
         the markup is an AVERAGE — the lines are not all at one markup. */
      markupAndMargin: 'Markup {{markup}} · Margin {{margin}}',
      markupAndMarginMixed: 'Markup {{markup}} on average · Margin {{margin}}',
      /* The phone card's second line, beneath the client amount (O5). */
      costAndMarkup: 'cost {{cost}} · markup {{markup}}',
      /* Q-12 / P0-4: since 2026-09-29 the offer prints ONE line, the freight
         rate at the client price (PrintedRevisionLines). */
      offerPrintsOneLine:
        'The client’s offer prints one line, “Freight rate”, at the client price. The client does not see these cost lines.',
      delete: 'Remove line',
      deleteConfirm: 'Remove this line and its share of the margin?',
      deleteConfirmAction: 'Remove',
    },

    /* Price from the cost side (FR-1): the markup panel on the costing tab.
       Asking a colleague for the price lives in `pricingState` below (S-2a). */
    pricing: {
      /* The markup panel. Two percentages, two labels — see MarginPanel.

         NO `customerTotal` HERE, deliberately. The panel prints `totals.sell`,
         which is the same figure the cost table's footer prints, and since the
         panel was mounted on the costing tab the two sit centimetres apart on
         one screen. It words that figure with `quotes.costLines.customerTotal`
         — the costing's own vocabulary, shared with the phone list and the
         wizard header. A second entry here held the identical string and was
         one reword away from making one number look like two. */
      title: 'Price from the cost side',
      cost: 'Cost',
      /* Named for its denominator, because the field beside it is a percentage
         over the OTHER one and the pair only reads correctly when each says
         which it used. */
      marginOverRevenue: 'Margin over client price',
      /* The input: «Наценка» is what is SET, the margin what results (S-5,
         L19). The base it is taken from is the hint's job, above it. */
      markup: 'Markup, %',
      /* The quote's stored markup, read-only, where a quote that has gone out
         shows it instead of the field (L18). */
      markupStored: 'Quote markup',
      /* The costing's effective markup beside the margin (O6): the stored one
         while every line is at it, an average when they differ. */
      markupEffective: 'Markup',
      markupAverage: 'Markup on average',
      /* Q-11: the two percentages side by side are one profit against two
         bases. The hint names both bases and gives a pair the reader can check
         in their head: 15% on 100 is 115, and 15 of 115 is 13.04%. Two
         decimals, as the margin beside it (O4 / L19) and the glossary. */
      markupHint:
        'A 15% markup is a 13.04% margin: the same profit, measured against cost for the markup and against the client price for the margin.',
      applyMarkup: 'Apply',
      /* Said BEFORE the press: the markup is now STORED on the quote (S-5), so
         Apply is a setting, not a one-off re-price, and a line written later
         follows it. */
      applyMarkupConfirm:
        'Saves the markup on the quote and re-prices every line at it. A line added or edited later takes the same markup.',
      /* Why Apply is refused while the field holds something it cannot store:
         the server takes 0 or more, at most four decimal places. */
      markupInvalid: 'Enter a markup of 0 or more, with at most four decimal places.',
      /* L10: a draft whose lines are not all at the quote's markup — only a
         costing priced before the markup was stored can hold one. Said before
         anybody edits, with what each act will do. */
      markupMixed_one:
        '{{count}} line is priced at a different markup. A line you edit takes the quote’s markup; Apply re-prices them all.',
      markupMixed_other:
        '{{count}} lines are priced at a different markup. A line you edit takes the quote’s markup; Apply re-prices them all.',
      preset: 'Preset',
      presetHint:
        'The standing charge lines, inserted with no amounts. Maintained in Settings.',
      noPreset: 'Settings holds no standing charge lines yet.',
    },
  },

  finance: {
    title: 'Money',
    /* "Who owes whom" is the actual question. Say "Money", never "Financials". */
    subtitle: 'Who owes whom.',
    sectionsLabel: 'Finance sections',
    /* N-21: the one state a reader without `finance.receivables.read` sees on
       /finance — instead of the heading, the tabs and a refusal per tab. */
    noAccess: {
      title: 'This section is not available',
      description:
        'Money across all cases opens with the permission “See aging, balances and the receivables register across cases”. Ask an administrator if you need it.',
    },

    tabs: {
      balances: 'Balances',
      aging: 'Aging',
      payments: 'Payments',
      /* "Rate", because this tab holds rate changes only (QW-10, F-1). Payment
         confirmations are a different queue on the Payments tab, and one word
         for both sent Finance to the wrong one. */
      approvals: 'Rate approvals',
      documents: 'Documents',
    },

    balances: {
      counterparty: 'Counterparty',
      type: 'Type',
      /* Which directory a row comes from. The server sends a CODE
         (`client` / `participant`), which was printed raw until QW-1. */
      kinds: {
        client: 'Client',
        /* `participant` is a counterparty directory record; the word
           "Participant" left with the in-case participants (S-22a). */
        participant: 'Counterparty',
      },
      receivable: 'Receivable',
      payable: 'Payable',
      net: 'Net',
      oldest: 'Oldest',
      /* The age of the oldest unsettled item, in days. ALWAYS PRESENT and
         SIGNED: the live read sends a number on every row, and a party that has
         nothing outstanding is not on the list at all. A NEGATIVE age — nothing
         due yet — is rendered with the aging bucket's own words rather than a
         second phrasing here, so the two screens cannot come to disagree in one
         language and not the other. */
      oldestDays_one: '{{count}} day',
      oldestDays_other: '{{count}} days',
      /* Names the grid per issuing entity, because there is one table per entity
         (FR-35) and "Balances" four times over identifies none of them. */
      tableLabel: 'Balances — {{entity}}',
      /* S-12: outbound payments nobody has allocated, all time and every
         case. The count and the per-currency sums as served — never
         converted or added (AD-9). */
      unallocatedOut: 'Unallocated outbound payments: {{n}} · {{amounts}}',
      /* N-04: what the balance rows leave out. An instalment names no
         counterparty (AD-26), so it cannot be a row, but Aging and the
         dashboard's «To collect» count it. The amount is printed as served. */
      uninvoiced: 'Excludes amounts not yet invoiced: {{amount}}',
      uninvoicedHint:
        'Instalments not yet invoiced: {{n}}. They are in Aging but not in the balances, because an instalment names no counterparty.',
      unallocatedShow: 'Show',
      emptyTitle: 'No balances in this period',
      emptyDescription:
        'A counterparty appears here once a case carries money against them — an invoice raised, a tranche due, or a vendor cost booked.',
    },

    aging: {
      title: 'Receivable by age',
      /* Five groups, not three. The two extra ones carry the whole point of
         the surface: FR-34 AC 5 forbids "outstanding" being read as "overdue",
         and AC 6 forbids an undated item being defaulted into a bucket. */
      buckets: {
        'not-yet-due': 'Not yet due',
        '0-30': '0–30 days',
        '31-60': '31–60 days',
        '60-plus': '60+ days',
        'no-date': 'No expected date',
      },
      emptyTitle: 'Nothing outstanding in this period',
      emptyDescription:
        'Tranches, issued invoices and vendor costs appear here until they are settled.',
    },

    payments: {
      title: 'Payments',
      record: 'Record payment',
      reference: 'Reference',
      referenceHint: 'The bank reference, as it appears on the statement.',
      /* Either direction (P-1): "Received" misread on an outgoing payment. */
      received: 'Payment date',
      amount: 'Amount',
      currency: 'Currency',
      direction: 'Direction',
      in: 'Incoming',
      out: 'Outgoing',
      /* Names the facet on the log. `direction` above labels the FIELD on the
         record sheet; a filter chip and a form label read differently even when
         they concern the same column. */
      directionLabel: 'Direction',
      allocated: 'Allocated',
      unallocated: 'Unallocated',
      /* The row opens as a sheet rather than expanding in place (UX-DR-4). */
      detailTitle: 'Payment {{reference}}',
      /* The case the payment belongs to, in the payment's sheet — a link, with
         the lane after a dot. Shown only when the case can be read. */
      caseLine: 'Case {{number}}',
      /* The visible answer to «Confirm»: the buttons leave together with the
         pending tag, and without this line the press would look like nothing
         happened. */
      confirmed: 'Payment confirmed',
      emptyTitle: 'No payments in this period',
      emptyDescription:
        'Money in and out appears here as it is recorded, with what each payment settles.',
      case: 'Case',
      caseHint: 'The case this money moves against.',
      method: 'Method',
      methods: {
        cash: 'Cash',
        bank_transfer: 'Bank transfer',
      },
      /* OURS, not theirs — the two readings of "issuing entity" produce
         different books, so the label says whose. */
      entity: 'Our entity',
      entityHint:
        'Which of our legal entities the money reached or left — not the counterparty.',
      /* The booking the server took at the case's rate basis (AD-9). Read from
         the row, never recomputed on screen. */
      bookedAt: 'Rate {{rate}} on {{date}} · {{usd}} USD',
      /* Unallocated cash is legal and visible; it is never auto-applied (AD-11). */
      allocationNote:
        'The payment lands unallocated. Apply it to invoices as a separate step.',

      counterpartyPlaceholder: 'Choose a counterparty',
      counterpartySearch: 'Search the directory',
      /* The hint on the case client's row in a payment's party list: the
         client is a different directory from the counterparties under it. */
      counterpartyKindClient: 'Client',
      /* A name saved before this field became a picker, or read off a transfer
         for a company nobody has a record of. It stays SELECTED: nothing here
         may reject data that is already saved. */
      counterpartyAsEntered: 'as entered',
      counterpartyAdd: 'Add a counterparty',

      /* The payment's party — whom we paid or whom we received from — by
         record rather than by spelling. Outgoing names a directory
         counterparty or the case client (a refund); incoming names the case
         client or another payer, as the bank printed it. */
      party: {
        caseClient: 'Case client — {{name}}',
        /* A client stored on the payment who is no longer this case's client:
           kept, never offered afresh. */
        asRecorded: 'as recorded',
        other: 'Another payer',
        otherName: 'Payer, as on the bank statement',
        hintOut: 'Who the money went to: a directory counterparty, or the case client for a refund.',
        hintIn: 'Who the money came from: the case client, or another payer as on the bank statement.',
        pickOut: 'Pick a directory counterparty or the case client',
        clientChanged: 'This is no longer this case’s client — pick again',
        /* S-22f: an outbound payment already allocated to costs cannot be
           re-addressed to the case client — a refund settles no cost. */
        refundAllocated: 'The payment is already allocated to costs — it cannot become a refund',
      },

      /* The bank's charge for moving the money (migration 0058).
         BLANK means no fee was recorded, never a fee of zero — a zero would
         read on a report as a transfer that cost nothing. */
      feeHeading: 'Transfer fee',
      fee: 'Fee',
      feeCurrency: 'Fee currency',
      feeHint:
        'What the bank took to move this money. Optional. It never reaches the counterparty and settles nothing — only the payment amount above does.',
      /* On the payment card, its own line beside the amount. The two figures
         are never summed, which is why they are not in one total. */
      feeRecorded: 'Transfer fee {{amount}}',

      /* The pending-confirmation workflow (0059, spec L3/L4/L8).
       *
       * Every sentence here is about the RECORD and never about the money. A
       * pending payment is counted in «Оплачено», in the balances and in the
       * aging exactly like a confirmed one, so nothing may read as "this money
       * does not count yet" — the wording that would send somebody to pay a
       * carrier twice.
       *
       * And there is no «отклонён» status to name, because rejection is a
       * DELETION: a rejected row left on the ledger is a figure every sum then
       * has to remember to exclude. */
      status: 'Status',
      statuses: {
        pending: 'Awaiting confirmation',
        confirmed: 'Confirmed',
      },
      /* The badge on a row. Short, because it sits beside a reference and a
         figure — the long form is in the status column and the filter. */
      pendingTag: 'not confirmed',
      pendingFilter: 'Awaiting confirmation ({{count}})',
      /* Above the queue: the page's range control keeps showing its own period
         while the queue spans every one. */
      pendingAllPeriods:
        'Every unconfirmed payment, from all periods — the date range above does not apply to this queue.',
      pendingEmptyTitle: 'Nothing awaits confirmation',
      pendingEmptyDescription: 'Every recorded payment has been checked.',
      /* S-12: the narrowing the balances tab's «Show» leads to. */
      unallocatedOutFilter: 'Unallocated outbound',
      unallocatedOutEmptyTitle: 'Nothing to allocate',
      unallocatedOutEmptyDescription: 'Every outbound payment is allocated to a cost.',
      confirm: 'Confirm',
      confirmedOn: 'Confirmed {{date}}',
      reject: 'Reject',
      rejectTitle: 'Reject payment {{reference}}',
      rejectBody:
        'The record is deleted together with what it settled. The carrier is still owed the cost — that line stays. Only a payment nobody has confirmed can be rejected.',
      rejectReason: 'Reason',
      rejectReasonHint:
        'Required. The row is about to be gone, so the audit trail is the only place left that can say why.',
      rejectSubmit: 'Reject and delete',
      /* S-18a: one payment can settle several costs — each stays a debt once
         the payment is deleted (L8). */
      rejectDebtsStay: 'These debts stay:',
      amend: 'Correct',
      amendTitle: 'Correct payment {{reference}}',
      amendSubmit: 'Save the correction',
      amendFixedFacts:
        'The currency ({{currency}}) and the date the money moved ({{date}}) cannot be corrected — every booking on this payment hangs off them. If either is the mistake, reject the record with a reason and enter it again.',
      amendAction: 'Correct payment {{reference}}',
      rejectAction: 'Reject payment {{reference}}',
      rejectBodyIn:
        'The record is removed with its allocations — the client’s documents become unpaid again by those amounts. Only a record nobody has confirmed can be removed.',
      delete: 'Delete',
      deleteTitle: 'Delete payment {{reference}}',
      deleteSubmit: 'Delete payment',
      deleteAction: 'Delete payment {{reference}}',
    },

    /* Allocating an OUTBOUND payment to the case's costs (Story 4.5, AD-11).
     * A client's payment received is allocated since S-21 in «Client
     * payment» (`clientPayment`), to the case's invoices, debit notes and
     * tranches. */
    allocations: {
      heading: 'Allocated to',
      allocate: 'Allocate',
      submit: 'Allocate',
      title: 'Allocate {{reference}}',
      none: 'Nothing allocated yet. Unallocated cash is legal — it is applied when someone says what it settles, never automatically.',
      fullyAllocated: 'This payment is fully allocated — there is nothing left to apply.',
      /* S-22f: an outbound payment to the case client is a refund — the
         client's money, not a supplier payment — and settles no cost line. */
      refundNotCost: 'A refund to the client cannot be allocated to a cost',
      target: 'Settles',
      targetPlaceholder: 'Choose a cost line',
      targetN: 'Split {{n}} target',
      targetRequired: 'Choose what this split settles.',
      /* An OUTBOUND payment settles a cost, never a tranche — the server
         refuses the mismatch outright (`ErrAllocationDirectionMismatch`), so
         offering tranches here was offering the one thing that cannot work.
         The empty state says the act that would change it rather than repeating
         the receivable advice, which has nothing to do with paying a carrier. */
      expenseTargetsNote:
        'The costs recorded on this case — an outgoing payment settles those, not the client’s tranches.',
      expenseTargetsNone:
        'No costs are recorded on this case yet, so there is nothing for this payment to settle. Record what is owed first — “Pay the carrier” on the leg does both in one step.',
      /* The OTHER empty list: costs are recorded and every one is paid in full.
         The sentence above would be false here, and its advice — record what is
         owed — is how a carrier comes to be paid twice. What is left to do is
         remove a duplicate, and only an unconfirmed record can be removed. */
      expenseTargetsAllPaid:
        'Every cost on this case is already paid, so there is nothing for this payment to settle. If it records a payment twice, reject it — only an unconfirmed record can be rejected.',
      /* Said on the BUTTON rather than inside the sheet. Opening a picker whose
         read has not answered would show "this case has no costs" about a case
         that may well have several. */
      expenseTargetsLoading: 'Reading the costs recorded on this case…',
      expenseTargetsFailed:
        'The costs on this case could not be read, so there is nothing safe to offer. Retry before allocating.',
      amountN: 'Split {{n}} amount',
      amountHint: 'In {{currency}}, the currency the payment arrived in.',
      add: 'Add split',
      remove: 'Remove split {{n}}',
      requested: 'Applying',
      splitsRequired: 'Name the document and the amount for every split.',
      exceedsBalance:
        'The splits add up to more than the {{balance}} still unallocated on this payment.',
      /* The settlement booking on the allocation row — not the document's own,
         which is never rewritten (AD-9). A NON-USD allocation only: a USD one
         shows the USD figure alone, since «at 1» restated the currency and
         read as a conversion that never happened (F1, the QW-6 precedent). */
      settledAt: 'at {{rate}} · {{usd}} USD',
      /* The settlement list's columns. «Booked» names the cell that reads
         «at 7.250000 · 2 500.00 USD» — the rate this settlement was taken at
         and what it came to in USD, which is the target's booking and not the
         payment's. A USD allocation reads «2 500.00 USD» there. */
      columns: { target: 'Settles', amount: 'Applied', booked: 'Booked at', at: 'Allocated' },
      fxDifference: 'FX {{value}}',
      fxDifferenceHint:
        'The difference between settling at this rate and the rate the document was booked at. Recorded here; the document’s booked value is unchanged.',
    },

    /* S-21: a case's receivables by document — invoices, the client's debit
     * notes and tranches, with what is paid and what is left. The figures are
     * the server's; only the words are here. Labels are composed from a
     * document's number or a tranche's place in its series, never from the
     * server's English `targetLabel`. */
    receivables: {
      invoice: 'Invoice {{number}}',
      debitNote: 'Debit note {{number}}',
      tranche: 'Tranche {{sequence}} of {{of}}',
      status: {
        open: 'Awaiting',
        partial: 'Part-paid',
        paid: 'Paid',
        overpaid: 'Overpaid {{amount}}',
      },
      replacedBy: 'replaced by invoice {{number}}',
      invoiceIssued: 'issued {{date}} · {{total}}',
      debitNoteIssued: 'issued {{date}} · {{total}}',
      trancheDue: 'not invoiced · due {{date}}',
      trancheUndated: 'not invoiced · no agreed date',
      paid: 'Paid',
      outstanding: 'Outstanding',
      pay: 'Record payment',
      payFor: 'Record a payment for {{target}}',
      carryOver: 'The {{amount}} already received on {{tranche}} moves onto this invoice.',
    },

    /* S-21: «Client payment» — record a client's payment and allocate it to
     * the case's open receivables in one act. Nothing is allocated by itself:
     * only the row the sheet was opened from starts filled. */
    clientPayment: {
      title: 'Client payment',
      allocateTitle: 'Allocate payment {{reference}}',
      receivedOn: 'Received on',
      entityHint: 'Which of our entities received the money.',
      feeSummary: 'Bank fee',
      pending:
        'The payment goes to Finance for confirmation. Until then you can correct or cancel it.',
      debtsHeading: 'Open receivables',
      columns: {
        document: 'Document',
        outstanding: 'Outstanding',
        apply: 'Apply',
        applyIn: 'Apply, {{currency}}',
      },
      openedFrom: 'ticked — opened from it',
      inPayment: '≈ {{amount}} at the rate of {{date}}',
      wholeRemainder: 'Full remainder',
      wholeRemainderFor: 'Full remainder of {{target}}',
      rowAmount: 'Amount for {{target}}',
      rateMissing:
        'There is no rate for {{date}} yet, so receivables in another currency are not converted here. The server checks the amounts on them when you record.',
      nothingOpen:
        'This case has no unpaid invoices, debit notes or tranches. The payment is recorded unallocated — it does not reduce the client’s debt.',
      nothingOpenAllocate:
        'This case has no unpaid invoices, debit notes or tranches — there is nothing to allocate this payment to.',
      receivablesLoading: 'Reading the case’s open receivables…',
      receivablesFailed:
        'The case’s receivables could not be read. You can record the payment unallocated or try again.',
      footer: {
        received: 'Received',
        payment: 'Payment',
      },
      submitAllocate: 'Record and allocate',
      blocked: {
        rowOver: '{{target}} takes at most {{amount}}',
        rowAmount: 'Enter an amount for every ticked receivable',
        totalOver: 'More is allocated than received — reduce the amounts',
        totalOverUnallocated:
          'More is allocated than is left on the payment ({{amount}}) — reduce the amounts',
        nothingTicked: 'Tick what the payment settles',
        /* A new date or currency is a new read of the remainders; until it
           answers there is nothing to send. */
        reading: 'The open debts are still being read — wait a moment',
      },
      alreadyRecorded:
        'This payment has already been recorded — the payment list has been refreshed. Close this window and check «Payments received».',
      refusal: {
        overTarget:
          'At most {{remaining}} can go to {{target}} — the rest is already paid. The remainders have been refreshed; correct the amount.',
        overPayment:
          'Only {{remaining}} is left on the payment — {{target}} cannot take that much. The remainders have been refreshed; correct the amounts.',
        overPaymentUnnamed:
          'Only {{remaining}} is left on the payment. The remainders have been refreshed; correct the amounts.',
        targetClosed:
          '{{target}} has been replaced by invoice {{number}} — allocate the payment to that invoice. The remainders have been refreshed.',
        unnamed: 'this document',
      },
    },

    /* S-21: undoing one or more allocations of a payment received — with a
     * reason, which goes to the history: the allocation row stops existing. */
    reverse: {
      action: 'Undo allocation',
      title: 'Undo allocation',
      what: 'What to undo',
      reason: 'Reason',
      reasonHint: 'Required — the reason goes to the history.',
      consequence:
        'The ticked amounts become unallocated again, and the documents unpaid by those amounts. The change goes to the history.',
      keep: 'Keep',
      submit: 'Undo allocation',
      submitting: 'Undoing…',
      pickRequired: 'Tick what to undo',
      stopped: '{{done}} of {{total}} undone; the server refused the next one:',
    },

    approvals: {
      pending: 'Pending',
      current: 'Current',
      requested: 'Requested',
      change: 'Change',
      approve: 'Approve',
      reject: 'Reject',
      confirmTitle: 'Approve the rate change on {{number}}',
      confirmBody:
        'The rate on {{number}} changes from {{current}} to {{requested}}. Approval is binding — a wrong rate needs a new request, not an undo.',
      emptyTitle: 'No approvals waiting',
      emptyDescription:
        'Rate changes needing a decision appear here and stay until resolved.',
    },

    documents: {
      title: 'Invoices and debit notes',
      newInvoice: 'New invoice',
      edit: 'Edit document',
      number: 'Number',
      /* The number is minted from the entity's scheme, never typed (AD-12). */
      numberNote: 'The number is assigned from this entity’s numbering scheme.',
      entity: 'Issuing entity',
      case: 'Case',
      issued: 'Issued',
      status: 'Status',
      due: 'Due',
      total: 'Total',
      outstanding: 'Outstanding',

      /* Everything from here down is the raise → generate → issue sheet and
       * its preview, and it lives under `finance` rather than under `cases`
       * on purpose: the case's Documents section and the Finance page raise
       * and preview through the SAME component and the same mutation. The
       * case adds a filter and a prefill; it does not fork the form. A second
       * copy of this copy under `cases` would be the first step towards
       * forking it, and the two screens would then drift apart in wording
       * before they drifted apart in behaviour.
       */

      /* The two lists the section is split into, and the render state of a row
         inside them. "Not rendered" is SAID rather than left blank, because a
         blank cell in an archive reads as a column that failed to load rather
         than as a document nobody has generated yet. */
      debitNotes: 'Debit notes',
      invoices: 'Invoices',
      rendered: 'Rendered',
      notRendered: 'Not rendered',

      /* N-30: the company's register of issued paper — the Documents tab with no
         case chosen. Issued only (a draft raises no receivable, FR-62). «Total»
         is in the document's own currency, «Paid» and «Outstanding» in booked
         USD: each column carries its own currency beside the number. */
      register: {
        allCases: 'All cases',
        caseColumn: 'Case',
        entityColumn: 'Entity',
        paidColumn: 'Paid, USD',
        outstandingColumn: 'Outstanding, USD',
        emptyTitle: 'No issued documents',
        emptyDescription:
          'Invoices and debit notes the company has already issued appear here. Choose a case to see its drafts and raise a new document.',
        capped: 'Showing the latest {{shown}} of {{total}} documents. Narrow the period to see the rest.',
        openCase: 'Open case {{number}}',
        noFile: 'This document has not been rendered.',
        fileUnavailable: 'The file of this document is not available to you.',
      },

      /* Three verbs, three server calls, and deliberately no shortcut that
         does two of them at once: generating is not issuing (UX-DR-15), and an
         "issue on generate" flag is something a client sets by accident.
         "Generate again" is a separate word from "Generate" because the second
         press replaces a file that is already in the archive, and a button
         that still said "Generate PDF" would read as a first render. */
      actions: {
        generate: 'Generate PDF',
        regenerate: 'Generate again',
        generating: 'Generating…',
        issue: 'Issue',
        issuing: 'Issuing…',
        /* The row's own actions. Icons, but every one of them names itself —
           read aloud by a screen reader, shown on hover — and the two
           dangerous ones (issue, discard) are decided in a dialogue where
           everything is spelled out in words. An icon here opens a
           conversation; it does not commit an act. */
        preview: 'View the document',
        open: 'Open the invoice',
        discard: 'Discard the draft',
      },

      /* Only a draft can be discarded. A client is holding the issued one, and
         a number that leads nowhere is worse than a document somebody has to
         explain (AD-24). The ceiling is named separately because it is the
         reason to discard at all: an abandoned draft's charges eat the headroom
         of every invoice raised after it. */
      discardConfirm: {
        title: 'Discard this draft?',
        body: 'The draft and its charges are removed for good. While it exists, its figures take up part of the agreed rate and reduce what is left for later invoices.',
        confirm: 'Discard',
      },

      /* Issue is the confirmed one because it is the irreversible one.
         Generating can be repeated all afternoon and costs nothing but a
         render; issuing mints the number, freezes the document and creates the
         receivable, and AD-24 makes that one-way. The body names all three
         consequences and then names the remedy, because "cannot be undone"
         on its own reads as a dead end rather than as the ordinary way paper
         is corrected. */
      issueConfirm: {
        title: 'Issue this document?',
        body: 'Issuing mints the number from this entity’s scheme, freezes the document and creates the receivable. It cannot be undone — a correction is a new document, raised against this case.',
        confirm: 'Issue',
      },

      /* AC 7, and a blocking message in the house form: the block and its
         reason together, so the rule is learned once. The number is stamped
         onto the archived file, so a record nobody has rendered has nothing to
         stamp — which is the whole of the reason, and why this says what the
         PDF is for instead of saying "not allowed yet". */
      needsRender: 'The PDF has to be generated before this can be issued — the number is stamped onto the archived file.',
      /* AC 9. This is the affordance, not the guard: the server refuses the
         edit regardless. The sentence exists so the screen says why the fields
         are gone rather than presenting ones that would be rejected. */
      issuedReadOnly: 'An issued document cannot be edited. Raise a new one to correct it.',
      /* The refusal a SECOND Issue press earns. Worded as a fact about the
         first press rather than as an error, because what the caller needs to
         learn is that the document went out — swallowing the refusal, or
         dressing it as a failure, teaches the opposite (UX-DR-24). */
      alreadyIssued: 'This document has already been issued.',
      /* The `document` module is nil-able: with no object storage configured
         main.go never constructs it and its routes are never mounted, so a
         laptop without a bucket gets a 404. It must not render as an empty
         list — an empty list says there is no paper on this case, and the
         truth is that there is no archive to look in. */
      archiveUnavailable: 'The document archive is not configured in this environment, so nothing can be rendered or shown here.',

      /* Two different things wear the word "preview", and this copy is what
         keeps them apart. The DRAFT preview is a layout the client draws from
         the fields on screen — no render, no archive row — so that the
         type-a-line-see-it loop does not cost a page render per keystroke. The
         RENDERED one is the archived PDF itself, the bytes the client
         receives. Somebody who only ever saw the first has not seen the
         document, which is why the second exists and why neither may borrow
         the other's words or offer the other's actions. */
      preview: {
        /* The sheet that shows the SERVER-composed page — the same template
           the client's PDF comes off. It sits in the same branch as the words
           below on purpose: those describe the hand-drawn layout this replaces,
           and keeping both under one key is what makes the pair visible to
           whoever removes the last of the old one. */
        title: 'Document',
        subtitle: 'How the document reads on paper. A draft prints DRAFT where its number will go.',
        composeFailed: 'The page could not be composed.',

        draftBadge: 'Draft',
        draftTitle: 'Draft preview',
        draftHint: 'This is a layout of what has been typed, not the document. Press Generate to produce the PDF the client will receive.',
        renderedTitle: 'Rendered document',
        /* AC 8. Shown instead of yesterday's PDF beside today's figures: the
           screen falls back to the live draft and says which of the two the
           reader is now looking at, rather than silently keeping a stale
           frame that still looks authoritative. */
        outOfDate: 'This draft has changed since it was rendered, so the archived copy is out of date. Generate again before issuing.',
        /* The signed URL is good for five minutes (AD-18), so that a link
           copied out of the page dies before it can be shared. Naming the
           expiry is what makes the retry beside this read as the ordinary next
           step rather than as a fault the reader caused. */
        failed: 'The document could not be loaded. Its link expires after five minutes.',
        loading: 'Loading the document…',
        empty: 'Pick a document on the left to see it.',
        /* The <iframe>'s accessible name. Without it a screen reader announces
           an unnamed frame in the middle of the page and the reader has no way
           to know what it holds. */
        frameTitle: 'Document preview',
      },

      /* The raise form itself — one form, both screens. */
      raise: {
        kind: 'Kind',
        counterparty: 'Billed to',
        /* No payer is picked (S-22a): a new debit note bills the case's client
           only, and the printed name and address come from the client record
           (AD-26). */
        counterpartyHint: 'A debit note is billed to this case’s client. The printed name and address come from the client record.',
        /* The header amend: the payer was fixed when the note was raised and
           never moves — only the date and their reference do. */
        counterpartyFixed: 'The payer was set when the note was raised and does not change.',
        reference: 'Their reference',
        noteDate: 'Document date',
        currency: 'Currency',
        /* An invoice arrives with the accepted quote's charges, copied by the
           server. Saying so is what stops somebody hunting this form for a
           charge table that is deliberately not on it. */
        submit: 'Raise',
      },

      /* The fork under "New document". The two papers are not two flavours of
       * one form: an invoice bills the CLIENT out of the rate agreed with them
       * (AD-10), a debit note re-bills a cost to whoever owes it (AD-26). The
       * question used not to be asked at all — the first screen offered the
       * case's own costs as tickboxes, and any tick silently made the document a
       * debit note. Hence a sentence per item: two words do not say which side
       * of the deal the paper faces or where its lines come from. */
      new: {
        invoice: 'Client invoice',
        invoiceHint: 'From the agreed rate. The charges are prefilled from the quote.',
        debitNote: 'Debit note',
        debitNoteHint: 'Re-bill a case cost to the client.',
      },

      lines: {
        label: 'Charges',
        /* A debit note IS its lines: the client is being billed for something
           nobody quoted, so "which charge" is the whole question. */
        empty: 'No charges yet. A debit note needs at least one — the client is being billed for something that was never quoted.',
        add: 'Add charge',
        description: 'What is being charged',
        descriptionN: 'Charge {{n}} description',
        notePlaceholder: 'Qualifier, e.g. 4 Containers (Open-top) — optional',
        noteN: 'Charge {{n}} qualifier',
        unitPrice: 'Unit price',
        unitPriceN: 'Charge {{n}} unit price',
        quantity: 'Qty',
        quantityN: 'Charge {{n}} quantity',
        remove: 'Remove charge {{n}}',
      },
    },

    /* The invoice's own workplace — `routes/CaseInvoicePage.tsx`.
     *
     * A PAGE rather than the third modal in a chain, because an invoice is a
     * document that prints on A4 and is read across: the charges, the ceiling
     * they are judged against and the booking that converts them are one
     * picture, and a 440px column can hold at most one of the three at a time.
     *
     * The copy lives under `finance` beside `documents` and `invoices` rather
     * than under `cases`, for the reason the block above states: the case's
     * section and the Money page reach the same paper, and a second catalogue
     * for one of them is where two screens start describing one record
     * differently. */
    invoiceDesk: {
      /* The way back. It names the CASE and the section, because the reader
         arrived from one of two places and both of them are that section. */
      back: 'Invoices',
      title: 'Client invoice',
      trancheN: 'Tranche {{n}}',
      /* An invoice that bills no instalment. Not "none" and not a blank: a
         re-billing, a correction and a case with no payment series are all
         ordinary, and the phrase has to read as a deliberate answer rather than
         as a field somebody failed to fill. */
      offSchedule: 'Off schedule',
      /* The option's own label while the tranche list is still in flight, or
         after that read failed — the invoice carries the link either way, and a
         select that fell back to "Off schedule" would state the opposite of
         what the record says. */
      trancheLinked: 'Scheduled payment',
      trancheOption: 'Tranche {{n}} · {{amount}}',
      numberOnIssue: 'the number is assigned when it is issued',
      issuedOn: 'Issued {{date}}',
      closedTranche: 'closed tranche {{n}}',

      whatWeBill: 'What is being billed',
      tranche: 'Scheduled payment',
      /* Said before the press, not after it. Issuing against a tranche removes
         that tranche from the allocation registry — the invoice takes its place
         — and a reader who does not know that reads the disappearance as data
         going missing. */
      trancheClosesNote:
        'Issuing closes tranche {{n}}: it stops being counted as an expected amount of its own, so the receivable is not claimed twice.',
      offScheduleNote:
        'No tranche is closed: the payment schedule stays as it is and this invoice becomes a receivable in its own right. The approved rate still caps it.',
      /* AD-9. The currency is one of the five facts frozen together at
         creation, so it is shown and not offered. */
      currencyFrozen: 'Frozen with the rate. A different currency is a new invoice.',

      lines: 'Invoice lines',
      linesFromQuote:
        'Copied from the accepted quote {{quote}}. Corrected here, one charge at a time.',
      linesOwn:
        'This case was opened without a quote, so there was nothing to copy — the charges are typed here.',
      /* The same two provenance lines for an ISSUED invoice, which cannot be
         edited — the draft wording above ends in an editing instruction (N-26). */
      linesFromQuoteIssued: 'Copied from the accepted quote {{quote}}.',
      linesOwnIssued: 'This case was opened without a quote; the charges were typed by hand.',
      fromQuote: 'from the quote',

      /* The tranche a demand bills and the charges on it are two figures that
         are meant to agree, and nothing on the server makes them. The banner
         states the gap rather than closing it: reconciling the two is a
         judgement — split the charges, move the invoice off the schedule, or
         re-cut the tranche — and none of those is a button this screen can press
         on somebody's behalf. */
      mismatchTitle: 'The charges do not add up to tranche {{n}}',
      mismatchBody:
        'The charges come to {{lines}} and tranche {{n}} expects {{tranche}}. Issued as it stands, this invoice closes that instalment with a document for a different amount, and the rest of the schedule is left uncovered. Correct the charges, or move this invoice off the schedule.',

      ceiling: 'Approved rate on this case',
      ceilingApproved: 'Agreed with the client',
      ceilingEarlier: 'Billed earlier',
      ceilingThis: 'On this invoice',
      ceilingRemaining: 'Left for the next invoice',
      /* The case has no agreed rate, which means there is NO ceiling — not a
         ceiling of zero. An invoice can still be raised; it simply has nothing
         to be checked against. */
      noCeilingTitle: 'No approved rate',
      noCeilingBody:
        'This case was opened directly, without a quote, so there are no charges to copy and no ceiling on the invoice. It can still be raised — it just has nothing to be checked against.',

      booking: 'How this converts to USD',
      bookingCurrency: 'Document currency',
      bookingRate: 'Rate for {{date}}',
      bookingNote:
        'The rate is fixed when the invoice is created and is never recalculated. A different currency is a new invoice.',

      prints: 'What will print',

      next: 'What happens next',
      nextOne: 'A draft can be corrected as often as you like.',
      nextTwo:
        'The PDF is built and filed in the case archive. Any edit to the draft cancels it.',
      nextThree:
        'Issuing assigns the number, closes the tranche and creates the receivable. It cannot be undone.',

      faceValue: 'Invoice total',
      received: 'Received',
      outstanding: 'Still owed',
    },

    /* Client invoices raised against a case (Story 5.4, FR-61).
     *
     * "Invoice" here is the paper sent to the client, not the archive row
     * behind it — the two are separate records and the vocabulary has to stay
     * separate with them, or "issue" and "render" become the same word. */
    invoices: {
      title: 'Client invoices',
      new: 'New invoice',
      newTitle: 'New invoice on {{number}}',
      raise: 'Raise draft',
      /* The title while the invoice is still loading, or after it failed. The
         one above interpolates the case number, and rendering it from a record
         that has not arrived produces the dangling "Draft invoice on ". */

      caseLabel: 'Case',
      /* Named as a business rule rather than left as a greyed-out button: an
         invoice belongs to a case, and this says which step comes first. */
      chooseCaseFirst: 'Choose a case first — an invoice is always raised against one.',
      emptyTitle: 'No invoices on this case',
      emptyDescription:
        'Raise one to bill the client. A case may carry several — one per payment tranche.',
      /* A draft has no number. Story 5.5 mints it from the entity's scheme when
         the invoice is issued, and anything printed here in the meantime would
         belong to no sequence (AD-12). */
      unnumbered: 'No number yet',
      /* Per ROW, not per case. Every invoice on a case-scoped list carries the
         same case number, so interpolating that gave a screen reader half a
         dozen controls with one identical name. The invoice's own number
         identifies it once it has one; until then its date does. */
      open: 'Open the invoice {{invoice}} dated {{date}}',

      invoiceDate: 'Invoice date',
      economicDate: 'Economic date',
      economicDateHint: 'The date the FX rate is taken for.',
      /* Said rather than hidden: the booking is five facts frozen together
         (AD-9), and a field that quietly vanished on save would look like a
         bug instead of a rule. */
      economicDateFrozen: 'Frozen with the rate. Raise a new invoice to change it.',
      customerReference: 'Your ref.',
      customerReferenceHint: "The client's own reference, as it prints on the invoice.",

      currency: 'Document currency',
      currencyHint:
        'Chosen once. It books the rate and the USD equivalent with it, and neither moves again.',

      vat: 'VAT',
      vatApplies: 'VAT applies',
      /* FR-10: the block is printed or omitted, never printed as zero — a zero
         VAT line is a statement about a zero-rated supply. */
      vatHint: 'The tax block prints only when this is ticked.',
      vatRate: 'VAT rate, %',
      vatRateHint: 'A percentage — 20 means 20%.',
      vatAmount: 'Tax on this invoice: {{amount}}',

      paymentTerms: 'Payment terms',
      bankDetails: 'Bank details',
      /* FR-64/AC-5: never typed on the invoice. Saying where they come from is
         what stops somebody looking for the field to edit. The terms are the
         case's, in the offer's words (#126, N-05); only the bank details are
         the entity's. */
      termsNote:
        'Payment terms come from the case, as agreed with the client, and are changed on the case. Only the bank details come from the issuing entity — change those in Settings. Neither is edited here.',
      saveHeader: 'Save header',

      lines: {
        label: 'Charges',
        empty:
          'No charges yet. A case converted from a quote arrives with the lines the client accepted; a case raised directly starts blank.',
        description: 'What is being charged',
        quantity: 'Qty',
        unitPrice: 'Unit price',
        amount: 'Amount',
        amountHint: 'What is billed, in {{currency}}.',
        /* Zero is refused with the negatives, as the server refuses it: a
           charge at 0.00 reads on a client's invoice as a fee waived rather
           than as a figure nobody typed. */
        amountPositive: 'Enter an amount greater than zero',
        /* Shown only when the amount does NOT equal the product of the columns
           beside it, which means somebody typed over the calculation. That is
           legitimate — a lump sum, a round figure, something agreed on the
           phone — but worth saying out loud, because a half-finished edit looks
           exactly the same. In the ordinary case the product is already in the
           field itself, and a hint that restates its own field teaches people
           to stop reading hints. */
        amountPreview: 'Does not match qty × unit price = {{amount}}',
        add: 'Add charge',
        saveCharge: 'Save charge',
        editN: 'Edit charge {{n}}',
        remove: 'Remove charge {{n}}',
        /* AD-10, refused at the point of ADDING the line rather than at issue.
           The figures are composed here from the invoice already on screen —
           the server deliberately sends none inside the refusal. */
        exceedsApprovedRate:
          'This charge takes the billed total {{excess}} past the approved rate of {{approved}}. {{billed}} is billed on this case already. An approved rate change is needed before it can be added.',
        exceedsShort: 'Past the approved rate',
        exceedsUnknownRate:
          'This charge would bill the client past the approved rate on this case. An approved rate change is needed before it can be added.',
      },
    },
  },

  clients: {
    title: 'Clients',
    subtitle: 'Who owes what, and every case behind the figure.',
    // A deep link from a role with no access to clients (#129).
    noAccess: {
      title: 'This section is not available',
      description:
        'Clients open with your role’s access to the Clients area. Ask an administrator if you need it.',
    },
    new: 'New client',
    year: { label: 'Statement year', all: 'All years' },
    tiles: {
      billed: 'Billed',
      received: 'Received',
      balance: 'Balance',
      balanceToday: 'Balance today',
      opening: 'Opening balance',
      cost: 'Cost',
    },
    filters: { withBalance: 'With balance only' },
    columns: {
      client: 'Client',
      /* Two different sets of people, labelled so they cannot be mistaken for
         each other: the client's own salesperson, and whoever ran its cases
         that year. */
      salesContact: 'Sales contact',
      managers: 'Case managers',
      cases: 'Cases',
      weight: 'Weight, kg',
      billed: 'Billed',
      received: 'Received',
      /* S-22f: money paid back to the client. */
      refunded: 'Refund',
      balance: 'Balance',
      cost: 'Cost',
      status: 'Status',
    },
    table: { label: 'Clients', emptyTitle: 'No clients yet', emptyDescription: 'Add the first client, and every quote starts by picking one.' },
    actions: { edit: 'Edit', merge: 'Merge', open: 'Open client' },
    /* Who stands behind the client: the salesperson responsible for it now,
       and the colleague who registered it, which never changes. */
    people: {
      salesContact: 'Sales contact',
      registeredBy: 'Registered by',
    },
    /* The receipt after a registration, naming the number the server minted. */
    registered: 'Client registered as {{number}}.',
    owner: {
      action: 'Change sales contact',
      title: 'Change sales contact',
      colleague: 'New sales contact',
      confirm: 'Change contact',
      hint: 'Only the client moves. Its existing quotes and cases stay with whoever is running them.',
      notEligible: 'This colleague cannot hold clients. Choose someone who works with clients.',
    },
    moneyUnavailable: {
      title: 'Figures temporarily unavailable',
      body: 'The directory is complete; the money could not be read right now. Try again in a minute.',
    },
    statement: {
      title: 'Statement',
      eyebrow: 'Client',
      tableLabel: 'Statement of {{name}}',
      openingRow: 'Opening balance',
      totalRow: 'Total',
      emptyTitle: 'No shipments this year',
      emptyDescription: 'Pick another year, or all years.',
      details: 'Details and contacts',
      columns: {
        no: 'No',
        openedOn: 'Receipt date',
        number: 'Case',
        route: 'Route',
        mode: 'Transport',
        cargo: 'Goods',
        weight: 'Weight, kg',
        volume: 'Volume, m³',
        billed: 'Billed',
        received: 'Received',
        refunded: 'Refund',
        running: 'Balance',
        paymentMethod: 'Payment',
        lastReceivedOn: 'Collected on',
        ata: 'Arrived',
        transitDays: 'Days',
        owner: 'Manager',
        cost: 'Cost',
        status: 'Status',
      },
    },
  },

  /* The «Контрагенты» section (S-22b): the list by category and one
     counterparty's page. The money (statement, tiles, average rate) comes
     later. */
  counterparties: {
    title: 'Counterparties',
    subtitle: 'Who provides us a service — carriers, forwarders, brokers, drivers and agents.',
    new: 'New counterparty',
    edit: 'Edit counterparty',
    chipsLabel: 'Counterparty categories',
    chips: {
      all: 'All',
      carrier: 'Carriers',
      forwarder: 'Forwarders',
      'customs-broker': 'Brokers',
      driver: 'Drivers',
      agent: 'Agents',
      other: 'Other',
    },
    search: 'Search by name',
    inactive: 'Inactive',
    table: { label: 'Counterparties' },
    columns: {
      counterparty: 'Counterparty',
      category: 'Category',
      country: 'Country',
    },
    empty: {
      title: 'No counterparties yet',
      description:
        'Add a carrier, forwarder or broker you work with — it then appears in every form that picks a counterparty.',
    },
    /* A reader without the counterparty read: only an old or typed link leads
       here — the sidebar offers them no item. */
    forbidden: {
      title: 'Counterparties are not available to your role',
      description:
        'The counterparty directory opens with the right to see counterparties. Ask an administrator if you need it.',
    },
    record: {
      eyebrow: 'Counterparty',
      facts: {
        label: 'Counterparty at a glance',
        country: 'Country',
        taxId: 'BIN',
        contact: 'Contact',
        averageRate: 'Average rate',
      },
      details: 'Details and contacts',
      noContacts: 'No contacts',
    },
    /* A counterparty's money (S-22e): the page's tiles and the list's columns.
       The server computes every figure; the advance — an unallocated payment —
       is shown beside them and is in neither «Paid» nor «Balance». */
    money: {
      tiles: {
        units: 'Shipments',
        billed: 'Billed',
        paid: 'Paid',
        balance: 'Balance to pay',
      },
      columns: {
        units: 'Shipments',
        billed: 'Billed',
        paid: 'Paid',
        balance: 'Balance to pay',
      },
      advance: 'Advance, not allocated:',
      /* A reader whose money is limited to their own works. */
      scoped: 'Figures for your works',
      unavailable: {
        title: 'Figures are temporarily unavailable',
        body: 'The works are shown, but the money could not be read just now. Try again in a minute.',
        listBody: 'The list is complete, but the money could not be read just now. Try again in a minute.',
      },
      forbidden: {
        title: 'No access to the money of works',
        description:
          'The statement opens with the right to see the money of works. Ask an administrator if you need it.',
      },
    },
    statement: {
      title: 'Statement',
      year: 'Year',
      tableLabel: 'Counterparty statement',
      openingRow: 'Balance brought forward',
      totalRow: 'Total',
      columns: {
        number: 'Work',
        openedOn: 'Date',
        route: 'Route',
        units: 'Shipments',
        billed: 'Billed',
        paid: 'Paid',
        balance: 'Balance',
        running: 'Running',
      },
      toggle: 'What work {{number}} holds',
      items: {
        leg: 'Leg {{position}}',
        legWhole: 'Leg {{position}} as a whole',
        other: 'Other costs',
        advance: 'Advance, not allocated',
        noNumber: 'no number',
      },
      settlement: {
        paid: 'paid',
        'partly-paid': 'partly paid',
        unpaid: 'not paid',
      },
      empty: {
        title: 'No works with this counterparty yet',
        description: 'Shipments, costs and payments will appear here.',
      },
      emptyYear: {
        title: 'No works this year',
        description: 'Pick another year or every year.',
      },
    },
    form: {
      category: 'Category',
      taxId: 'BIN / Tax ID',
      registrationId: 'Registration no.',
      address: 'Address',
    },
    actions: {
      edit: 'Edit',
      more: 'Counterparty actions',
      deactivate: 'Deactivate',
      activate: 'Activate',
      merge: 'Merge',
      delete: 'Delete',
    },
    /* Server refusals in words (FR-39): the numbers are interpolated, never
       concatenated. */
    refusals: {
      deleteTitle: 'Cannot delete',
      mergeTitle: 'Cannot merge',
      /* On a debit note: notes are frozen history and are never moved.
         Deactivation is offered, never a merge. */
      mergeDebitNotes:
        'This counterparty is on debit notes {{list}}, and those are never moved to another record. Deactivate it — it stops being offered for new records and stays where it is already named.',
      categoryMismatched: 'Only counterparties of one category can be merged.',
    },
  },

  directories: {
    title: 'Directories',
    subtitle: 'The records every quote and case joins to, kept in one place.',

    /* A refused delete (FR-39, Story 7.1 AC 4, Story 7.4 AC 5).
     *
     * The record numbers are interpolated rather than concatenated at the call
     * site, because the list sits in a different place in the Russian clause and
     * a sentence assembled from fragments cannot move it (UX-DR-30). */
    referenced: {
      /* `more`, never `count`: i18next reads `count` as the pluralisation
         trigger and would then look for andMore_one / andMore_other here and
         four Russian forms next door, none of which this phrasing needs — "and
         3 more" agrees with nothing. A plural key nobody wrote is a key that
         falls back to the raw token in front of a user. */
      andMore: '{{named}} and {{more}} more',
      mergeInstead:
        'This record is used by {{list}}, so deleting it would orphan them. Merge it into another record instead.',
      deactivateInstead:
        'This entry is used by {{list}}, so it cannot be deleted. Deactivate it instead — it will stop being offered on new records and stay on the ones that already use it.',
      // The merge that cannot run because this deployment has no way to move a
      // reference. A different sentence from the two above on purpose: it must
      // not send the reader back to the action that just refused them.
      mergeUnavailable:
        'This record is used by {{list}} and this installation cannot move those references yet, so merging would orphan them. Deactivate it instead.',
    },

    /* Merging one record into another — the action FR-39 offers when a delete is
     * refused. */
    merge: {
      action: 'Merge',
      title: 'Merge into another record',
      description:
        'Everything that names this record will name the one you choose, and this record is then removed. It cannot be undone.',
      target: 'Merge into',
      targetPlaceholder: 'Choose the record to keep',
      confirm: 'Merge and remove',
      sameRecord: 'Choose a different record to merge into.',
    },

    activate: 'Activate',
    deactivate: 'Deactivate',
    // Why the control is refusing, shown on hover rather than by disabling it:
    // a disabled button makes its own title unhoverable (UX-DR-22).
    needsGrant: 'You need the directory grant for this list.',

    /* The contacts block, shared by clients and counterparties (FR-39, FR-41). */
    contacts: {
      legend: 'Contacts',
      label: 'Contact person',
      labelPlaceholder: 'e.g. Ivan Petrov or Dispatch desk',
      add: 'Add a contact',
      remove: 'Remove this contact',
      primary: 'Primary contact',
      handlePlaceholder: 'handle or profile link',
      /* Said plainly, because a field that looks like a delivery channel will
         be treated as one. */
      messengerNote:
        'Contact details only. The platform does not send messages to these — messaging stays outside it.',
      restricted: 'Contact details are not available to your role.',
      /* The server did not recognise a contact's id: the list on screen is
         older than the stored one. */
      stale: 'Someone else changed a contact — reopen the form',
    },

    clients: {
      title: 'Clients',
      new: 'New client',
      delete: 'Delete',
      edit: 'Edit client',
      name: 'Name',
      country: 'Country',
      taxId: 'Tax ID',
      registrationId: 'Registration ID',
      address: 'Address',
      code: 'Client code',
      /* It expands into a document number (FR-39, FR-68), so somebody
         choosing one should know where it shows up. */
      codeHint: 'Optional. Can be used in document numbers, so keep it short.',
      codeInvalid: 'Letters, digits and hyphens only',
      /* The registry number is minted by the server and never typed, so the
         form states it rather than offering a field. */
      number: 'Client number',
      numberAssigned: 'Assigned automatically when the client is saved, from the one company-wide client series.',
      numberTaken:
        'The next client number is already taken. Change the client numbering pattern in Settings → Numbering, then save again.',
      /* A client billed on debit notes is never merged: issued notes are
         frozen history and are never moved. Deactivation is offered, never a
         merge again. */
      mergeDebitNotes:
        'This client is billed on debit notes {{list}}, and those are never moved to another record. Deactivate the client — it stops being offered for new records and stays where it is already named.',
      contact: 'Contact',
      owner: 'Owner',
      open: 'Open',
      receivable: 'Receivable',
      /* The client picker on the quote forms (QW-12). It searches the
         registry number and the code as well as the name, because the number
         is how a client is named on paper. */
      search: 'Name or number…',
      /* The picker with nothing in it. A Sales user's directory is their own
         clients only, so an empty list on the first day is the scope, not a
         fault — and the copy names both ways out. The second variant is for a
         role that may not create a client. */
      pickerEmpty:
        'Only your clients are listed here. If the company already works with this client, ask a manager to reassign it to you; otherwise create a new one.',
      pickerEmptyNoCreate:
        'Only your clients are listed here. If the company already works with this client, ask a manager to reassign it to you.',
    },

    users: {
      title: 'Users',
      new: 'New user',
      edit: 'Edit user',
      email: 'Email',
      invalidEmail: 'Enter a valid email address',
      /* A person's name, not a company's (S-7, A-2). */
      name: 'Name',
      role: 'Role',
      office: 'Office',
      /* The list's column: the account's own permissions beyond its role (S-7). */
      extras: 'Additional',
      /* The user card (S-7, A-5): what the role gives, read-only, then what
         was granted to this person. */
      roleCapabilities: 'Role permissions',
      roleCapabilitiesHint:
        'Set on the role, for everyone who holds it, under Roles & permissions.',
      /* The same without the pointer, for a reader who is not offered the
         Roles & permissions tab (admin.users.manage only). */
      roleCapabilitiesHintNoEditor: 'Set on the role, for everyone who holds it.',
      roleCapabilitiesNone: 'This role grants no permissions.',
      roleCapabilitiesLoading: "Loading the role's permissions",
      roleCapabilitiesFailed: "Couldn't load the role's permissions.",
      extraGrants: 'In addition, for this person',
      extraGrantsHint: 'On top of the role: the person keeps it even if the role loses it.',
      grantedByRole: 'Given by the role',
      alsoGrantedByRole: 'Also given by the role',
      /* The authority rule (S-7, O7): you grant only what you hold, and act
         only on an account that holds no more than you. */
      roleNotAssignable: 'This role grants more than you hold',
      /* A new account, and no role on the list is yours to give. */
      noAssignableRole: 'There is no role you can assign',
      grantNotYours: 'You cannot grant this permission',
      outranked: 'This person holds more permissions than you',
      outrankedRefusal:
        'This person holds more permissions than you: only the super-admin can change their account.',
      /* The server's refusals by their `detail` token (S-13, the Conflict
         contract in http-api.md). The token itself is never shown. */
      refusals: {
        emailTaken: 'An account with this email already exists',
        selfDemotion: 'You cannot change your own role',
        selfDeactivation: 'You cannot deactivate your own account',
        lastSuperAdmin: 'At least one active super-admin must remain',
      },
      /* Grants are additive booleans on the user, never new roles (AD-7). */
      grantLabels: {
        manageClients: 'Manage clients',
        manageParticipants: 'Manage counterparties',
        manageTransport: 'Manage transport references',
      },
      status: 'Status',
      inactive: 'Inactive',
      language: 'Interface language',
      /* An address identifies an account; changing one would rewrite who every
         historical row was attributed to (Story 1.3). */
      emailFixed: 'The email address cannot be changed after the account is created.',
      awaitingPasswordChange: 'Password not yet changed',
      resetPassword: 'Reset password',
      deactivate: 'Deactivate',
      activate: 'Reactivate',
      /* The credential hand-off. The password is shown here exactly once —
         the server stores only its hash. The mail to its owner does not always
         go: with no SMTP, or outside production, it reaches nobody, and the
         server says so in `credentialMailed`. Hence two branches, and neither
         says "sent": true means QUEUED, not delivered (QW-5). The first-login
         change is now enforced, so both branches promise it. */
      credentialTitle: 'Password for {{name}}',
      credentialIntroMailed:
        'The account is ready to use. The password is being emailed to {{email}} with a link to sign in, and is shown here in case you need to hand it over yourself.',
      credentialNotMailed: 'No email went to the owner — hand the password over yourself',
      credentialIntroNotMailed:
        'The account for {{email}} is ready to use. The owner was not emailed this password.',
      credentialCopy: 'Copy',
      credentialCopied: 'Copied',
      credentialOnce: 'This password is shown once here and cannot be recovered.',
      credentialFirstLogin: 'The system asks the owner for a new password at first sign-in.',
      credentialMailMissing:
        'If the email does not arrive, reset the password to issue and send a new one.',
      credentialDone: 'I have saved it',
    },

    transport: {
      title: 'Transport',
      modes: 'Modes',
      equipmentTypes: 'Equipment types',
      add: 'Add',
      mode: 'mode',
      equipment: 'equipment type',
      addItem: 'New {{item}}',
      editItem: 'Edit {{item}}',
      code: 'Code',
      codeHint: 'Short machine-facing code, e.g. FCL or 40HC.',
      delete: 'Delete',
    },

    /** The row action that opens the edit sheet, named for screen readers. */
    editRecord: 'Edit {{name}}',
  },

  settings: {
    title: 'Settings',
    /* The page subtitle names only the tabs the reader holds (N-31): each tab
       contributes one lowercase noun phrase, joined by `settingsSubtitle`. */
    subtitleParts: {
      entities: 'issuing entities',
      numbering: 'numbering',
      templates: 'offer templates',
      directions: 'route templates',
      users: 'people',
      access: 'roles',
    },
    sectionsLabel: 'Settings sections',

    tabs: {
      entities: 'Issuing entities',
      numbering: 'Numbering',
      /* "Offer templates", not bare "Templates": "Route templates" sits next to
         it, and the bare word read as a second name for that catalogue (F1). */
      templates: 'Offer templates',
      directions: 'Route templates',
      users: 'Users',
      access: 'Roles & permissions',
    },

    /* No tab is open to this reader — usually an old link: such a reader has
       no Settings item in the sidebar. */
    noTabs: {
      title: 'No sections available',
      description:
        'Settings sections open with administration permissions: accounts, templates, roles. Ask an administrator if you need one.',
    },

    /* Roles & permissions — the editable model of migration 0021.
     *
     * Wording note: a "capability" is what somebody may DO and "access" is
     * whose records they may SEE. The two are named differently throughout
     * because they are different axes, and a founder who conflates them builds
     * a role that can issue invoices against no cases. */
    roles: {
      title: 'Roles',
      intro:
        'A role is a set of permissions you name. People hold one role, plus any extra permissions granted to them individually.',
      create: 'New role',
      loading: 'Loading roles',
      edit: 'Edit {{name}}',
      editAction: 'Edit {{name}}',
      deleteAction: 'Delete {{name}}',
      loadFailed: 'The roles could not be loaded. Try again.',
      /* The server's refusals by their `detail` token (S-13, the Conflict
         contract in http-api.md). A built-in role, the super-admin role and the
         last super-admin are reachable from this screen only from a stale tab:
         the buttons that would cause them are not offered. */
      refusals: {
        roleInUse: 'People still hold this role — reassign them first',
        systemRole: 'A built-in role cannot be deleted',
        systemRoleKeyFixed: 'A built-in role’s key cannot be changed',
        superuserRole: 'The super-admin role cannot be deleted, nor its permissions changed',
        lastSuperAdmin: 'At least one active super-admin must remain',
      },
      superuserLocked:
        'This role always has every permission and sees every record, so there is nothing to tick or choose. That is what makes it the role that can restore the others.',
      /* Managing roles is the super-admin's alone for now (S-7, O8). */
      superuserOnly: 'Super-admin only',
      /* #133: a role that can change legs but cannot see counterparties is
         served every saved leg with its carriers hidden, and the leg page opens
         it read-only. Both rights are named by their labels. */
      warnings: {
        legsNeedCounterparties: {
          title: 'Saved legs will open read-only',
          body: 'The role holds “{{edit}}” but not “{{read}}”. Without it the role does not see carriers and drivers, so a saved leg opens read-only for it. Tick the second one so the role can work with legs and trucks.',
        },
      },
      badges: {
        superuser: 'Full access',
        system: 'Built in',
      },
      summary: {
        allPermissions: 'Every permission',
        counts: '{{capabilities}} permissions · {{surfaces}} areas',
        users_one: '{{count}} person',
        users_other: '{{count}} people',
      },
      fields: {
        key: 'Identifier',
        keyHint: 'Lowercase letters, digits and hyphens. Cannot be changed later.',
        name: 'Name',
        /* Under the name of a BUILT-IN role, which the interface names by its
           identifier in each reader's language — so a rename shows only here. */
        nameSeededHint:
          'A built-in role. The interface calls it “{{label}}” in each reader’s language, whatever is typed here.',
        description: 'Description',
        descriptionHint: 'What this role is for, in your own words.',
      },
      scopes: {
        title: 'What they can see',
        hint: 'Whose records this role reaches. Set an area to no access and it disappears from their navigation entirely.',
      },
      capabilities: {
        title: 'What they can do',
        hint: 'Every permission is off until you turn it on.',
      },
      levels: {
        none: 'No access',
        own: 'Only their own',
        assigned: 'Their own and ones they are on',
        all: 'Everything',
      },
      surfaces: {
        quotes: 'Quotes',
        cases: 'Cases',
        'case-money': 'Case costs and profit',
        'cross-case-finance': 'Company finance',
        clients: 'Clients',
      },
      groups: {
        quote: 'Quotes',
        case: 'Cases',
        directory: 'Directories',
        counterparty: 'Counterparties',
        finance: 'Finance',
        document: 'Documents',
        collab: 'Files and chat',
        route: 'Route templates',
        admin: 'Administration',
      },
      /* One label per capability the server registers, keyed by the capability
         in camelCase (`features/settings/capabilityLabels.ts` holds the map and
         its test reads the Go registry). Written for the founder configuring a
         role, as the server's own descriptions are; the server's text is shown
         only for a key this map does not know yet. */
      capabilityLabels: {
        quoteCreate: 'Open a draft quote',
        quoteSend: 'Send a quote to the client',
        quoteTeamAssign: 'Put a colleague on a quote',
        quotePricingProvide: 'Price a quote when asked, and mark the price ready',
        caseCreate: 'Open a case, and cancel one',
        caseCloseOperational: 'Close a case operationally (the shipment is delivered)',
        caseCloseFinancial: 'Close a case financially (every balance is settled)',
        caseRouteEdit: 'Add, amend or remove a case’s legs',
        caseTeamAssign: 'Put a colleague on a case',
        caseReopen: 'Reopen a financially closed case (audited)',
        caseOperationsLead:
          'Be named the Operations owner when a quote becomes a case, and see cases with no legs, legs with no truck and cases with no actual costs in My tasks',
        directoryClientCreate: 'Add a client, becoming its owner',
        directoryClientManage: 'Merge, delete and edit any client, not only your own',
        directoryTransportManage: 'Add, rename or deactivate transport modes and equipment',
        directoryContactRead: 'See participant contact details (phone, email, address)',
        counterpartyRead: 'See the counterparties: carriers, forwarders, brokers, drivers and agents',
        counterpartyCreate: 'Add a counterparty, and edit or deactivate any counterparty',
        counterpartyManage: 'Merge and delete counterparties',
        financeExpenseRecord: 'Enter and amend planned and actual costs on a case',
        financePaymentRecord: 'Record a payment and allocate it to documents',
        financePaymentClient:
          'Record a client’s payment on a case you reach and allocate it to the case’s invoices, debit notes and tranches',
        financeReceivablesRead: 'See aging, balances and the receivables register across cases',
        financePaymentCarrier:
          'Pay a carrier for a leg’s cost: recognise the debt, record and allocate in one act',
        financePaymentConfirm: 'Confirm a recorded payment, or delete a pending one',
        financeRateApprove: 'Approve a change to a case’s agreed rate',
        financeRateSet: 'Register a case’s agreed rate',
        documentOfferRender: 'Draft an offer PDF',
        documentOfferIssue: 'Issue an offer as the document of record',
        documentInvoiceRender: 'Draft and open invoices and their PDFs',
        documentInvoiceIssue: 'Issue an invoice, creating the receivable',
        documentDebitnoteRender: 'Draft and open debit notes and their PDFs',
        documentDebitnoteIssue: 'Issue a debit note, creating the receivable',
        documentArchiveRead:
          'List the documents produced from a record; open offers and status updates',
        collabFilesRead: 'Open a case’s or quote’s files and chat',
        collabFilesUpload: 'Add a file to a case',
        collabChatPost: 'Write in a case’s or quote’s chat',
        collabChatDelete: 'Remove another person’s chat message',
        collabChatReadAll: 'Read every case’s and quote’s chat, not only your own team’s',
        /* S-10: without the rest of Settings; deleting a template stays on the
           settings capability. */
        routeTemplateManage: 'Create, amend and hide route templates',
        adminUsersRead: 'See the user list',
        adminUsersManage: 'Create accounts, amend them, and reset passwords',
        adminRolesRead: 'See the roles and what each may do',
        adminRolesManage: 'Create and edit roles, and change what each role may do',
        /* What the capability really opens (S-7, O6): issuing entities and
           numbering sit behind the accounts capability. */
        adminSettingsManage: 'Change offer templates, reminder thresholds and route templates',
        adminAuditRead: 'Read the audit trail',
      },
    },
    access: {
      title: 'Access matrix',
      /* N-09: the table is built from the stored roles — a column for each,
         ones a founder created included. Its cells are worded as the role
         editor words them (`settings.roles.levels`, `capabilityLabels`), so
         there are no levels of its own here any more. */
      subtitle:
        'What each role is set up to see and do — one column per role, including the ones you created. On top of a role, a person may hold personal grants on their user record.',
      tableCaption: 'What each role sees and may do',
      dataGroup: 'Area or permission',
      /* For a screen reader: a permission cell shows only a tick or a dash. */
      held: 'Granted',
      notHeld: 'Not granted',

      enforcementNote:
        'Enforced on the server for every request. What the interface hides is a convenience, never the boundary itself.',

      grantsTitle: 'Ownership grants',
      grantsSubtitle:
        'Additive rights on top of a role, not roles of their own. A grant widens a directory; it never widens access to cases or money.',
      grants: {
        manageClients:
          'Manage the whole client list, including records owned by other people — merging and deleting included.',
        manageParticipants:
          'Delete and merge counterparties. Takes effect only together with seeing counterparties.',
        manageTransport: 'Maintain transport modes and equipment types.',
      },
      grantsNote:
        'Granted per person in Settings → Users. Founders hold all three.',

      boundariesTitle: 'Boundaries worth stating',
      boundaries: {
        /* The capability is named as the role editor names it, not by its key. */
        agentIdentityScope: {
          title: 'Agent hiding covers structured fields only',
          body:
            'Leg carriers, the carrier and driver of every unit (truck, container, wagon…), vendors on cost lines and a debit note’s party are shown only to a role holding “See the counterparties”; without it the field reads “Carrier hidden”, “Vendor hidden” or “Agent hidden” rather than sitting empty. Uploaded files and case chat are not redacted.',
        },
      },
      externalTag: 'Not users',
      externalNote:
        'Clients, shippers, consignees, agents, vehicle owners, dispatchers and drivers never log in. They receive email only.',
    },

    entities: {
      title: 'Issuing entities',
      new: 'New entity',
      edit: 'Edit entity',
      name: 'Entity name',
      legalFooter: 'Legal footer',
      /* The two short codes a numbering pattern expands (FR-68). */
      deactivate: 'Deactivate',
      activate: 'Reactivate',
      inactive: 'Deactivated',
      /* An unset footer or bank block reaches a client's paper as a blank. */
      notSet: 'Not set',
      initials: 'Initials',
      officeCode: 'Office code',
      /* The entity card names its codes in words rather than as pattern
         tokens: {INITIALS} means something to whoever opened the numbering tab,
         not to whoever reads the card. The form's hint keeps the token — inside
         the sentence that explains it. */
      initialsTag: 'Initials: {{value}}',
      officeTag: 'Office: {{value}}',
      initialsHint: 'Stands in for {INITIALS} in a number',
      officeCodeHint: 'Stands in for {OFFICE} in a number',
      defaultOffice: 'Default for office',
      defaultOfficeHint:
        "Records created by this office's users are issued from this entity. At most one entity per office.",
      noDefaultOffice: 'No office defaults to this entity',
      legalFooterHint: 'Printed at the foot of every document this entity issues.',
      bankDetails: 'Bank details',
      emailSignature: 'Email signature',

      /* The uploaded mark (FR-64, migration 0041). Until this existed, every
         document printed the entity's NAME where its logo belongs. */
      logo: {
        upload: 'Upload logo',
        replace: 'Replace logo',
        remove: 'Remove',
        sending: 'Sending the logo…',
        /* Says what a document will do, not what the form accepts: the reader's
           question is what a client receives. */
        hint: 'PNG or JPEG, up to 2 MB. Printed on every offer, invoice and debit note this entity issues. Without one, documents carry the entity name instead.',
        errorType: 'That file type cannot be printed on a document. Use PNG or JPEG.',
        errorSize: 'That logo is larger than 2 MB. Export it smaller and try again.',
        errorUpload: 'The logo could not be sent to storage. Check the connection and try again.',
        errorGeneric: 'The logo could not be saved.',
      },
    },

    numbering: {
      title: 'Numbering schemes',
      new: 'New scheme',
      edit: 'Edit scheme',
      entity: 'Entity',
      document: 'Document',
      pattern: 'Pattern',
      kinds: {
        quote: 'Quote',
        case: 'Case',
        invoice: 'Invoice',
        'debit-note': 'Debit note',
        client: 'Client',
      },
      /* The series that belong to the whole company rather than to one issuing
         entity — today the client registry alone. */
      company: {
        title: 'Company-wide',
        hint: 'Client numbers are one series for the whole company, whichever office registers the client. The series never resets.',
        changeNote:
          'Changing the pattern affects clients registered from now on. Numbers already issued stay as they are, and the count continues rather than restarting.',
      },
      /* Shown when no pattern is stored and the onboarding default applies. */
      usingDefault: 'Default',
      /* The server refuses a pattern with a stable token; these are its
         translations (AD-27). */
      errors: {
        missingSeq: 'A pattern needs {seq}, or every record gets the same number.',
        unknownToken:
          'Unknown token. Use {INITIALS}, {OFFICE}, {YY}, {YYYY}, {MM} or {seq}.',
        seqTooWide: 'The sequence padding is too wide.',
        periodTokenNotAllowed:
          'The client series never resets, so it cannot use {YY}, {YYYY} or {MM}.',
        entityTokenNotAllowed:
          'The client series belongs to no entity, so it cannot use {INITIALS} or {OFFICE}.',
        invalid: 'This pattern cannot be used.',
      },
      liveExample: 'Live example',
      next: 'Next',
      changeNote:
        'Changing a pattern affects documents issued from now on. Numbers already issued keep the form they were minted with.',
    },

    templates: {
      /* Four groups, and only the first three ever print. The wording keeps that
         difference visible: the printed three are described as text on a
         document, the presets as a list the costing screen starts from. */
      included: 'Included',
      notIncluded: 'Not included',
      disclaimers: 'Disclaimers',
      costItems: 'Charge line presets',
      costItemsHint:
        'The charge lines a costing starts from. They are never printed on an offer and carry no amounts — the money is entered per quote.',
      /* The text every new quote starts with (N-19). It is built into the product
         rather than held in the list below, so the Included, Not included and
         Disclaimers cards used to look empty while every quote carried text. */
      standing: {
        title: 'Standing text',
        note: 'Built into the product and added to every new quote in its author’s language. Not editable here.',
        none: 'There is no standing text for this section.',
        loadFailed: 'The standing text could not be loaded.',
      },
      library: {
        title: 'Your templates',
        empty: 'No templates of your own yet',
      },
      new: 'New template',
      edit: 'Edit template',
      label: 'Label',
      body: 'Text',
      bodyHint: 'Appears verbatim on the offer. Write it as the client will read it.',
      bodyHintCostItem:
        'Optional. A remark for whoever prices the quote — it stays inside the company.',
      /* The reason a template can be deleted outright while every directory
         record is only deactivated: migration 0033 copies the body onto the
         quote, so removing one changes no document that ever quoted it. */
      removeHint: 'Removing a template does not change any offer already sent.',
    },

    /* Story 2.9 AC 4. Each hint names who reads the number, including the two
       that nobody reads yet — a settings field that silently changes nothing is
       how a founder learns the controls are decorative. */
    thresholds: {
      title: 'Reminder and attention thresholds',
      configured: 'These values were set for this company.',
      usingDefaults: 'No values saved yet, so the defaults below are in force.',
      followUp: 'Follow-up reminder',
      followUpHint: 'Days after an offer is sent with no answer',
      validityWarning: 'Validity expiry warning',
      validityWarningHint: 'Days before a quote’s validity date',
      stuckShipment: 'Stuck shipment',
      stuckShipmentHint: 'Days a case may go without movement',
      defaultIs: 'default {{days}}',
      range: 'Between {{min}} and {{max}} days',
      wholeDays: 'Enter a whole number of days.',
      outOfRange: 'Outside the range this setting accepts.',
      awaitingReminderJob: 'no reminder job reads this yet',
      /* Who reads the stuck-shipment threshold. A saved value is read by the
         dashboard and by the case list's marker (`stuckDaysFor` in
         readmodel.go); until one is saved the server uses its own default. The
         environment variable is not named — it is the operator's knob, not the
         reader's. */
      stuckReadBy: 'Used by the dashboard and by the “No movement” marker in the case list.',
      stuckUsingDefault: 'Not saved yet — the server default applies.',
    },

    /* The route template catalogue (issue #35 story 1; the key is still
       `directions`). A named route as an ordered list of points, each with a
       country and a normative transit time.
       Wording note: issue #35 kept the word "template" off this screen and
       called the entries "directions", like the leg they fill. The QW-2
       glossary (owner decision, 2026-10-08) reversed that: applying a template
       COPIES its points onto a leg and links nothing, so the two are different
       things and carry different names. The subtitle still says the copy out
       loud rather than leaving it to the word. */
    directions: {
      title: 'Route templates',
      subtitle:
        'Ready-made routes a case leg is filled from. Applying one copies its points onto the leg, so editing a template never moves a shipment already under way.',
      empty: 'No route templates yet.',
      new: 'New route template',
      edit: 'Edit route template',
      inactive: 'off',
      activate: 'Switch on',
      deactivate: 'Switch off',
      pointCount_one: '{{count}} point',
      pointCount_other: '{{count}} points',
      normDays_one: '{{count}} day normative',
      normDays_other: '{{count}} days normative',
      name: 'Name',
      nameHint: 'How the desk says it — “Khorgos → Ashgabat (via Sarahs)”.',
      points: 'Points',
      pointsHint: 'In travel order. The first point is where the cargo starts.',
      pointPlaceholder: 'Khorgos',
      pointName: 'Point {{n}}',
      pointCountry: 'Country of point {{n}}',
      pointDays: 'Days to point {{n}}',
      daysPlaceholder: 'days',
      firstPointStart: 'start',
      addPoint: 'Add point',
      removePoint: 'Remove point {{n}}',
      maxPoints: 'A route template holds at most {{max}} points.',
      daysNote:
        'The day count sets the transition from the previous point. When it elapses, the cargo’s position on the case moves forward on its own.',
      delete: 'Delete this route template',
      deleteConfirm:
        'Delete it? Legs already filled from it keep their own copy and are unaffected.',
      deleteConfirmAction: 'Delete',
      errors: {
        minPoints: 'A route template needs at least two points.',
        wholeDays: 'Enter a whole number of days.',
      },
    },
  },

  auth: {
    /* The sign-in surface (FR-57, epics Story 1.2). Email and password, no 2FA
     * and no SSO in v1, so this copy IS the whole authentication story a user
     * ever reads.
     *
     * The failure copy is deliberately GENERIC and singular. Story 1.2 requires
     * the response body and timing to be identical whether or not the email
     * exists, so the endpoint cannot be used to discover who has an account.
     * Naming the wrong half — "no account with that email" — would hand back
     * from the client exactly what the server took care not to say. There is
     * therefore no per-field "unknown email" string here, and there must not be. */
    signIn: {
      title: 'Sign in',
      subtitle: 'Quotes, cases and money for the ASA team.',
      email: 'Email',
      emailPlaceholder: 'you@asagateway.com',
      password: 'Password',
      submit: 'Sign in',
      pending: 'Signing in',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      /* States what happened, then what to do. Never apologises, and never
         says which of the two fields was wrong. */
      failed: 'That email and password do not match',
      failedDetail: 'Check both and try again.',
      /* Five failures in fifteen minutes locks the email for fifteen minutes.
         The wait is named, because an unexplained refusal reads as an outage
         and turns into a message to a founder. */
      throttled: 'Too many attempts',
      throttledDetail: 'Sign-in for this email is paused for 15 minutes.',
      /* An idle or absolute session expiry lands here rather than at a dead
         end, so the reason for the interruption is stated once. */
      expired: 'Your session ended',
      expiredDetail: 'Sign in again to pick up where you left off.',
      /* Passwords are reset by a Super-admin (FR-57) — there is no self-serve
         reset in v1, so this says who to ask rather than offering a link that
         would go nowhere. */
      forgotten: 'Forgotten your password? A Super-admin can reset it for you.',
    },
    validation: {
      emailRequired: 'Enter your email',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Enter your password',
    },
    signOut: 'Sign out',

    /* The password change (QW-5). A password an administrator issued must be
     * replaced at first sign-in: until it is, the server refuses everything
     * but the session read and the change itself. The server's rule is at
     * least 8 characters and different from the current one; the hint says
     * exactly that and nothing more. */
    changePassword: {
      title: 'Change your password',
      subtitleForced:
        'An administrator issued this password. Choose your own to continue.',
      subtitleVoluntary: 'Choose a new password for your account.',
      current: 'Current password',
      next: 'New password',
      repeat: 'Repeat the new password',
      policy: 'At least 8 characters, and different from the current one.',
      submit: 'Change password',
      pending: 'Saving…',
      errors: {
        currentRequired: 'Enter your current password',
        nextRequired: 'Enter a new password',
        repeatRequired: 'Repeat the new password',
        mismatch: 'The passwords do not match',
        incorrect: 'The current password is wrong',
        tooShort: 'The password is shorter than 8 characters',
        tooLong: 'The password is too long',
        unchanged: 'The new password is the same as the current one',
      },
      /* 409: an administrator reset the password while it was being changed. */
      changedElsewhere: {
        title: 'The password was changed elsewhere',
        detail:
          'An administrator issued a new one while you were changing it. Sign in with the password they gave you.',
      },
    },
  },

  profile: {
    title: 'Profile',
    changePassword: 'Change password',
    subtitle:
      'Your account, your role, and the language this interface speaks to you in.',
    loading: 'Loading profile',

    photo: {
      change: 'Change your photo',
      remove: 'Remove',
      /* Says the two things that get an upload refused, before it is refused. */
      hint: 'JPEG, PNG, WebP or GIF, up to 5 MB.',
      uploading: 'Sending your photo…',
      failed: 'The photo was not saved. Try again.',
      removeFailed: 'The photo was not removed. Try again.',
      rejected: {
        /* Names the reason rather than saying "that did not work" — otherwise
           people retry the same file and get the same silence. */
        type: 'That file is not a photo we can use. Choose a JPEG, PNG, WebP or GIF.',
        size: 'That photo is over 5 MB. Choose a smaller one.',
      },
    },

    language: {
      title: 'Language',
      description:
        'The interface language is yours alone. An offer is written in the language chosen on the offer, status emails go out in the client’s language, and invoices and debit notes are always in English.',
      error: 'The language was not changed. Try again.',
    },

    access: {
      title: 'Access',
      /* Read-only by design: the person reading this page is the one person who
       * may not change it, so it says who to ask. */
      description:
        'What your account may reach. Roles and directory ownership are set by a Super-admin on your user record in <0>Directories</0>.',
      noGrants:
        "You hold no directory ownership grants. Your role's own access is unaffected by this.",
      grants: {
        manageClients: 'Maintains the client directory',
        manageParticipants: 'Maintains the counterparty directory',
        manageTransport: 'Maintains transport reference data',
      },
    },
  },

  /* The interface glossary (QW-2, owner decision 2026-10-08), published at
     /glossary and linked from the sidebar footer beside the profile. A
     catalogue group rather than a table in code, so parity.ts keeps the two
     languages in step. Each term carries the word, the same word in the other
     interface language, and what it means; `routes/GlossaryPage.tsx` decides
     the order and the groups.

     The glossary is the INTERFACE's rule. Paper and mail sent to outside
     parties use the recipient's words, and server-composed text is out of its
     scope. */
  glossary: {
    title: 'Glossary',
    subtitle: 'One word for one thing: how the interface names records, shipments and money.',
    /* Column labels; on a phone the columns fold into a card and "In Russian"
       stays for screen readers only. */
    columns: {
      term: 'Term',
      other: 'In Russian',
      meaning: 'Meaning',
    },
    groups: {
      records: 'Records',
      movement: 'Moving the cargo',
      money: 'Money',
      people: 'People',
      dates: 'Dates',
    },
    terms: {
      quote: {
        term: 'Quote',
        other: 'Котировка',
        meaning:
          'The record of a price worked out for a client. Its list, page, team, status, chat and history carry this name.',
      },
      offer: {
        term: 'Offer',
        other: 'КП',
        meaning: 'The document the client receives: Send offer, the preview, what prints on the offer.',
      },
      /* The Settings tab "Offer templates" (F1, owner decision 2026-10-09). */
      offerTemplate: {
        term: 'Offer template',
        other: 'Шаблон КП',
        meaning:
          'The text blocks that print on the offer, and the ready cost items a quote’s costing starts from.',
      },
      /* The quote's pricing state (S-2a, owner decision 2026-10-10). */
      requestPricing: {
        term: 'Request price',
        other: 'Запросить расчёт',
        meaning:
          'Ask a colleague who may price quotes for the price. They are notified, and the quote waits for it.',
      },
      pricingRequested: {
        term: 'Awaiting price',
        other: 'Ждёт расчёта',
        meaning:
          'The price was asked for and is not ready yet. A ready quote returns here when its cargo, route or transport terms change.',
      },
      pricingReady: {
        term: 'Price ready',
        other: 'Расчёт готов',
        meaning:
          'The price is worked out and the offer can go. Marked by whoever priced it; whoever asked is notified while they work the quote (on its team or its owner).',
      },
      /* «Мои задачи» (S-2b.1, owner decision 2026-10-11; Finance, S-2b.2). */
      myTasks: {
        term: 'My tasks',
        other: 'Мои задачи',
        meaning:
          'The page of what is waiting on you: the quotes and cases you work and, for Finance, rate approvals, payments to confirm and the amount to pay. Each reader sees only their own sections — the ones their role can act on. For Finance the same sections also sit above the dashboard.',
      },
      awaitingClient: {
        term: "Awaiting the client's answer",
        other: 'Ждут ответа клиента',
        meaning:
          'Sent offers that are still valid and went out at least as many days ago as the "Follow-up reminder" setting says (3 by default), with no answer from the client recorded.',
      },
      case: {
        term: 'Case',
        other: 'Работа',
        meaning:
          'The record of a shipment we run once the client has accepted the offer. To the client it is a "shipment".',
      },
      /* S-13: the movement itself and the client's word; its record is the case. */
      shipment: {
        term: 'Shipment',
        other: 'Перевозка',
        meaning:
          'The movement of the cargo itself, and the client’s word for it: mail to the client and the offer say "shipment". Its record in the interface is the "Case".',
      },
      leg: {
        term: 'Leg',
        other: 'Плечо',
        meaning: 'One movement of the cargo within a case: from where, to where, and by which mode.',
      },
      route: {
        term: 'Route',
        other: 'Маршрут',
        meaning:
          'The cargo’s path. In lists and headers, from and to; on a leg, its chain of points ("Route points"). Not a quote’s delivery option, nor a route template.',
      },
      /* S-13 (owner decision 2026-10-14): a quote's priced alternative, once
         a "route option". */
      deliveryOption: {
        term: 'Delivery option',
        other: 'Вариант доставки',
        meaning:
          'One of the ways of moving the cargo priced for the client: the route, the mode of transport and the transit time. The client picks one, and the cost lines are priced for the one selected.',
      },
      routeTemplate: {
        term: 'Route template',
        other: 'Шаблон маршрута',
        meaning:
          'A ready-made chain of points kept in Settings. Applying one copies its points onto a leg, so editing the template later does not change the leg.',
      },
      truck: {
        term: 'Truck',
        other: 'Фура',
        meaning: 'One vehicle on a leg, with its own plate, carrier and rate.',
      },
      plate: {
        term: 'Plate',
        other: 'Госномер',
        meaning: 'The truck’s registration number.',
      },
      unit: {
        term: 'Unit',
        other: 'Единица плеча',
        meaning:
          'What a leg carries and what its carrier is paid for: a truck, a container, a wagon, an air waybill, a bill of lading or another unit. Each has its own number, carrier, rate and remainder.',
      },
      container: {
        term: 'Container',
        other: 'Контейнер',
        meaning: 'A unit with a container number, a seal and a type (40HC, say). Not the container number a truck carries.',
      },
      wagon: {
        term: 'Wagon',
        other: 'Вагон',
        meaning: 'A rail leg’s unit, by wagon number; the seal (ZPU) and the type are optional.',
      },
      awb: {
        term: 'Air waybill',
        other: 'Авианакладная',
        meaning: 'An air leg’s unit — the waybill, with its number and the cargo weight.',
      },
      bl: {
        term: 'Bill of lading',
        other: 'Коносамент',
        meaning: 'A sea leg’s unit — the bill of lading or the booking, by number.',
      },
      carrier: {
        term: 'Carrier',
        other: 'Перевозчик',
        meaning: 'The company that moves the cargo on a leg, and that we pay for it.',
      },
      rate: {
        term: 'Rate',
        other: 'Ставка',
        meaning: 'The amount agreed with the client for a case. It sits in the case’s header.',
      },
      carrierRate: {
        term: 'Carrier rate',
        other: 'Ставка перевозчика',
        meaning: 'The amount agreed with the carrier for a truck, a container or another unit of the leg.',
      },
      clientPrice: {
        term: 'Client price',
        other: 'Цена клиенту',
        meaning: 'The amount a quote works out for the client: the cost plus the markup.',
      },
      /* S-5 (owner decisions O1, O6): one markup per quote, and the pair of
         words the costing shows together. */
      markup: {
        term: 'Markup',
        other: 'Наценка',
        meaning:
          'The percentage a quote adds to the cost of every line: the client price is the cost times one plus the markup. One per quote, set with Apply in the costing; a line added or edited later takes it too.',
      },
      margin: {
        term: 'Margin',
        other: 'Маржа',
        meaning:
          'What the quote earns: the client price minus the cost. As a percentage it is taken of the client price, so a 15% markup is a 13.04% margin.',
      },
      expense: {
        term: 'Expense',
        other: 'Затрата',
        meaning:
          'Money a case spends: planned or actual. “Cost · forecast” in the case header is the actual cost where a line has one and the planned cost where it does not yet.',
      },
      /* One word for three figures (S-6, owner decision 2026-10-12): the
         qualifier names which figure, not a separate label. */
      profit: {
        term: 'Profit',
        other: 'Прибыль',
        meaning:
          'What the case earns. Plan: the rate less planned costs. Forecast: the rate plus extra invoices and debit notes, less the actual costs and the planned cost of lines with no actual yet. Final: the money received and allocated less the money paid; it replaces the forecast once the case is financially closed or cancelled.',
      },
      /* The dashboard tile (Story B7). The definition is
         docs/llm-wiki/components/money-and-fx.md#turnover. */
      turnover: {
        term: 'Turnover this month',
        other: 'Оборот за месяц',
        meaning:
          'What was billed to clients since the 1st: issued invoices and client debit notes, in USD, net of VAT. It does not match "To collect", which includes VAT.',
      },
      payment: {
        term: 'Payment',
        other: 'Платёж',
        meaning:
          'Money received from a client or paid out to a carrier or another party: a payment is incoming or outgoing.',
      },
      team: {
        term: 'Team',
        other: 'Команда',
        meaning:
          'The colleagues working a quote or a case. Being on the team is what gives access to the record.',
      },
      /* S-1 (2026-10-10): the required choice when a quote becomes a case. */
      operationsOwner: {
        term: 'Operations owner',
        other: 'Ответственный (Операции)',
        meaning:
          'The Operations colleague who runs a case. Chosen when a quote becomes a case, and put on its team.',
      },
      /* A retired term (S-22a): cases and quotes no longer carry a list of
         participants. Kept so the old word, in a chat or an old history row,
         still finds an explanation. */
      participants: {
        term: 'Participants',
        other: 'Участники',
        meaning:
          'No longer used: cases and quotes carry no list of participants. A company or person providing a service is a counterparty, named where it acts — on a leg, a truck, a cost line or a payment.',
      },
      counterAgent: {
        term: 'Counter-agent',
        other: 'Контрагент',
        meaning:
          'A company or person providing us a service: a carrier, forwarder, customs broker, driver, agent or other. All of them are in the Counterparties section. In finance, also the other side of a payment or a balance: a client or a counterparty.',
      },
      etd: {
        term: 'ETD',
        other: 'ETD',
        meaning: 'The planned departure date.',
      },
      eta: {
        term: 'ETA',
        other: 'ETA',
        meaning: 'The planned arrival date.',
      },
    },
    /* The sidebar footer's link. */
    navLabel: 'Glossary',
  },

  notFound: {
    title: 'Not found',
    /* Never say "the record does not exist" — a 404 is returned for both
       missing and not-owned, so existence is not leaked either way. */
    heading: 'This page is not available',
    description:
      'The link may be out of date, or the record may belong to another team.',
  },

  errors: {
    forbidden: {
      title: 'This section is not available to your role',
      detail: 'Ask a Super-admin if you need access.',
    },
    notFound: {
      title: 'This record is not available',
      detail: 'It may have been removed, or it belongs to another team.',
    },
    server: {
      title: 'The server could not answer',
      detail: 'The data was not changed. Try again in a moment.',
    },
    network: {
      title: 'Could not load this data',
      detail: 'Check the connection and try again.',
    },
    /* The interface itself failed, not a request. Unlike `server` above, this
       must NOT say "the data was not changed" — a crashed render knows nothing
       about what reached the server, and a reassurance we cannot back is worse
       than none. Say what to do instead. */
    crash: {
      title: 'Something went wrong',
      heading: 'This screen could not be displayed',
      detail:
        'Try again, or reload the page. If it keeps happening, send this to your administrator with what you were doing.',
      reload: 'Reload the page',
      details: 'Technical details',
    },
    /** Fallback detail for a problem document that carries no `detail`. */
    validation: {
      detail: 'Check the values and try again.',
    },
    /* The FR-64 refusal, in two shapes, for the three sheets that can be handed
       it: the offer, a case's invoice or debit note, and a client invoice. One
       form rather than three, because the sentence's subject is the ISSUING
       ENTITY and the screen that fixes it — never the paper that was refused.
       Naming the document would need three sentences to say the one thing the
       reader has to do, and the reader is already looking at the sheet that
       failed.
       The server names the fields its entity has not filled in, so the usual
       sentence is the one that lists them: "bank details" and "legal footer"
       are two different afternoons in Settings, and one sentence covering
       both used to name the footer over an entity whose footer was fine.
       The unnamed sentence is what remains when the refusal carried no field
       — an older server, or a field key this catalogue has no words for. It
       still points at Settings, because that is where the fix is either way,
       and it names nothing, because naming a field the server did not report
       is the bug the first sentence exists to fix. */
    branding: {
      incomplete: 'The issuing entity is missing its document details — no PDF can be produced without them. A super-admin can fill them in under Settings → Entities.',
      incompleteFields: 'The issuing entity is missing: {{fields}}. No PDF can be produced without them — a super-admin can fill them in under Settings → Entities.',
      /* The field names arrive as machine keys (`legal-footer`) and the words
         are ours (AD-27). Bare nouns rather than possessive phrases, because
         they are joined into a list. */
      field: {
        name: 'trading name',
        logo: 'logo',
        legalFooter: 'legal footer',
        bankDetails: 'bank details',
      },
    },
    /* The `rate-missing` refusal with its members (S-4.1 follow-ups): the
       server names the currency and the day it had no rate for. Today's rate
       arrives by itself within the hour; a past date's never will, and an
       administrator enters it. Two remedies, so two sentences. */
    rateMissing: {
      today:
        "Today's {{currency}} rate has not been loaded yet — rates load automatically within the hour. Try again later.",
      past: 'There is no {{currency}} rate for {{date}} — without it the amount cannot be converted to USD. Ask an administrator: a rate for a past date is entered by hand.',
    },
    /* One entry per suffix in the server's checked-in registry
       (platform/httpx/problem.go). The suffix is the contract; the words are
       ours, because a problem document's own title and detail are written for
       a developer reading a log (AD-22, AD-27). */
    problems: {
      validationFailed: {
        title: 'Some values need attention',
        detail: 'The highlighted fields could not be accepted.',
      },
      authRequired: {
        title: 'Your session has ended',
        detail: 'Sign in again to continue. Nothing you typed has been sent.',
      },
      forbidden: {
        title: 'This is not available to your role',
        detail: 'Ask a Super-admin if you need access.',
      },
      notFound: {
        title: 'This record is not available',
        detail: 'It may have been removed, or it belongs to another team.',
      },
      conflict: {
        title: 'This change conflicts with the record',
        detail: 'Reload to see the current state, then try again.',
      },
      idempotencyConflict: {
        title: 'This was already submitted',
        detail: 'The earlier submission stands. Reload to see it.',
      },
      /* Only a quote's conversion answers this today, so the detail says so. */
      issuingEntityInactive: {
        title: 'Issuing entity deactivated',
        detail:
          "This quote's issuing entity is deactivated — reactivate it in Settings to create the case.",
      },
      /* Without its currency and date; with them, errors.rateMissing words it. */
      rateMissing: {
        title: 'Exchange rate missing',
        detail:
          'There is no exchange rate for the date this needs — without it the amount cannot be converted to USD.',
      },
      /* S-10: somebody else saved this leg's trucks after the page read them;
         nothing was written. */
      legUnitsChanged: {
        title: 'Leg units already changed',
        detail: 'Somebody else has already changed this leg’s units — reload the page',
      },
      /* S-21: a split ran past the payment's amount or the document's balance;
         with the refusal's members the client payment sheet names which. */
      allocationOverCeiling: {
        title: 'More than the balance',
        detail: 'More was allocated than the payment or the document has left. Nothing was saved.',
      },
      /* S-21: the tranche has been replaced by an issued invoice. */
      allocationTargetClosed: {
        title: 'Tranche replaced by an invoice',
        detail: 'This tranche has been invoiced — allocate the payment to the invoice.',
      },
      /* S-21: the case names no client. */
      caseHasNoClient: {
        title: 'The case has no client',
        detail: 'Set the client on the case card to record their payment.',
      },
      /* QW-5: the account still holds a password an administrator issued.
         RequireSession moves the reader to the change page at once, so this
         is on screen for a moment only. */
      passwordChangeRequired: {
        title: 'Change your password first',
        detail: 'Choose your own password to continue working.',
      },
      rateLimited: {
        title: 'Too many attempts',
        detail: 'Wait a moment and try again.',
      },
      payloadTooLarge: {
        title: 'That is too large to send',
        detail: 'Reduce the size and try again.',
      },
      unsupportedMediaType: {
        title: 'That file type is not accepted',
        detail: 'Use one of the accepted formats.',
      },
      unavailable: {
        /* Not a fault: the feature depends on a service this installation has
           not configured. Says so, rather than reporting a server error nobody
           can act on. */
        title: 'This is not available here',
        detail: 'The feature needs a service that is not set up. Ask an administrator.',
      },
      internal: {
        title: 'The server could not answer',
        detail: 'Nothing was changed. Try again in a moment.',
      },
      unknown: {
        title: 'Something went wrong',
        detail: 'Try again. If it keeps happening, tell a Super-admin.',
      },
    },

    /* The connection bar. Persistent, never a toast: a toast that vanishes
       during a lost connection is worse than nothing (UX-DR-18). */
    connection: {
      offline: 'No connection. Your work is kept on screen and nothing has been sent.',
      restored: 'Connection restored.',
      retry: 'Retry now',
    },
    requestFailed: 'Request failed',
  },
} as const
